import React, { useState, useEffect, useMemo } from 'react';
import { Modal, message, DatePicker, Select, Switch, Checkbox, Tooltip, Tag, Spin, Dropdown } from 'antd';
import {
    CloseOutlined,
    FolderAddOutlined,
    LoadingOutlined,
    DownOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    WarningOutlined,
    ArrowLeftOutlined,
    ArrowRightOutlined,
    RobotOutlined,
    FileExcelOutlined,
    FileTextOutlined,
    TeamOutlined,
    SettingOutlined,
    RocketOutlined,
    AimOutlined,
    InboxOutlined,
    ReloadOutlined,
    FilePdfOutlined,
    PictureOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    QuestionCircleOutlined,
    SearchOutlined,
    BranchesOutlined,
    MenuOutlined,
    RightOutlined,
    CheckSquareOutlined,
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import dayjs from 'dayjs';
import Papa from 'papaparse';
import { createProject, updateProject, fetchEmployeeNames, fetchTasks, fetchProjectById, syncChecklistFeedback, rejectProjectRequest } from '../api/projectManagementApi';
import { uploadProjectFile, fetchFiles as fetchProjectFiles } from '../files/api/projectFilesApi';

const COLOR_SWATCHES = [
    '#03488a', '#006591', '#00AAF1',
    '#059669', '#d97706', '#dc2626',
    '#7c3aed', '#db2777', '#0891b2',
];

const DEPARTMENTS = [
    'Development',
    'Design',
    'Marketing',
    'SEO',
    'Operations',
    'Human Resource',
    'Finance',
    'Business Development',
    'Project Management Office',
];

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Critical'];

// Helper to get consistent unique key for each checklist item
const getChecklistItemKey = (item, idx) => {
    return String(item?.checklistItemId || item?._id || `chk_${idx}`);
};
const STATUS_OPTIONS = ['Backlog', 'In Progress', 'Under Review', 'Completed', 'On Hold', 'Cancelled'];
const HEALTH_OPTIONS = ['On Track', 'At Risk', 'Critical'];

const getTaskMilestoneIndex = (t, milestones) => {
    if (!t || !t.milestoneId || !Array.isArray(milestones) || milestones.length === 0) return -1;
    const rawMs = String(t.milestoneId).trim();

    // 1. Direct _id or id match
    const directIdx = milestones.findIndex((m) =>
        (m._id && String(m._id) === rawMs) ||
        (m.id && String(m.id) === rawMs)
    );
    if (directIdx !== -1) return directIdx;

    // 2. Direct name/title match or substring match
    const nameIdx = milestones.findIndex((m) =>
        m.name && (
            m.name.toLowerCase() === rawMs.toLowerCase() ||
            rawMs.toLowerCase().includes(m.name.toLowerCase()) ||
            m.name.toLowerCase().includes(rawMs.toLowerCase())
        )
    );
    if (nameIdx !== -1) return nameIdx;

    // 3. Fallback for temp IDs ending in "-1", "-2", "-3" (1-based index)
    const endMatch = rawMs.match(/-(\d+)$/);
    if (endMatch) {
        const num = parseInt(endMatch[1], 10);
        if (num >= 1 && num <= milestones.length) return num - 1;
        if (num >= 0 && num < milestones.length) return num;
    }

    // 4. Fallback for plain number strings ("0", "1", "2")
    const plainNum = parseInt(rawMs, 10);
    if (!isNaN(plainNum)) {
        if (plainNum >= 0 && plainNum < milestones.length) return plainNum;
        if (plainNum >= 1 && plainNum <= milestones.length) return plainNum - 1;
    }

    return -1;
};

const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['clean'],
    ],
};

const CATEGORIES_WITH_SUBCATEGORIES = {
    'Amazon & Ecommerce': [
        'Listing Design',
        'Store Management',
        'Amazon Store Setup',
        'Product Research',
        'PPC & Advertising',
        'A+ Content Design',
    ],
    'Website': [
        'Website Development',
        'Website Maintenance',
        'Website Redesign',
        'UI/UX Design',
        'CMS Integration',
        'E-Commerce Website',
    ],
    'Graphic Design': [
        'Logo Design',
        'Stationery Design',
        'Brochure Design',
        'Social Media Design',
        'Brand Identity',
        'Packaging Design',
    ],
    'Mobile App': [
        'iOS App Development',
        'Android App Development',
        'Cross-Platform App',
        'Mobile App Maintenance',
        'App UI/UX Design',
    ],
    'Video Animation': [
        '2D Explainer Video',
        '3D Animation Video',
        'Video Editing',
        'Motion Graphics',
        'Logo Intro Animation',
    ],
    'Marketing': [
        'Social Media Marketing',
        'SEO Optimization',
        'Content Marketing',
        'Email Marketing',
        'PPC Campaign Management',
    ],
};

const INTERNAL_BRANDS = [
    { id: 'brand-dymaxtech', name: 'DymaxTech', code: 'INT-DYMAX', icon: 'corporate_fare', color: '#006a61', desc: 'Enterprise Solutions & IT' },
    { id: 'brand-e360digital', name: 'e360Digital', code: 'INT-E360', icon: 'language', color: '#0070F3', desc: 'Digital Marketing & Web' },
    { id: 'brand-cellularport', name: 'CellularPort', code: 'INT-CPORT', icon: 'smartphone', color: '#7c3aed', desc: 'Mobile & Hardware Tech' },
];

const INITIAL_FORM = {
    // Step 1
    name: '',
    projectCode: '',
    description: '',
    creProjectId: '',
    brandId: 'brand-dymaxtech',
    brandName: 'DymaxTech',
    isClientProject: false,
    projectType: 'In-house',
    category: 'Graphic Design',
    subCategory: 'Logo Design',
    durationMode: 'Fixed', // 'Fixed' | 'Ongoing'
    questionnaires: [],
    questionnaireTemplateName: '',
    questionnaireUrl: '',
    department: 'Development',
    priority: 'Medium',
    startDate: dayjs().format('YYYY-MM-DD'),
    dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
    owner: '',
    projectManager: '',
    collaborators: [],
    status: 'Backlog',
    health: 'On Track',
    color: '#03488a',
    smartGoals: [],

    // Step 2
    planMode: 'tasks', // 'tasks' | 'milestones' | 'ai' | 'excel'
    milestones: [],
    tasks: [],

    // Step 3
    documents: [],
    assets: [],
    requirementChecklist: [],

    // Step 4
    sprintManagement: true,
    availableViews: {
        list: true,
        kanban: true,
        timeline: true,
        sprint: true,
    },
    defaultView: 'Kanban Board View',
};

export default function CreateProjectModal({ open, onClose, onSuccess, projectToEdit }) {
    const [currentStep, setCurrentStep] = useState(1);
    const [form, setForm] = useState(INITIAL_FORM);
    const [loading, setLoading] = useState(false);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [employees, setEmployees] = useState([]);

    // Temporary input states for manual goal/milestone/task modals or inline creation
    const [aiGeneratingGoals, setAiGeneratingGoals] = useState(false);
    const [aiGeneratingPlan, setAiGeneratingPlan] = useState(false);
    const [showGoalModal, setShowGoalModal] = useState(false);
    const [editingGoalId, setEditingGoalId] = useState(null);
    const [goalForm, setGoalForm] = useState({ name: '', description: '', successCriteria: '', targetDate: '', priority: 'Medium' });

    // Rejection Note Modal & CRE Sync States
    const [showRejectItemModal, setShowRejectItemModal] = useState(false);
    const [rejectingItemIndex, setRejectingItemIndex] = useState(null);
    const [rejectItemReason, setRejectItemReason] = useState('');
    const [syncingChecklist, setSyncingChecklist] = useState(false);
    const [showRejectProjectModal, setShowRejectProjectModal] = useState(false);
    const [rejectProjectReason, setRejectProjectReason] = useState('');
    const [rejectingProject, setRejectingProject] = useState(false);

    // ── Checklist Item Selection for CRE Feedback Sync ────────────────────────
    const [selectedChecklistKeys, setSelectedChecklistKeys] = useState(() => new Set());
    const [checklistFilter, setChecklistFilter] = useState('all'); // 'all' | 'needs_action' | 'rejected' | 'approved'
    const [expandedChecklistNotes, setExpandedChecklistNotes] = useState(() => new Set());

    const toggleExpandChecklistNote = (key) => {
        setExpandedChecklistNotes((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSetItemStatus = (index, newStatus) => {
        const item = form.requirementChecklist[index];
        const key = getChecklistItemKey(item, index);

        if (newStatus === 'Rejected') {
            handleOpenRejectItemModal(index);
            return;
        }

        if (newStatus === 'Pending') {
            setForm((prev) => ({
                ...prev,
                requirementChecklist: prev.requirementChecklist.map((it, i) =>
                    i === index
                        ? {
                            ...it,
                            met: false,
                            rejected: false,
                            status: 'Pending',
                            decision: 'Pending',
                            isFeedbackSynced: false,
                        }
                        : it
                ),
            }));
            setSelectedChecklistKeys((prev) => new Set(prev).add(key));
            return;
        }

        if (newStatus === 'Approved') {
            setForm((prev) => ({
                ...prev,
                requirementChecklist: prev.requirementChecklist.map((it, i) =>
                    i === index
                        ? {
                            ...it,
                            met: true,
                            rejected: false,
                            status: 'Approved',
                            decision: 'Approved',
                            approvedBy: 'Project Manager',
                            approvedAt: new Date().toISOString(),
                            isFeedbackSynced: false,
                            note: '',
                            rejectionNote: '',
                        }
                        : it
                ),
            }));
            setSelectedChecklistKeys((prev) => new Set(prev).add(key));
            return;
        }
    };

    // Auto-select items that have pending (unsynced) reviews or newly resubmitted items
    useEffect(() => {
        if (!Array.isArray(form.requirementChecklist) || form.requirementChecklist.length === 0) return;
        setSelectedChecklistKeys((prev) => {
            const next = new Set(prev);
            form.requirementChecklist.forEach((item, idx) => {
                const key = getChecklistItemKey(item, idx);
                const isSynced = Boolean(item?.isFeedbackSynced);
                const isResubmitted = item?.status === 'Resubmitted' || item?.decision === 'Resubmitted';
                const hasPendingDecision = item?.met || item?.rejected || (item?.rejectionNote && item.rejectionNote.trim()) || isResubmitted;
                // If not yet synced and has action/review needed, select by default
                if (!isSynced && hasPendingDecision && !prev.has(key)) {
                    next.add(key);
                }
            });
            return next;
        });
    }, [form.requirementChecklist]);

    const toggleItemSelection = (index, forcedValue) => {
        const item = form.requirementChecklist[index];
        const key = getChecklistItemKey(item, index);
        setSelectedChecklistKeys((prev) => {
            const next = new Set(prev);
            const shouldAdd = forcedValue !== undefined ? forcedValue : !next.has(key);
            if (shouldAdd) next.add(key);
            else next.delete(key);
            return next;
        });
    };

    const handleSelectAllUnsynced = () => {
        const next = new Set();
        (form.requirementChecklist || []).forEach((item, idx) => {
            if (!item?.isFeedbackSynced) {
                next.add(getChecklistItemKey(item, idx));
            }
        });
        setSelectedChecklistKeys(next);
    };

    const handleDeselectAll = () => {
        setSelectedChecklistKeys(new Set());
    };

    // Items that have been reviewed (approved, rejected, or have notes)
    const reviewedItems = useMemo(() => {
        if (!Array.isArray(form.requirementChecklist)) return [];
        return form.requirementChecklist.filter(
            (item) => item && (item.met || item.rejected || (item.rejectionNote && item.rejectionNote.trim()) || (item.note && item.note.trim()))
        );
    }, [form.requirementChecklist]);

    const approvedItemsCount = useMemo(() => {
        if (!Array.isArray(form.requirementChecklist)) return 0;
        return form.requirementChecklist.filter((item) => item && item.met).length;
    }, [form.requirementChecklist]);

    const rejectedItemsCount = useMemo(() => {
        if (!Array.isArray(form.requirementChecklist)) return 0;
        return form.requirementChecklist.filter((item) => item && item.rejected).length;
    }, [form.requirementChecklist]);

    const canSendFeedbackToCRE = selectedChecklistKeys.size > 0;

    const handleOpenRejectItemModal = (index) => {
        const item = form.requirementChecklist[index];
        setRejectingItemIndex(index);
        setRejectItemReason(item?.rejectionNote || item?.note || '');
        setShowRejectItemModal(true);
    };

    const handleSaveItemRejection = () => {
        if (rejectingItemIndex === null) return;
        const targetItem = form.requirementChecklist[rejectingItemIndex];
        const key = getChecklistItemKey(targetItem, rejectingItemIndex);

        setForm((prev) => ({
            ...prev,
            requirementChecklist: prev.requirementChecklist.map((item, i) =>
                i === rejectingItemIndex
                    ? {
                        ...item,
                        met: false,
                        rejected: true,
                        status: 'Rejected',
                        decision: 'Rejected',
                        note: rejectItemReason.trim(),
                        rejectionNote: rejectItemReason.trim(),
                        rejectedBy: 'Project Manager',
                        rejectedAt: new Date().toISOString(),
                        isFeedbackSynced: false,
                    }
                    : item
            ),
        }));
        // Auto-select this newly rejected item for the feedback sync
        setSelectedChecklistKeys((prev) => new Set(prev).add(key));
        setShowRejectItemModal(false);
        setRejectingItemIndex(null);
        message.info('Rejection note saved for checklist requirement.');
    };

    const handleSyncChecklistToCRE = async () => {
        const targetId = projectToEdit?._id || form.projectRequestId || form.creProjectId;
        const selectedItemIds = (form.requirementChecklist || [])
            .filter((item, idx) => selectedChecklistKeys.has(getChecklistItemKey(item, idx)))
            .map((item) => item.checklistItemId || item._id)
            .filter(Boolean);

        if (selectedItemIds.length === 0 && selectedChecklistKeys.size === 0) {
            message.warning('Please select at least one checklist item to send feedback.');
            return;
        }

        setSyncingChecklist(true);
        try {
            const { data } = await syncChecklistFeedback(targetId, {
                requirementChecklist: form.requirementChecklist || [],
                selectedItemIds,
                creProjectId: form.creProjectId || projectToEdit?.creProjectId || '',
                pcrNumber: projectToEdit?.pcrNumber || '',
                name: form.name,
            });

            // Mark locally selected items as synced
            setForm((prev) => ({
                ...prev,
                requirementChecklist: (prev.requirementChecklist || []).map((item, idx) => {
                    const key = getChecklistItemKey(item, idx);
                    if (selectedChecklistKeys.has(key)) {
                        return { ...item, isFeedbackSynced: true, feedbackSyncedAt: new Date().toISOString() };
                    }
                    return item;
                }),
            }));
            // Clear selection since feedback has now been sent
            setSelectedChecklistKeys(new Set());
            message.success(data?.message || `Feedback for ${selectedItemIds.length || selectedChecklistKeys.size} item(s) synced with CRE successfully!`);
        } catch (err) {
            console.error('Failed to sync checklist to CRE:', err);
            message.error(err?.response?.data?.message || 'Failed to sync checklist feedback with CRE.');
        } finally {
            setSyncingChecklist(false);
        }
    };

    const handleRejectEntireProject = async () => {
        if (!rejectProjectReason.trim()) {
            message.warning('Please provide a reason for rejecting this project request.');
            return;
        }
        const targetId = projectToEdit?._id || form.projectRequestId || form.creProjectId;
        setRejectingProject(true);
        try {
            const { data } = await rejectProjectRequest(targetId, {
                rejectionReason: rejectProjectReason.trim(),
                requirementChecklist: form.requirementChecklist || [],
                creProjectId: form.creProjectId || projectToEdit?.creProjectId || '',
                pcrNumber: projectToEdit?.pcrNumber || '',
            });
            message.success(data?.message || 'Project request rejected and CRE notified.');
            setShowRejectProjectModal(false);
            onClose?.();
            onSuccess?.(null);
        } catch (err) {
            console.error('Failed to reject project request:', err);
            message.error(err?.response?.data?.message || 'Failed to reject project request.');
        } finally {
            setRejectingProject(false);
        }
    };

    const openAddGoal = () => {
        setEditingGoalId(null);
        const nextNum = form.smartGoals.length + 1;
        setGoalForm({
            name: `Goal ${nextNum} — `,
            description: '',
            successCriteria: '',
            targetDate: form.dueDate || '',
            priority: 'Medium',
        });
        setShowGoalModal(true);
    };

    const openEditGoal = (goal) => {
        setEditingGoalId(goal.id);
        setGoalForm({
            name: goal.name || '',
            description: goal.description || '',
            successCriteria: goal.successCriteria || '',
            targetDate: goal.targetDate || '',
            priority: goal.priority || 'Medium',
        });
        setShowGoalModal(true);
    };

    // Step 2 Manual Inputs
    const [showMilestoneModal, setShowMilestoneModal] = useState(false);
    const [editingMilestone, setEditingMilestone] = useState(null); // null = add new, object = edit
    const [milestoneForm, setMilestoneForm] = useState({ name: '', description: '', startDate: '', dueDate: '', completionCriteria: '' });
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null); // null = add new, object = edit
    const [taskForm, setTaskForm] = useState({ name: '', description: '', milestoneId: '', assignee: '', priority: 'Medium', estimatedHours: 4 });
    const [taskSearchQuery, setTaskSearchQuery] = useState('');
    const [taskViewMode, setTaskViewMode] = useState('tree'); // 'tree' | 'flat'
    const [taskStatusFilter, setTaskStatusFilter] = useState('all');
    const [taskAssigneeFilter, setTaskAssigneeFilter] = useState('all');
    const [taskMilestoneFilter, setTaskMilestoneFilter] = useState('all'); // 'all' | 'unassigned' | milestone index string
    const [taskTypeFilter, setTaskTypeFilter] = useState('all'); // 'all' | 'parents' | 'subtasks'
    const [taskPageSize, setTaskPageSize] = useState(15);
    const [taskPage, setTaskPage] = useState(1);
    const [expandedTaskIds, setExpandedTaskIds] = useState(new Set());
    const [expandedDeliverableMilestoneIds, setExpandedDeliverableMilestoneIds] = useState(new Set());
    const toggleExpandDeliverables = (key) => {
        setExpandedDeliverableMilestoneIds((prev) => {
            const next = new Set(prev);
            const strKey = String(key);
            if (next.has(strKey)) next.delete(strKey);
            else next.add(strKey);
            return next;
        });
    };
    const toggleTaskExpand = (taskId) => {
        setExpandedTaskIds((prev) => {
            const next = new Set(prev);
            const key = String(taskId);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };
    const expandAllTree = () => {
        const allParentIds = (form.tasks || []).filter((t) => !t.parentId).map((t) => String(t.id || t._id));
        setExpandedTaskIds(new Set(allParentIds));
    };
    const collapseAllTree = () => {
        setExpandedTaskIds(new Set());
    };

    // Filter, grouping and KPI calculations for Step 2 tasks
    const allParentTasks = useMemo(() => (form.tasks || []).filter((t) => !t.parentId), [form.tasks]);
    const allSubtasks = useMemo(() => (form.tasks || []).filter((t) => Boolean(t.parentId)), [form.tasks]);
    const completedTasksCount = useMemo(() => (form.tasks || []).filter((t) => ['Completed', 'Closed', 'Close'].includes(t.status)).length, [form.tasks]);
    const assignedTasksCount = useMemo(() => (form.tasks || []).filter((t) => Boolean(t.assignee || t.assignedTo)).length, [form.tasks]);
    const unassignedTasksCount = (form.tasks || []).length - assignedTasksCount;

    // Unassigned to milestone tasks (Backlog pool)
    const unassignedToMilestoneTasks = useMemo(() => {
        return (form.tasks || []).filter((t) => getTaskMilestoneIndex(t, form.milestones) === -1);
    }, [form.tasks, form.milestones]);

    const unassignedMilestoneParents = useMemo(() => {
        return unassignedToMilestoneTasks.filter((t) => !t.parentId);
    }, [unassignedToMilestoneTasks]);

    const unassignedMilestoneSubs = useMemo(() => {
        return unassignedToMilestoneTasks.filter((t) => Boolean(t.parentId));
    }, [unassignedToMilestoneTasks]);

    const handleMoveTaskToMilestone = (taskId, targetMilestoneId) => {
        const targetKey = String(taskId);
        const childIds = new Set();
        (form.tasks || []).forEach((t) => {
            const pId = String(typeof t.parentId === 'object' ? (t.parentId?._id || t.parentId?.id) : t.parentId);
            if (pId === targetKey) {
                childIds.add(String(t.id || t._id));
            }
        });

        setForm((prev) => ({
            ...prev,
            tasks: (prev.tasks || []).map((t) => {
                const currentId = String(t.id || t._id);
                if (currentId === targetKey || childIds.has(currentId)) {
                    return {
                        ...t,
                        milestoneId: targetMilestoneId || '',
                    };
                }
                return t;
            }),
        }));
        message.success(targetMilestoneId ? 'Task moved to milestone' : 'Task moved to Backlog (Unassigned)');
    };

    const handleBulkMoveUnassignedToMilestone = (targetMilestoneId) => {
        if (!targetMilestoneId) return;
        setForm((prev) => ({
            ...prev,
            tasks: (prev.tasks || []).map((t) => {
                if (getTaskMilestoneIndex(t, prev.milestones) === -1) {
                    return {
                        ...t,
                        milestoneId: targetMilestoneId,
                    };
                }
                return t;
            }),
        }));
        const mObj = form.milestones.find((m, idx) => String(m._id || m.id || `m-${idx}`) === String(targetMilestoneId));
        message.success(`All unassigned tasks moved to ${mObj?.name || 'Milestone'}`);
    };

    const subtasksByParentId = useMemo(() => {
        const map = new Map();
        (form.tasks || []).forEach((t) => {
            if (t.parentId) {
                const pId = String(typeof t.parentId === 'object' ? (t.parentId._id || t.parentId.id) : t.parentId);
                if (!map.has(pId)) map.set(pId, []);
                map.get(pId).push(t);
            }
        });
        return map;
    }, [form.tasks]);

    const taskById = useMemo(() => {
        const map = new Map();
        (form.tasks || []).forEach((t) => {
            map.set(String(t._id || t.id), t);
        });
        return map;
    }, [form.tasks]);

    const doesTaskMatchFilters = (t) => {
        if (!t) return false;
        const term = (taskSearchQuery || '').toLowerCase().trim();
        if (term) {
            const title = (t.name || t.title || '').toLowerCase();
            const code = (t.taskId || '').toLowerCase();
            const assigneeObj = t.assignee || t.assignedTo;
            const assigneeName = (typeof assigneeObj === 'object' && assigneeObj?.name
                ? `${assigneeObj.name} ${assigneeObj.surname || ''}`
                : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' ? assigneeObj : ''))).toLowerCase();
            if (!title.includes(term) && !code.includes(term) && !assigneeName.includes(term)) return false;
        }
        const normTaskStatus = (t.status === 'Closed' || t.status === 'Close') ? 'Completed' : (t.status === 'Active' ? 'In Progress' : (t.status || 'Pending'));
        if (taskStatusFilter !== 'all' && normTaskStatus !== taskStatusFilter) return false;
        const hasAssignee = Boolean(t.assignee || t.assignedTo);
        if (taskAssigneeFilter === 'assigned' && !hasAssignee) return false;
        if (taskAssigneeFilter === 'unassigned' && hasAssignee) return false;

        // Milestone Filter
        if (taskMilestoneFilter === 'unassigned') {
            if (getTaskMilestoneIndex(t, form.milestones) !== -1) return false;
        } else if (taskMilestoneFilter !== 'all') {
            if (String(getTaskMilestoneIndex(t, form.milestones)) !== String(taskMilestoneFilter)) return false;
        }

        return true;
    };

    const filteredTreeParents = useMemo(() => {
        return allParentTasks.filter((parent) => {
            const parentMatches = doesTaskMatchFilters(parent);
            const children = subtasksByParentId.get(String(parent._id || parent.id)) || [];
            const hasMatchingChild = children.some((c) => doesTaskMatchFilters(c));
            return parentMatches || hasMatchingChild;
        });
    }, [allParentTasks, subtasksByParentId, taskSearchQuery, taskStatusFilter, taskAssigneeFilter, taskMilestoneFilter, form.milestones, employees]);

    const orphanSubtasks = useMemo(() => {
        return allSubtasks.filter((st) => {
            const pId = String(typeof st.parentId === 'object' ? (st.parentId._id || st.parentId.id) : st.parentId);
            return !taskById.has(pId) && doesTaskMatchFilters(st);
        });
    }, [allSubtasks, taskById, taskSearchQuery, taskStatusFilter, taskAssigneeFilter, taskMilestoneFilter, form.milestones, employees]);

    const filteredFlatTasks = useMemo(() => {
        return (form.tasks || []).filter((t) => {
            if (!doesTaskMatchFilters(t)) return false;
            if (taskTypeFilter === 'parents' && t.parentId) return false;
            if (taskTypeFilter === 'subtasks' && !t.parentId) return false;
            return true;
        });
    }, [form.tasks, taskTypeFilter, taskSearchQuery, taskStatusFilter, taskAssigneeFilter, taskMilestoneFilter, form.milestones, employees]);

    const isTree = taskViewMode === 'tree';
    const activeListLength = isTree ? filteredTreeParents.length : filteredFlatTasks.length;
    const totalTaskPages = taskPageSize === 9999 ? 1 : (Math.ceil(activeListLength / taskPageSize) || 1);
    const paginatedTreeParents = taskPageSize === 9999 ? filteredTreeParents : filteredTreeParents.slice((taskPage - 1) * taskPageSize, (taskPage - 1) * taskPageSize + taskPageSize);
    const paginatedFlatTasks = taskPageSize === 9999 ? filteredFlatTasks : filteredFlatTasks.slice((taskPage - 1) * taskPageSize, (taskPage - 1) * taskPageSize + taskPageSize);
    const [expandedReviewTaskIds, setExpandedReviewTaskIds] = useState(new Set());
    const toggleReviewTaskExpand = (taskId) => {
        setExpandedReviewTaskIds((prev) => {
            const next = new Set(prev);
            const key = String(taskId);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const openAddMilestone = () => {
        setEditingMilestone(null);
        const nextNum = form.milestones.length + 1;
        setMilestoneForm({
            name: `Milestone ${nextNum} — `,
            description: '',
            startDate: form.startDate || '',
            dueDate: form.dueDate || '',
            completionCriteria: '',
            owner: form.projectManager || form.owner || '',
            status: 'Pending',
        });
        setShowMilestoneModal(true);
    };
    const openEditMilestone = (m) => {
        setEditingMilestone(m);
        const dels = (Array.isArray(m.deliverableChecklist) && m.deliverableChecklist.length > 0)
            ? m.deliverableChecklist
            : (Array.isArray(m.deliverables) ? m.deliverables : []);
        setMilestoneForm({
            name: m.name || m.title || '',
            description: m.description || '',
            startDate: m.startDate || '',
            dueDate: m.dueDate || '',
            completionCriteria: m.completionCriteria || '',
            owner: m.owner?._id || m.owner || form.projectManager || form.owner || '',
            status: m.status || 'Pending',
            deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
            deliverableChecklist: dels.map((d, idx) => ({
                deliverableId: d.deliverableId || d.creDeliverableId || `DELIV-${idx + 1}`,
                creDeliverableId: d.creDeliverableId || d.deliverableId || '',
                title: d.title || d.name || 'Deliverable',
                completed: Boolean(d.completed || d.done),
                rejected: Boolean(d.rejected),
                rejectionNote: d.rejectionNote || '',
                order: d.order !== undefined ? d.order : idx + 1,
            })),
        });
        setShowMilestoneModal(true);
    };

    const handleToggleStep2Deliverable = (mIdx, dIdx) => {
        setForm((prev) => {
            const nextMilestones = [...prev.milestones];
            const targetM = { ...nextMilestones[mIdx] };
            let checklist = (Array.isArray(targetM.deliverableChecklist) && targetM.deliverableChecklist.length > 0)
                ? [...targetM.deliverableChecklist]
                : (Array.isArray(targetM.deliverables) ? [...targetM.deliverables] : []);

            if (checklist[dIdx]) {
                const nextDone = !Boolean(checklist[dIdx].completed || checklist[dIdx].done);
                checklist[dIdx] = {
                    ...checklist[dIdx],
                    completed: nextDone,
                    done: nextDone,
                    rejected: nextDone ? false : checklist[dIdx].rejected,
                    completedAt: nextDone ? new Date().toISOString() : null,
                };
            }
            targetM.deliverableChecklist = checklist;
            if (Array.isArray(targetM.deliverables)) {
                targetM.deliverables = targetM.deliverables.map((d, i) => {
                    if (i === dIdx) {
                        const nextDone = !Boolean(d.done || d.completed);
                        return { ...d, done: nextDone, completed: nextDone };
                    }
                    return d;
                });
            }
            nextMilestones[mIdx] = targetM;
            return { ...prev, milestones: nextMilestones };
        });
    };

    const handleQuickCompleteMilestone = (mIdx) => {
        const targetM = form.milestones[mIdx];
        if (!targetM) return;
        const delList = (Array.isArray(targetM.deliverableChecklist) && targetM.deliverableChecklist.length > 0)
            ? targetM.deliverableChecklist
            : (Array.isArray(targetM.deliverables) ? targetM.deliverables : []);

        const unverified = delList.filter((d) => !Boolean(d.completed || d.done));
        if (delList.length > 0 && unverified.length > 0) {
            message.warning(`Cannot complete milestone: ${unverified.length} deliverable(s) must be verified and checked first!`);
            openEditMilestone(targetM);
            return;
        }

        setForm((prev) => {
            const nextMilestones = [...prev.milestones];
            nextMilestones[mIdx] = { ...targetM, status: 'Completed' };
            return { ...prev, milestones: nextMilestones };
        });
        message.success(`Milestone "${targetM.name || targetM.title}" marked as Completed!`);
    };
    const openAddTask = () => {
        setEditingTask(null);
        setTaskForm({
            name: '',
            description: '',
            milestoneId: form.milestones[0]?._id || form.milestones[0]?.id || '',
            assignee: form.owner || '',
            priority: 'Medium',
            estimatedHours: 4,
        });
        setShowTaskModal(true);
    };
    const openAddTaskForMilestone = (m) => {
        setEditingTask(null);
        setTaskForm({
            name: '',
            description: '',
            milestoneId: m._id || m.id || '',
            assignee: form.owner || '',
            priority: 'Medium',
            estimatedHours: 4,
        });
        setShowTaskModal(true);
    };
    const openEditTask = (t) => {
        setEditingTask(t);
        let resolvedMsId = t.milestoneId || '';
        if (form.milestones.length > 0) {
            const mIdx = getTaskMilestoneIndex(t, form.milestones);
            if (mIdx !== -1 && form.milestones[mIdx]) {
                resolvedMsId = form.milestones[mIdx]._id || form.milestones[mIdx].id || resolvedMsId;
            }
        }
        setTaskForm({
            name: t.name || t.title || '',
            description: t.description || '',
            milestoneId: resolvedMsId,
            assignee: t.assignee?._id || t.assignee || '',
            priority: t.priority || 'Medium',
            estimatedHours: t.estimatedHours || 4,
        });
        setShowTaskModal(true);
    };

    // Step 2 Excel Import
    const [excelFile, setExcelFile] = useState(null);
    const [excelColumns, setExcelColumns] = useState([]);
    const [excelRows, setExcelRows] = useState([]);
    const [excelMapping, setExcelMapping] = useState({
        name: '',
        description: '',
        milestone: '',
        assignee: '',
        priority: '',
        estimatedHours: '',
    });

    // Step 1 Questionnaire state & presets
    const [questionnaireInput, setQuestionnaireInput] = useState('');
    const [showLinkSelector, setShowLinkSelector] = useState(false);

    const EXISTING_QUESTIONNAIRE_TEMPLATES = [
        'Website Development Intake Form',
        'Graphic Design & Branding Brief',
        'Amazon Store Setup & Management Sheet',
        'Mobile Application Spec Questionnaire',
        'Video Animation Project Brief',
        'Digital Marketing Campaign Scope',
    ];

    const handleAddQuestionnaire = (titleToAdd) => {
        const title = (titleToAdd || questionnaireInput).trim();
        if (!title) return;
        setForm(prev => {
            const updated = prev.questionnaires.includes(title) ? prev.questionnaires : [...prev.questionnaires, title];
            return {
                ...prev,
                questionnaires: updated,
                questionnaireTemplateName: updated[0] || title,
            };
        });
        setQuestionnaireInput('');
        setShowLinkSelector(false);
        message.success(`Linked questionnaire: "${title}"`);
    };

    const handleSelectQuestionnaireTemplate = (templateName) => {
        handleAddQuestionnaire(templateName);
    };

    const handleUnlinkQuestionnaire = () => {
        setForm(prev => ({
            ...prev,
            questionnaires: [],
            questionnaireTemplateName: '',
            questionnaireUrl: '',
        }));
        setShowLinkSelector(false);
        message.info('Questionnaire unlinked.');
    };

    const handleRemoveQuestionnaire = (indexToRemove) => {
        setForm(prev => {
            const updated = prev.questionnaires.filter((_, i) => i !== indexToRemove);
            return {
                ...prev,
                questionnaires: updated,
                questionnaireTemplateName: updated[0] || '',
            };
        });
    };

    // Auto-generate preview Project Code on open or load existing project to edit
    useEffect(() => {
        if (open) {
            setCurrentStep(1);
            if (projectToEdit) {
                const loadProjectDetails = async () => {
                    setLoadingTasks(true);
                    try {
                        let freshProject = projectToEdit;
                    if (projectToEdit._id) {
                        try {
                            const { data } = await fetchProjectById(projectToEdit._id);
                            if (data?.result) freshProject = data.result;
                        } catch (e) {
                            console.error('Failed to fetch full project details:', e);
                        }
                    }

                    const isDraft = Boolean(freshProject.isDraft || freshProject.status === 'Draft');

                    let parsedGoals = [];
                    if (Array.isArray(freshProject.smartGoals) && freshProject.smartGoals.length > 0) {
                        parsedGoals = freshProject.smartGoals;
                    } else if (Array.isArray(freshProject.objectives) && freshProject.objectives.length > 0) {
                        parsedGoals = freshProject.objectives.map((obj, i) => {
                            if (typeof obj === 'string') {
                                const parts = obj.split(':');
                                return {
                                    id: `g-${i}`,
                                    name: parts[0]?.trim() || `Goal ${i + 1}`,
                                    description: obj,
                                    successCriteria: parts.slice(1).join(':').trim() || 'Completed',
                                    targetDate: freshProject.dueDate ? dayjs(freshProject.dueDate).format('YYYY-MM-DD') : '',
                                    priority: 'Medium',
                                };
                            }
                            return { id: obj.id || `g-${i}`, ...obj };
                        });
                    }

                    const legacyMilestones = (freshProject.milestones || []).map((m, i) => {
                        const resolvedCreId = m.creMilestoneId || (typeof m._id === 'string' && m._id.length > 5 ? m._id : (typeof m.id === 'string' && m.id.length > 5 && !m.id.startsWith('m-') ? m.id : ''));
                        const checklist = Array.isArray(m.deliverableChecklist) && m.deliverableChecklist.length > 0
                            ? m.deliverableChecklist
                            : (Array.isArray(m.deliverables)
                                ? m.deliverables.map((d, dIdx) => ({
                                    deliverableId: d.deliverableId || `DELIV-${dIdx + 1}`,
                                    title: d.title || d.name || 'Deliverable',
                                    completed: Boolean(d.done || d.completed),
                                    rejected: Boolean(d.rejected),
                                    rejectionNote: d.rejectionNote || '',
                                }))
                                : []);
                        return {
                            id: m._id || m.id || resolvedCreId || `m-${i}`,
                            _id: m._id || m.id,
                            creMilestoneId: resolvedCreId,
                            name: m.name || m.title || `Milestone ${i + 1}`,
                            title: m.title || m.name || `Milestone ${i + 1}`,
                            description: m.description || '',
                            status: m.status || 'Pending',
                            startDate: m.startDate ? dayjs(m.startDate).format('YYYY-MM-DD') : (m.eta ? dayjs(m.eta).format('YYYY-MM-DD') : ''),
                            dueDate: m.dueDate ? dayjs(m.dueDate).format('YYYY-MM-DD') : (m.eta ? dayjs(m.eta).format('YYYY-MM-DD') : ''),
                            completionCriteria: m.completionCriteria || m.notes || '',
                            owner: m.owner?._id || m.owner || '',
                            deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
                            deliverableChecklist: checklist,
                            itemType: 'milestone',
                        };
                    });

                    // Extract native drawer-created milestones from freshProject.tasks
                    const nativeMilestones = (freshProject.tasks || [])
                        .filter((t) => t.itemType === 'milestone')
                        .map((m, i) => {
                            const checklist = Array.isArray(m.deliverableChecklist) && m.deliverableChecklist.length > 0
                                ? m.deliverableChecklist
                                : (Array.isArray(m.deliverables)
                                    ? m.deliverables.map((d, dIdx) => ({
                                        deliverableId: d.deliverableId || `DELIV-${dIdx + 1}`,
                                        title: d.title || d.name || 'Deliverable',
                                        completed: Boolean(d.done || d.completed),
                                        rejected: Boolean(d.rejected),
                                        rejectionNote: d.rejectionNote || '',
                                    }))
                                    : []);
                            return {
                                id: m._id || m.id,
                                _id: m._id || m.id,
                                name: m.title || m.name || `Milestone ${i + 1}`,
                                title: m.title || m.name || `Milestone ${i + 1}`,
                                description: m.description || '',
                                status: m.status || 'In Progress',
                                startDate: m.startDate ? dayjs(m.startDate).format('YYYY-MM-DD') : '',
                                dueDate: m.dueDate ? dayjs(m.dueDate).format('YYYY-MM-DD') : '',
                                completionCriteria: m.completionCriteria || '',
                                owner: m.milestoneOwner?._id || m.milestoneOwner || m.owner || '',
                                deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
                                deliverableChecklist: checklist,
                                itemType: 'milestone',
                            };
                        });

                    // Combine both legacy and native task milestones without duplicates
                    const existingIds = new Set(legacyMilestones.map(m => String(m._id || m.id)));
                    const filteredNative = nativeMilestones.filter(m => !existingIds.has(String(m._id || m.id)));
                    const parsedMilestones = [...legacyMilestones, ...filteredNative];

                    // Executable tasks (excluding milestones)
                    const executableTasks = (freshProject.tasks || []).filter((t) => t.itemType !== 'milestone');

                    const getCleanId = (val) => {
                        if (!val) return '';
                        if (typeof val === 'object') return val._id || val.id || '';
                        return typeof val === 'string' && val.length === 24 ? val : '';
                    };

                    const initialOwner = getCleanId(freshProject.createdBy) || getCleanId(freshProject.owner);
                    const initialPM = getCleanId(freshProject.projectManager);

                    const resolvedCategory = freshProject.category || freshProject.projectType || 'Graphic Design';
                    const resolvedSubCategory = freshProject.subCategory || freshProject.subcategory || freshProject.projectSubType || 'Logo Design';
                    const isClient = Boolean(freshProject.isClientProject || freshProject.projectType === 'Client' || freshProject.creProjectId || freshProject.pcrNumber);
                    const resolvedDuration = freshProject.durationMode || (freshProject.dueDate || freshProject.eta ? 'Fixed' : 'Ongoing');
                    const loadedQList = Array.isArray(freshProject.questionnaires) && freshProject.questionnaires.length > 0
                        ? freshProject.questionnaires
                        : (freshProject.questionnaire?.name ? [freshProject.questionnaire.name] : (freshProject.questionnaireTemplateName ? [freshProject.questionnaireTemplateName] : []));

                    const qUrl = freshProject.questionnaire?.url || freshProject.linkedQuestionnaire?.url || freshProject.questionnaireUrl || '';

                    // Normalise legacy status strings to canonical before populating the form.
                    // Import normalizeStatus from the shared frontend utility so the alias map
                    // is the single source of truth — no manual if-chains needed here.
                    const { normalizeStatus: _normalizeStatus } = await import('../../../utils/normalizeStatus');
                    let resolvedStatus = _normalizeStatus(freshProject.status, 'In Progress');
                    // Draft projects always open as In Progress in the edit form
                    if (isDraft) resolvedStatus = 'In Progress';

                    const resolvedClientName = freshProject.clientName || freshProject.client?.name || (freshProject.client?.company ? `${freshProject.client.name} (${freshProject.client.company})` : '') || freshProject.owner?.name || '';

                    setForm({
                        name: freshProject.name || '',
                        projectCode: freshProject.projectCode || `PRJ-${Math.floor(1000 + Math.random() * 9000)}`,
                        description: freshProject.description || '',
                        creProjectId: freshProject.creProjectId || '',
                        isClientProject: isClient,
                        projectType: isClient ? 'Client' : 'In-house',
                        brandId: freshProject.brandId || 'brand-dymaxtech',
                        brandName: freshProject.brandName || 'DymaxTech',
                        category: resolvedCategory,
                        subCategory: resolvedSubCategory,
                        durationMode: resolvedDuration,
                        clientName: resolvedClientName,
                        billingType: freshProject.billingType || 'Fixed',
                        source: freshProject.projectSource || freshProject.source || 'CRE',
                        questionnaires: loadedQList,
                        questionnaireTemplateName: loadedQList[0] || freshProject.questionnaireTemplateName || '',
                        questionnaireUrl: qUrl,
                        department: freshProject.department || 'Development',
                        priority: freshProject.priority || 'Medium',
                        startDate: freshProject.startDate ? dayjs(freshProject.startDate).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                        dueDate: freshProject.dueDate ? dayjs(freshProject.dueDate).format('YYYY-MM-DD') : (freshProject.eta ? dayjs(freshProject.eta).format('YYYY-MM-DD') : dayjs().add(30, 'day').format('YYYY-MM-DD')),
                        owner: initialOwner,
                        projectManager: initialPM,
                        collaborators: (freshProject.members || freshProject.collaborators || []).map((m) => (typeof m === 'object' ? m._id : m)).filter(Boolean),
                        status: resolvedStatus,
                        health: freshProject.health || 'On Track',
                        color: freshProject.color || '#03488a',
                        smartGoals: Array.isArray(parsedGoals) ? parsedGoals : [],
                        planMode: 'tasks',
                        milestones: Array.isArray(parsedMilestones) ? parsedMilestones : [],
                        tasks: Array.isArray(executableTasks) ? executableTasks : [],
                        documents: Array.isArray(freshProject.documents) ? freshProject.documents : [],
                        assets: Array.isArray(freshProject.assets) ? freshProject.assets : [],
                        requirementChecklist: Array.isArray(freshProject.requirementChecklist) && freshProject.requirementChecklist.length > 0
                            ? freshProject.requirementChecklist.map((item) => {
                                const isPendingRev = freshProject.status === 'Pending Review' || projectToEdit?.status === 'Pending Review';
                                const wasExplicitlyRejected = Boolean(item.rejected);
                                const wasResubmitted = item.status === 'Resubmitted' || item.decision === 'Resubmitted';
                                return {
                                    ...item,
                                    checklistItemId: item.checklistItemId || item._id || item.id || '',
                                    label: item.label || item.item || item.detail || 'Requirement',
                                    detail: item.detail || item.description || '',
                                    category: item.category || '',
                                    required: item.required !== undefined ? item.required : true,
                                    met: wasResubmitted ? false : (isPendingRev ? false : Boolean(item.met)),
                                    rejected: wasResubmitted ? false : wasExplicitlyRejected,
                                    status: item.status || (wasResubmitted ? 'Resubmitted' : (wasExplicitlyRejected ? 'Rejected' : (item.met ? 'Approved' : 'Pending'))),
                                    decision: item.decision || (wasResubmitted ? 'Resubmitted' : (wasExplicitlyRejected ? 'Rejected' : (item.met ? 'Approved' : ''))),
                                    rejectionNote: wasExplicitlyRejected ? (item.rejectionNote || item.note || '') : '',
                                    previousRejectionNote: item.previousRejectionNote || (wasResubmitted ? (item.note || item.rejectionNote || '') : ''),
                                    fixNote: item.fixNote || '',
                                    reason: item.reason || item.approvalReason || '',
                                    approvalReason: item.approvalReason || item.reason || '',
                                    note: item.note || item.rejectionNote || '',
                                    acknowledged: Boolean(item.acknowledged),
                                    acknowledgedBy: item.acknowledgedBy || '',
                                    acknowledgedByEmail: item.acknowledgedByEmail || '',
                                    acknowledgedAt: item.acknowledgedAt || null,
                                    approvedBy: item.approvedBy || '',
                                    approvedAt: item.approvedAt || null,
                                };
                            })
                            : (Array.isArray(freshProject.projectChecklist)
                                ? freshProject.projectChecklist.map((item) => ({
                                    checklistItemId: item.checklistItemId || item._id || item.id || '',
                                    label: item.label || item.item || item.detail || 'Requirement',
                                    detail: item.detail || item.description || '',
                                    category: item.category || '',
                                    required: item.required !== undefined ? item.required : true,
                                    met: Boolean(item.completed || item.met),
                                    rejected: Boolean(item.rejected),
                                    status: item.status || (item.rejected ? 'Rejected' : (item.completed || item.met ? 'Approved' : 'Pending')),
                                    decision: item.decision || '',
                                    fixNote: item.fixNote || '',
                                    previousRejectionNote: item.previousRejectionNote || '',
                                    rejectionNote: item.rejectionNote || '',
                                    note: item.rejected ? (item.rejectionNote || '') : '',
                                    reason: item.reason || '',
                                    approvalReason: item.approvalReason || '',
                                    approvedBy: item.approvedBy || '',
                                    approvedAt: item.approvedAt || null,
                                    acknowledged: Boolean(item.acknowledged),
                                    acknowledgedBy: item.acknowledgedBy || '',
                                    acknowledgedByEmail: item.acknowledgedByEmail || '',
                                    acknowledgedAt: item.acknowledgedAt || null,
                                }))
                                : []),
                        sprintManagement: freshProject.autoSprintPlanning ?? true,
                        availableViews: freshProject.customFields?.availableViews || { list: true, kanban: true, timeline: true, sprint: true },
                        defaultView: freshProject.customFields?.defaultView || 'Kanban Board View',
                    });

                    if (freshProject._id) {
                        try {
                            const { data: tasksRes } = await fetchTasks({ projectId: freshProject._id, limit: 'all' });
                            const fetchedRaw = tasksRes?.result || [];

                            const fetchedNativeMilestones = fetchedRaw
                                .filter((t) => t.itemType === 'milestone')
                                .map((m, i) => {
                                    const checklist = Array.isArray(m.deliverableChecklist) && m.deliverableChecklist.length > 0
                                        ? m.deliverableChecklist
                                        : (Array.isArray(m.deliverables)
                                            ? m.deliverables.map((d, dIdx) => ({
                                                deliverableId: d.deliverableId || `DELIV-${dIdx + 1}`,
                                                title: d.title || d.name || 'Deliverable',
                                                completed: Boolean(d.done || d.completed),
                                                rejected: Boolean(d.rejected),
                                                rejectionNote: d.rejectionNote || '',
                                            }))
                                            : []);
                                    return {
                                        id: m._id || m.id,
                                        _id: m._id || m.id,
                                        name: m.title || m.name || `Milestone ${i + 1}`,
                                        title: m.title || m.name || `Milestone ${i + 1}`,
                                        description: m.description || '',
                                        status: m.status || 'In Progress',
                                        startDate: m.startDate ? dayjs(m.startDate).format('YYYY-MM-DD') : '',
                                        dueDate: m.dueDate ? dayjs(m.dueDate).format('YYYY-MM-DD') : '',
                                        completionCriteria: m.completionCriteria || '',
                                        owner: m.milestoneOwner?._id || m.milestoneOwner || m.owner || '',
                                        deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
                                        deliverableChecklist: checklist,
                                        itemType: 'milestone',
                                    };
                                });

                            const projectTasks = fetchedRaw
                                .filter((t) => t.itemType !== 'milestone')
                                .map((t) => {
                                    const rawMsId = t.milestoneId ? (typeof t.milestoneId === 'object' ? (t.milestoneId._id || t.milestoneId.id) : t.milestoneId) : '';
                                    const rawParentId = t.parentId ? (typeof t.parentId === 'object' ? (t.parentId._id || t.parentId.id) : t.parentId) : null;
                                    return {
                                        id: t._id,
                                        _id: t._id,
                                        taskId: t.taskId || '',
                                        name: t.title || t.name,
                                        title: t.title || t.name,
                                        description: t.description || '',
                                        milestoneId: String(rawMsId || ''),
                                        parentId: rawParentId ? String(rawParentId) : null,
                                        assignee: t.assignee?._id || t.assignee || '',
                                        priority: t.priority || 'Medium',
                                        status: t.status || 'Backlog',
                                        estimatedHours: t.estimatedTimeMs ? Math.round(t.estimatedTimeMs / 3600000) : (parseInt(t.estimatedTime, 10) || 4),
                                    };
                                });

                            setForm((prev) => {
                                const currentMsIds = new Set((prev.milestones || []).map((m) => String(m._id || m.id)));
                                const newNative = fetchedNativeMilestones.filter((m) => !currentMsIds.has(String(m._id || m.id)));
                                return {
                                    ...prev,
                                    milestones: [...(prev.milestones || []), ...newNative],
                                    tasks: projectTasks.length > 0 ? projectTasks : prev.tasks,
                                };
                            });
                        } catch (e) {
                            console.error('Failed to fetch tasks for project:', e);
                        }

                        // ── 2-Way Sync: Merge any files uploaded from Files Module into form.documents and form.assets ──
                        const isPendingReview = freshProject.status === 'Pending Review' || projectToEdit?.status === 'Pending Review' || Boolean(freshProject.pcrNumber && !freshProject.erpProjectId);
                        if (!isPendingReview) {
                            try {
                                const filesRes = await fetchProjectFiles({ projectId: freshProject._id });
                                if (filesRes?.success && Array.isArray(filesRes.data)) {
                                    const filesFromStorage = filesRes.data;
                                    const extraDocs = filesFromStorage.filter((f) => f.folderCode === '01');
                                    const extraAssets = filesFromStorage.filter((f) => f.folderCode === '02' || f.folderCode === '05');

                                setForm((prev) => {
                                    const docNames = new Set((prev.documents || []).map((d) => (d.fileName || d.originalName || '').toLowerCase().trim()));
                                    const assetNames = new Set((prev.assets || []).map((a) => (a.fileName || a.originalName || '').toLowerCase().trim()));

                                    const newDocs = extraDocs
                                        .filter((f) => !docNames.has((f.name || '').toLowerCase().trim()))
                                        .map((f) => ({
                                            id: f.id,
                                            _id: f.id,
                                            fileName: f.name,
                                            fileSize: f.size,
                                            fileType: f.type,
                                            publicUrl: f.publicUrl || '',
                                            storageKey: f.storageKey || '',
                                            uploadedAt: f.date,
                                            uploadedBy: f.uploadedBy || 'Team Member',
                                        }));

                                    const newAssets = extraAssets
                                        .filter((f) => !assetNames.has((f.name || '').toLowerCase().trim()))
                                        .map((f) => ({
                                            id: f.id,
                                            _id: f.id,
                                            fileName: f.name,
                                            fileSize: f.size,
                                            fileType: f.type,
                                            publicUrl: f.publicUrl || '',
                                            storageKey: f.storageKey || '',
                                            uploadedAt: f.date,
                                            uploadedBy: f.uploadedBy || 'Team Member',
                                        }));

                                    return {
                                        ...prev,
                                        documents: [...(prev.documents || []), ...newDocs],
                                        assets: [...(prev.assets || []), ...newAssets],
                                    };
                                });
                            }
                        } catch (e) {
                            console.error('Failed to sync files from storage module:', e);
                        }
                    }
                    }
                    } finally {
                        setLoadingTasks(false);
                    }
                };

                loadProjectDetails();
            } else {
                // If creating a new project and form is empty, set initial state; otherwise preserve un-saved draft state
                setForm((prev) => {
                    if (prev.name || prev.description || (prev.questionnaires && prev.questionnaires.length > 0)) {
                        return prev; // Retain user's typed data across modal toggles
                    }
                    const randomNum = Math.floor(1000 + Math.random() * 9000);
                    return {
                        ...INITIAL_FORM,
                        projectCode: `PRJ-${randomNum}`,
                        startDate: dayjs().format('YYYY-MM-DD'),
                        dueDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
                    };
                });
            }
            loadEmployees();
        }
    }, [open, projectToEdit]);

    const reloadTasks = async () => {
        const targetId = projectToEdit?._id || form.projectRequestId || form.creProjectId;
        if (!targetId) return;
        setLoadingTasks(true);
        try {
            const { data: tasksRes } = await fetchTasks({ projectId: targetId, limit: 'all' });
            const fetchedRaw = tasksRes?.result || [];

            const fetchedNativeMilestones = fetchedRaw
                .filter((t) => t.itemType === 'milestone')
                .map((m, i) => ({
                    id: m._id || m.id,
                    _id: m._id || m.id,
                    name: m.title || m.name || `Milestone ${i + 1}`,
                    title: m.title || m.name || `Milestone ${i + 1}`,
                    description: m.description || '',
                    status: m.status || 'In Progress',
                    startDate: m.startDate ? dayjs(m.startDate).format('YYYY-MM-DD') : '',
                    dueDate: m.dueDate ? dayjs(m.dueDate).format('YYYY-MM-DD') : '',
                    completionCriteria: m.completionCriteria || '',
                    owner: m.milestoneOwner?._id || m.milestoneOwner || m.owner || '',
                    deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
                    itemType: 'milestone',
                }));

            const projectTasks = fetchedRaw
                .filter((t) => t.itemType !== 'milestone')
                .map((t) => {
                    const rawMsId = t.milestoneId ? (typeof t.milestoneId === 'object' ? (t.milestoneId._id || t.milestoneId.id) : t.milestoneId) : '';
                    const rawParentId = t.parentId ? (typeof t.parentId === 'object' ? (t.parentId._id || t.parentId.id) : t.parentId) : null;
                    return {
                        id: t._id,
                        _id: t._id,
                        taskId: t.taskId || '',
                        name: t.title || t.name,
                        title: t.title || t.name,
                        description: t.description || '',
                        milestoneId: String(rawMsId || ''),
                        parentId: rawParentId ? String(rawParentId) : null,
                        assignee: t.assignee?._id || t.assignee || '',
                        priority: t.priority || 'Medium',
                        status: t.status || 'Backlog',
                        estimatedHours: t.estimatedTimeMs ? Math.round(t.estimatedTimeMs / 3600000) : (parseInt(t.estimatedTime, 10) || 4),
                    };
                });

            setForm((prev) => {
                const currentMsIds = new Set((prev.milestones || []).map((m) => String(m._id || m.id)));
                const newNative = fetchedNativeMilestones.filter((m) => !currentMsIds.has(String(m._id || m.id)));
                return {
                    ...prev,
                    milestones: [...(prev.milestones || []), ...newNative],
                    tasks: projectTasks,
                };
            });
            message.success('Tasks and milestones refreshed.');
        } catch (e) {
            console.error('Failed to reload tasks:', e);
            message.error('Failed to refresh tasks.');
        } finally {
            setLoadingTasks(false);
        }
    };

    const loadEmployees = async () => {
        try {
            const { data } = await fetchEmployeeNames();
            const list = (data?.names || []).map((e) => ({
                _id: e.id,
                name: e.fullName || `${e.name || ''} ${e.surname || ''}`.trim(),
            }));
            setEmployees(list);
            if (list.length > 0) {
                setForm((prev) => ({
                    ...prev,
                    owner: prev.owner ? prev.owner : list[0]._id,
                    projectManager: prev.projectManager ? prev.projectManager : list[0]._id,
                }));
            }
        } catch (err) {
            console.error('Failed to load employees:', err);
        }
    };

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    // AI Requirements Guidance Modal state
    const [aiGuidanceModal, setAiGuidanceModal] = useState({ open: false, target: 'goals', missingFields: [] });

    const checkStep1AiRequirements = (targetType) => {
        const missing = [];
        if (!form.name.trim()) missing.push({ field: 'Project Name', reason: 'Defines the main domain and title for goals & tasks' });
        if (!form.description?.trim() || form.description.trim().length < 8) missing.push({ field: 'Project Description', reason: 'Enables AI to understand project scope and key deliverables' });
        if (!form.startDate) missing.push({ field: 'Start Date', reason: 'Required for calculating milestone phase start dates' });
        if (!form.dueDate) missing.push({ field: 'End Date / Due Date', reason: 'Required for calculating time-bound deadlines' });
        if (!form.department) missing.push({ field: 'Department', reason: 'Aligns tasks with operational team workflows' });
        if (!form.owner) missing.push({ field: 'Project Owner', reason: 'Required for designating milestone leads and primary accountability' });
        if (!form.projectManager) missing.push({ field: 'Project Manager', reason: 'Required for managing milestone phases and task oversight' });

        if (missing.length > 0) {
            setAiGuidanceModal({ open: true, target: targetType, missingFields: missing });
            return false;
        }
        return true;
    };

    const openAddGoalGuarded = () => {
        const isReady = checkStep1AiRequirements('goals');
        if (isReady) {
            openAddGoal();
        }
    };

    const openAddMilestoneGuarded = () => {
        const isReady = checkStep1AiRequirements('plan');
        if (isReady) {
            openAddMilestone();
        }
    };

    // AI Conflict Modal state (Replace vs Append)
    const [aiConflictModal, setAiConflictModal] = useState({ open: false, target: 'goals' });

    // ── AI Smart Goals Generator ──────────────────────────────────────────────
    const triggerSmartGoalsGeneration = (shouldAppend = false) => {
        setAiGeneratingGoals(true);
        setTimeout(() => {
            const projectDueDate = form.dueDate || dayjs().add(30, 'day').format('YYYY-MM-DD');
            const startNum = shouldAppend ? form.smartGoals.length + 1 : 1;

            const generated = [
                {
                    id: `goal-${Date.now()}-1`,
                    name: `Goal ${startNum}: Automate ${form.name || 'Project'} Workflows`,
                    description: `Streamline operational efficiency and reduce manual processing time for ${form.department || 'the team'}.`,
                    successCriteria: 'Achieve 95%+ operational automation with minimal error rates',
                    targetDate: projectDueDate,
                    priority: 'High',
                },
                {
                    id: `goal-${Date.now()}-2`,
                    name: `Goal ${startNum + 1}: Ensure High Quality & Accuracy for ${form.name || 'Deliverables'}`,
                    description: 'Establish rigorous testing, QA benchmarks, and stakeholder review loops.',
                    successCriteria: '100% compliance with performance benchmarks and client satisfaction',
                    targetDate: projectDueDate,
                    priority: 'Medium',
                },
                {
                    id: `goal-${Date.now()}-3`,
                    name: `Goal ${startNum + 2}: Enhance Reporting & Executive Visibility`,
                    description: 'Provide executive dashboards, milestone progress tracking, and audit logs.',
                    successCriteria: 'Real-time metrics available to department managers and stakeholders',
                    targetDate: form.startDate && dayjs(form.startDate).add(14, 'day').isBefore(dayjs(projectDueDate))
                        ? dayjs(form.startDate).add(14, 'day').format('YYYY-MM-DD')
                        : projectDueDate,
                    priority: 'Medium',
                },
            ];
            setForm((prev) => ({
                ...prev,
                smartGoals: shouldAppend ? [...prev.smartGoals, ...generated] : generated,
            }));
            setAiGeneratingGoals(false);
            message.success(shouldAppend ? `AI appended Goal ${startNum} through Goal ${startNum + 2}!` : 'AI generated 3 S.M.A.R.T. Goals!');
        }, 1200);
    };

    const handleGenerateSmartGoals = () => {
        const isReady = checkStep1AiRequirements('goals');
        if (!isReady) return;
        if (form.smartGoals.length > 0) {
            setAiConflictModal({ open: true, target: 'goals' });
        } else {
            triggerSmartGoalsGeneration(false);
        }
    };

    const handleAddGoal = () => {
        if (!goalForm.name.trim()) {
            message.warning('Goal Name is required.');
            return;
        }
        if (!goalForm.description?.trim() && !goalForm.successCriteria?.trim()) {
            message.warning('Goal Description or Success Criteria is required.');
            return;
        }
        const defaultDate = goalForm.targetDate || form.dueDate || dayjs().add(30, 'day').format('YYYY-MM-DD');
        const defaultCriteria = goalForm.successCriteria?.trim() || goalForm.description?.trim() || 'Achieve goal objectives with 100% compliance';

        const updatedGoal = {
            ...goalForm,
            targetDate: defaultDate,
            successCriteria: defaultCriteria,
        };

        if (editingGoalId) {
            setForm((prev) => ({
                ...prev,
                smartGoals: prev.smartGoals.map((g) =>
                    g.id === editingGoalId
                        ? { ...g, ...updatedGoal }
                        : g
                ),
            }));
            message.success('Smart Goal updated.');
        } else {
            const newGoal = {
                id: `goal-${Date.now()}`,
                ...updatedGoal,
            };
            setForm((prev) => ({ ...prev, smartGoals: [...prev.smartGoals, newGoal] }));
            message.success('Smart Goal added.');
        }
        setGoalForm({ name: '', description: '', successCriteria: '', targetDate: '', priority: 'Medium' });
        setEditingGoalId(null);
        setShowGoalModal(false);
    };

    const handleRemoveGoal = (goalId) => {
        setForm((prev) => ({ ...prev, smartGoals: prev.smartGoals.filter((g) => g.id !== goalId) }));
    };

    // ── AI Execution Plan Generator (Step 2) ──────────────────────────────────
    const triggerAIPlanGeneration = (shouldAppend = false) => {
        setAiGeneratingPlan(true);
        setTimeout(() => {
            const m1Id = `m-${Date.now()}-1`;
            const m2Id = `m-${Date.now()}-2`;
            const m3Id = `m-${Date.now()}-3`;

            const startNum = shouldAppend ? form.milestones.length + 1 : 1;

            let start = form.startDate ? dayjs(form.startDate) : dayjs();
            if (shouldAppend && form.milestones.length > 0) {
                const latestDueDate = form.milestones.reduce((latest, m) => (m.dueDate && dayjs(m.dueDate).isAfter(dayjs(latest)) ? m.dueDate : latest), start.format('YYYY-MM-DD'));
                start = dayjs(latestDueDate).add(1, 'day');
            }

            const end = form.dueDate && dayjs(form.dueDate).isAfter(start) ? dayjs(form.dueDate) : start.add(30, 'day');
            const totalDays = Math.max(7, end.diff(start, 'day'));
            const p1Days = Math.max(2, Math.round(totalDays * 0.25));
            const p2Days = Math.max(3, Math.round(totalDays * 0.5));

            const m1Due = start.add(p1Days, 'day').format('YYYY-MM-DD');
            const m2Start = start.add(p1Days + 1, 'day').format('YYYY-MM-DD');
            const m2Due = start.add(p1Days + p2Days, 'day').format('YYYY-MM-DD');
            const m3Start = start.add(p1Days + p2Days + 1, 'day').format('YYYY-MM-DD');
            const m3Due = end.format('YYYY-MM-DD');

            const generatedMilestones = [
                {
                    id: m1Id,
                    name: `Milestone ${startNum} — Requirements & Architecture`,
                    description: 'Review workflow, document specifications, and finalize architecture.',
                    startDate: start.format('YYYY-MM-DD'),
                    dueDate: m1Due,
                    completionCriteria: 'Signed off specification document & architecture sign-off',
                    owner: form.projectManager || form.owner || '',
                },
                {
                    id: m2Id,
                    name: `Milestone ${startNum + 1} — Core Development & Integration`,
                    description: 'Build backend APIs, frontend UI components, and integrate workflows.',
                    startDate: m2Start,
                    dueDate: m2Due,
                    completionCriteria: 'All core modules built & passing automated unit tests',
                    owner: form.owner || form.projectManager || '',
                },
                {
                    id: m3Id,
                    name: `Milestone ${startNum + 2} — QA, UAT & Deployment`,
                    description: 'Integration testing, user acceptance sign-off, and production release.',
                    startDate: m3Start,
                    dueDate: m3Due,
                    completionCriteria: 'Zero critical bugs and successful production deployment',
                    owner: form.projectManager || form.owner || '',
                },
            ];

            const generatedTasks = [
                { id: `t-${Date.now()}-1`, name: `Task ${startNum}.1: Review existing workflow & specs`, milestoneId: m1Id, priority: 'High', estimatedHours: 8, assignee: form.owner },
                { id: `t-${Date.now()}-2`, name: `Task ${startNum}.2: Document requirements document`, milestoneId: m1Id, priority: 'Medium', estimatedHours: 6, assignee: form.projectManager },
                { id: `t-${Date.now()}-3`, name: `Task ${startNum + 1}.1: Develop REST API endpoints`, milestoneId: m2Id, priority: 'High', estimatedHours: 24, assignee: form.owner },
                { id: `t-${Date.now()}-4`, name: `Task ${startNum + 1}.2: Build dashboard frontend components`, milestoneId: m2Id, priority: 'Medium', estimatedHours: 18, assignee: form.projectManager },
                { id: `t-${Date.now()}-5`, name: `Task ${startNum + 2}.1: Integration & End-to-End Testing`, milestoneId: m3Id, priority: 'Critical', estimatedHours: 12, assignee: form.owner },
                { id: `t-${Date.now()}-6`, name: `Task ${startNum + 2}.2: Final User Acceptance & Sign-off`, milestoneId: m3Id, priority: 'High', estimatedHours: 6, assignee: form.projectManager },
            ];
            setForm((prev) => {
                const hasExistingTasks = prev.tasks && prev.tasks.length > 0;
                return {
                    ...prev,
                    planMode: 'milestones',
                    milestones: shouldAppend ? [...prev.milestones, ...generatedMilestones] : generatedMilestones,
                    tasks: hasExistingTasks ? prev.tasks : generatedTasks,
                };
            });
            setAiGeneratingPlan(false);
            message.success(
                form.tasks?.length > 0
                    ? `AI generated ${generatedMilestones.length} Milestones! Your ${form.tasks.length} tasks are ready in the backlog.`
                    : (shouldAppend ? `AI appended Milestone ${startNum} through Milestone ${startNum + 2} & 6 Tasks!` : `AI generated 3 Milestones & 6 Tasks!`)
            );
        }, 1200);
    };

    const handleGenerateAIPlan = () => {
        const isReady = checkStep1AiRequirements('plan');
        if (!isReady) return;
        if (form.milestones.length > 0) {
            setAiConflictModal({ open: true, target: 'plan' });
        } else {
            triggerAIPlanGeneration(false);
        }
    };

    // ── Excel Import Parsing (Step 2) ─────────────────────────────────────────
    const handleExcelFileUpload = (file) => {
        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const cols = results.meta.fields || [];
                setExcelColumns(cols);
                setExcelRows(results.data || []);
                setExcelFile(file);
                // Auto-guess mapping
                const guess = { name: '', description: '', milestone: '', assignee: '', priority: '', estimatedHours: '' };
                cols.forEach((col) => {
                    const lower = col.toLowerCase();
                    if (lower.includes('task') || lower.includes('title') || lower.includes('name')) guess.name = col;
                    if (lower.includes('desc')) guess.description = col;
                    if (lower.includes('mile')) guess.milestone = col;
                    if (lower.includes('assign') || lower.includes('owner')) guess.assignee = col;
                    if (lower.includes('prior')) guess.priority = col;
                    if (lower.includes('est') || lower.includes('hour')) guess.estimatedHours = col;
                });
                setExcelMapping(guess);
                message.success(`Parsed ${results.data.length} rows from ${file.name}`);
            },
            error: () => message.error('Failed to parse file.'),
        });
    };

    const handleImportExcelData = () => {
        if (!excelMapping.name) {
            message.warning('Please map at least the Task Name column.');
            return;
        }
        const importedTasks = excelRows.map((row, i) => ({
            id: `excel-t-${i}`,
            name: row[excelMapping.name] || `Task ${i + 1}`,
            description: row[excelMapping.description] || '',
            priority: row[excelMapping.priority] || 'Medium',
            estimatedHours: parseInt(row[excelMapping.estimatedHours], 10) || 4,
            assignee: form.owner,
        }));

        setForm((prev) => ({ ...prev, tasks: [...prev.tasks, ...importedTasks], planMode: 'tasks' }));
        message.success(`Imported ${importedTasks.length} tasks from file!`);
    };

    // ── Environment Check & QA Demo Data Generator ────────────────────────────
    const isDemoEnvironment = useMemo(() => {
        if (typeof window === 'undefined') return false;
        const host = window.location.hostname.toLowerCase();
        return host.includes('localhost') || host === '127.0.0.1' || host.includes('qaerp.dymaxtech.com');
    }, []);

    const handleFillDemoData = () => {
        const scenarios = [
            {
                name: 'AI-Powered Customer Support Portal Migration',
                department: 'Software Engineering',
                description: 'Modernize legacy customer support tools by migrating to an AI-driven ticketing and self-service portal with real-time analytics.',
                isClientProject: true,
                projectType: 'Client',
                category: 'Website',
                subCategory: 'Website Development',
                durationMode: 'Fixed',
                questionnaireTemplateName: 'Website Development Intake Questionnaire',
                priority: 'High',
                health: 'On Track',
                status: 'In Progress',
                color: '#03488a',
                smartGoals: [
                    {
                        id: 'g-demo-1',
                        name: 'Reduce First Response Time by 40%',
                        description: 'Implement AI chat routing to resolve 30% of tier-1 tickets automatically.',
                        successCriteria: 'Average first response time < 15 minutes across all departments',
                        targetDate: dayjs().add(20, 'day').format('YYYY-MM-DD'),
                        priority: 'High',
                    },
                    {
                        id: 'g-demo-2',
                        name: 'Achieve 98% Portal System Uptime',
                        description: 'Deploy auto-scaling cloud infrastructure and load balancing for high traffic.',
                        successCriteria: 'Zero unannounced downtime during peak business hours',
                        targetDate: dayjs().add(40, 'day').format('YYYY-MM-DD'),
                        priority: 'Critical',
                    },
                    {
                        id: 'g-demo-3',
                        name: 'Stakeholder & Team Training Sign-off',
                        description: 'Conduct training workshops for 50+ support agents and department managers.',
                        successCriteria: '100% agent certification on new portal interface',
                        targetDate: dayjs().add(45, 'day').format('YYYY-MM-DD'),
                        priority: 'Medium',
                    },
                ],
                milestones: (m1Id, m2Id, m3Id, ownerId, pmId, startStr, m1Due, m2Start, m2Due, m3Start, m3Due) => [
                    {
                        id: m1Id,
                        name: 'Phase 1: Architecture & AI Model Training',
                        description: 'Define REST API contracts, train initial intent classification models, and design database schema.',
                        startDate: startStr,
                        dueDate: m1Due,
                        completionCriteria: 'API specs signed off & baseline AI model accuracy > 88%',
                        owner: pmId || ownerId,
                    },
                    {
                        id: m2Id,
                        name: 'Phase 2: Core Development & Integration',
                        description: 'Build Next.js portal UI, integrate ticketing REST APIs, and implement OAuth security.',
                        startDate: m2Start,
                        dueDate: m2Due,
                        completionCriteria: 'All core tickets passing automated unit & end-to-end tests',
                        owner: ownerId || pmId,
                    },
                    {
                        id: m3Id,
                        name: 'Phase 3: QA, Security Audit & Production Launch',
                        description: 'Conduct penetration testing, UAT with support leads, and execute production DNS cutover.',
                        startDate: m3Start,
                        dueDate: m3Due,
                        completionCriteria: 'Zero critical security vulnerabilities & executive sign-off',
                        owner: pmId || ownerId,
                    },
                ],
                tasks: (m1Id, m2Id, m3Id, ownerId, pmId) => [
                    { id: 't-demo-1', name: 'Draft REST API & Database Schema Specs', milestoneId: m1Id, priority: 'High', estimatedHours: 12, assignee: ownerId },
                    { id: 't-demo-2', name: 'Train & Evaluate Intent Classification AI', milestoneId: m1Id, priority: 'High', estimatedHours: 20, assignee: pmId },
                    { id: 't-demo-3', name: 'Develop Responsive React Support Dashboard', milestoneId: m2Id, priority: 'High', estimatedHours: 24, assignee: ownerId },
                    { id: 't-demo-4', name: 'Integrate Real-Time Webhook Notifications', milestoneId: m2Id, priority: 'Medium', estimatedHours: 16, assignee: pmId },
                    { id: 't-demo-5', name: 'Execute Automated Security & Load Testing', milestoneId: m3Id, priority: 'Critical', estimatedHours: 14, assignee: ownerId },
                    { id: 't-demo-6', name: 'Conduct User Acceptance Testing & Cutover', milestoneId: m3Id, priority: 'High', estimatedHours: 10, assignee: pmId },
                ],
            },
            {
                name: 'Q3 Global Brand Rebrand & Digital Campaign',
                department: 'Marketing',
                description: 'Execute comprehensive brand refresh across digital channels, product packages, and global marketing assets.',
                isClientProject: true,
                projectType: 'Client',
                category: 'Graphic Design',
                subCategory: 'Brand Identity',
                durationMode: 'Ongoing',
                questionnaireTemplateName: 'Brand Identity & Creative Intake Form',
                priority: 'High',
                health: 'On Track',
                status: 'Backlog',
                color: '#7c3aed',
                smartGoals: [
                    {
                        id: 'g-demo-1',
                        name: 'Increase Organic Brand Reach by 50%',
                        description: 'Launch unified social media and SEO campaign targeting core enterprise demographics.',
                        successCriteria: 'Over 500,000 unique impressions within first 30 days',
                        targetDate: dayjs().add(25, 'day').format('YYYY-MM-DD'),
                        priority: 'High',
                    },
                    {
                        id: 'g-demo-2',
                        name: 'Complete Brand Identity Kit & Design System',
                        description: 'Deliver updated typography, logo variants, UI component kit, and brand guidelines.',
                        successCriteria: 'Full brand book approved by CMO & Executive Leadership',
                        targetDate: dayjs().add(15, 'day').format('YYYY-MM-DD'),
                        priority: 'Medium',
                    },
                ],
                milestones: (m1Id, m2Id, m3Id, ownerId, pmId, startStr, m1Due, m2Start, m2Due, m3Start, m3Due) => [
                    {
                        id: m1Id,
                        name: 'Phase 1: Brand Strategy & Identity Guidelines',
                        description: 'Finalize brand messaging framework, typography hierarchy, logo variations, and color palettes.',
                        startDate: startStr,
                        dueDate: m1Due,
                        completionCriteria: 'Executive board approval on brand identity kit',
                        owner: pmId || ownerId,
                    },
                    {
                        id: m2Id,
                        name: 'Phase 2: Digital Asset Creation & Website Redesign',
                        description: 'Redesign marketing landing pages, ad collateral, video teasers, and sales deck templates.',
                        startDate: m2Start,
                        dueDate: m2Due,
                        completionCriteria: '100% digital collateral staged and responsive',
                        owner: ownerId || pmId,
                    },
                    {
                        id: m3Id,
                        name: 'Phase 3: Global Media Launch & Analytics Tracking',
                        description: 'Distribute press releases, launch paid social campaigns, and monitor conversion metrics.',
                        startDate: m3Start,
                        dueDate: m3Due,
                        completionCriteria: 'Campaign live on all global ad channels with tracking active',
                        owner: pmId || ownerId,
                    },
                ],
                tasks: (m1Id, m2Id, m3Id, ownerId, pmId) => [
                    { id: 't-demo-1', name: 'Create Brand Identity Guidelines & Typography', milestoneId: m1Id, priority: 'High', estimatedHours: 16, assignee: pmId },
                    { id: 't-demo-2', name: 'Design High-Converting Landing Page Mockups', milestoneId: m2Id, priority: 'High', estimatedHours: 20, assignee: ownerId },
                    { id: 't-demo-3', name: 'Produce Video Teaser & Social Media Collateral', milestoneId: m2Id, priority: 'Medium', estimatedHours: 18, assignee: pmId },
                    { id: 't-demo-4', name: 'Setup Campaign Analytics & Conversion Tracking', milestoneId: m3Id, priority: 'Critical', estimatedHours: 10, assignee: ownerId },
                ],
            },
            {
                name: 'Enterprise Mobile App & Onboarding Portal',
                department: 'Development',
                description: 'Build cross-platform mobile application and portal for real-time tracking, document signing, and operational KPIs.',
                isClientProject: false,
                projectType: 'In-house',
                category: 'Mobile App',
                subCategory: 'Cross-Platform App',
                durationMode: 'Fixed',
                questionnaireTemplateName: 'Mobile App Technical Intake Questionnaire',
                priority: 'Medium',
                health: 'On Track',
                status: 'In Progress',
                color: '#059669',
                smartGoals: [
                    {
                        id: 'g-demo-1',
                        name: 'Reduce Mobile App Load Time under 1.5 seconds',
                        description: 'Optimize bundle size and offline caching.',
                        successCriteria: 'Average app load time under 1.5s across iOS and Android devices',
                        targetDate: dayjs().add(30, 'day').format('YYYY-MM-DD'),
                        priority: 'High',
                    },
                    {
                        id: 'g-demo-2',
                        name: '100% Security Audit Compliance',
                        description: 'Ensure encrypted local storage and biometric auth pass security benchmarks.',
                        successCriteria: 'Zero security findings in external audit',
                        targetDate: dayjs().add(40, 'day').format('YYYY-MM-DD'),
                        priority: 'Critical',
                    },
                ],
                milestones: (m1Id, m2Id, m3Id, ownerId, pmId, startStr, m1Due, m2Start, m2Due, m3Start, m3Due) => [
                    {
                        id: m1Id,
                        name: 'Phase 1: Wireframing & Cross-Platform Setup',
                        description: 'Design app UX screens, set up React Native repo, and integrate auth API.',
                        startDate: startStr,
                        dueDate: m1Due,
                        completionCriteria: 'Wireframes approved & authentication flow working',
                        owner: pmId || ownerId,
                    },
                    {
                        id: m2Id,
                        name: 'Phase 2: Core Feature Build & Push Notifications',
                        description: 'Implement offline sync, push notifications, and user dashboard.',
                        startDate: m2Start,
                        dueDate: m2Due,
                        completionCriteria: 'Beta build published to TestFlight & Internal Play Store',
                        owner: ownerId || pmId,
                    },
                    {
                        id: m3Id,
                        name: 'Phase 3: App Store Deployment & Monitoring',
                        description: 'Submit app to Apple App Store & Google Play Store, configure crash reporting.',
                        startDate: m3Start,
                        dueDate: m3Due,
                        completionCriteria: 'App live in stores with crash rate < 0.1%',
                        owner: pmId || ownerId,
                    },
                ],
                tasks: (m1Id, m2Id, m3Id, ownerId, pmId) => [
                    { id: 't-demo-1', name: 'Design High-Fidelity App Wireframes in Figma', milestoneId: m1Id, priority: 'High', estimatedHours: 14, assignee: pmId },
                    { id: 't-demo-2', name: 'Develop React Native Core Navigation & Auth', milestoneId: m1Id, priority: 'High', estimatedHours: 18, assignee: ownerId },
                    { id: 't-demo-3', name: 'Integrate Real-Time Push Notification Engine', milestoneId: m2Id, priority: 'Medium', estimatedHours: 12, assignee: pmId },
                ],
            },
        ];

        const scenario = scenarios[Math.floor(Math.random() * scenarios.length)];
        const start = dayjs();
        const end = dayjs().add(45, 'day');
        const startStr = start.format('YYYY-MM-DD');
        const endStr = end.format('YYYY-MM-DD');

        const m1Id = `m-${Date.now()}-1`;
        const m2Id = `m-${Date.now()}-2`;
        const m3Id = `m-${Date.now()}-3`;

        const m1Due = start.add(10, 'day').format('YYYY-MM-DD');
        const m2Start = start.add(11, 'day').format('YYYY-MM-DD');
        const m2Due = start.add(30, 'day').format('YYYY-MM-DD');
        const m3Start = start.add(31, 'day').format('YYYY-MM-DD');
        const m3Due = endStr;

        const defaultOwner = employees[0]?._id || form.owner || '';
        const defaultPM = employees[1]?._id || employees[0]?._id || form.projectManager || '';

        const generatedMilestones = scenario.milestones(m1Id, m2Id, m3Id, defaultOwner, defaultPM, startStr, m1Due, m2Start, m2Due, m3Start, m3Due);
        const generatedTasks = scenario.tasks(m1Id, m2Id, m3Id, defaultOwner, defaultPM);

        setForm((prev) => ({
            ...prev,
            name: scenario.name,
            projectCode: `PRJ-${Math.floor(1000 + Math.random() * 9000)}`,
            description: scenario.description,
            isClientProject: scenario.isClientProject,
            projectType: scenario.projectType,
            category: scenario.category,
            subCategory: scenario.subCategory,
            durationMode: scenario.durationMode,
            questionnaireTemplateName: scenario.questionnaireTemplateName,
            questionnaires: [scenario.questionnaireTemplateName].filter(Boolean),
            department: scenario.department,
            priority: scenario.priority,
            startDate: startStr,
            dueDate: scenario.durationMode === 'Ongoing' ? '' : endStr,
            status: scenario.status,
            health: scenario.health,
            color: scenario.color,
            owner: defaultOwner,
            projectManager: defaultPM,
            smartGoals: scenario.smartGoals,
            milestones: generatedMilestones,
            tasks: generatedTasks,
            planMode: 'tasks',
        }));

        message.success(`Populated Demo Scenario: "${scenario.name}" (${scenario.category} > ${scenario.subCategory})`);
    };
    const handleFileUpload = async (e, type) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        const isEditing = Boolean(projectToEdit && projectToEdit._id);
        const folderCode = type === 'documents' ? '01' : '02';
        const category = type === 'documents' ? 'Project Document' : 'Project Asset';

        if (isEditing) {
            const hide = message.loading(`Streaming ${files.length} file(s) to Cloudflare R2...`, 0);
            try {
                const uploadedItems = [];
                for (const file of files) {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('projectId', projectToEdit._id);
                    formData.append('folderCode', folderCode);
                    formData.append('category', category);
                    formData.append('description', 'Uploaded from Edit Project modal');

                    const res = await uploadProjectFile(formData);
                    if (res?.success && res?.data) {
                        const d = res.data;
                        uploadedItems.push({
                            id: d.id,
                            _id: d.id,
                            fileName: d.originalName || file.name,
                            fileSize: d.size || ((file.size / 1024).toFixed(1) + ' KB'),
                            fileType: d.type || file.name.split('.').pop().toUpperCase(),
                            publicUrl: d.publicUrl || '',
                            storageKey: d.storageKey || '',
                            uploadedAt: new Date().toISOString(),
                            uploadedBy: form.owner || 'You',
                        });
                    }
                }
                setForm((prev) => ({
                    ...prev,
                    [type]: [...(prev[type] || []), ...uploadedItems],
                }));
                hide();
                message.success(`Uploaded ${uploadedItems.length} file(s) to Project and Project Files!`);
            } catch (err) {
                hide();
                console.error('Direct file upload to R2 failed:', err);
                message.error('Failed to upload file(s) to storage.');
            }
        } else {
            // New project creation flow — keep pending files to upload upon project creation
            const newItems = files.map((file) => {
                const objectUrl = URL.createObjectURL(file);
                return {
                    id: `${type}-${Date.now()}-${Math.random()}`,
                    fileName: file.name,
                    fileSize: (file.size / 1024).toFixed(1) + ' KB',
                    fileType: file.name.split('.').pop().toUpperCase(),
                    publicUrl: objectUrl,
                    url: objectUrl,
                    uploadedAt: new Date().toISOString(),
                    uploadedBy: form.owner || 'You',
                    _rawFile: file,
                    _folderCode: folderCode,
                    _category: category,
                };
            });

            setForm((prev) => ({
                ...prev,
                [type]: [...(prev[type] || []), ...newItems],
            }));
            message.success(`Added ${newItems.length} ${type}. Will sync to Project Files on Save!`);
        }
        e.target.value = '';
    };

    const handleRemoveFile = (type, id) => {
        setForm((prev) => ({
            ...prev,
            [type]: prev[type].filter((item) => item.id !== id),
        }));
    };

    // ── Readiness & Validation Engine (Step 5) ────────────────────────────────
    const validationResults = useMemo(() => {
        const dateValid = form.durationMode === 'Ongoing'
            ? Boolean(form.startDate)
            : (form.startDate && form.dueDate && (dayjs(form.dueDate).isAfter(dayjs(form.startDate)) || dayjs(form.dueDate).isSame(dayjs(form.startDate))));

        const unfulfilledRequiredChecklist = Array.isArray(form.requirementChecklist)
            ? form.requirementChecklist.filter((item) => item && item.required && !item.met)
            : [];
        const hasRequiredChecklist = Array.isArray(form.requirementChecklist) && form.requirementChecklist.some((item) => item && item.required);

        const checks = [
            { id: 'c1', label: 'Project Name & Description complete', valid: Boolean(form.name.trim()) },
            { id: 'c2', label: 'Category & Sub-Category classified', valid: Boolean(form.category && form.subCategory) },
            { id: 'c3', label: 'Department assigned', valid: Boolean(form.department) },
            { id: 'c4', label: 'Project Owner assigned', valid: Boolean(form.owner) },
            { id: 'c5', label: 'Project Manager assigned', valid: Boolean(form.projectManager) },
            { id: 'c6', label: form.durationMode === 'Ongoing' ? 'Start Date valid (Ongoing)' : 'Project dates valid (End >= Start)', valid: Boolean(dateValid) },
            { id: 'c7', label: 'Milestones & Tasks structure defined', valid: form.tasks.length > 0 || form.milestones.length > 0 },
            { id: 'c8', label: 'Configuration setup complete', valid: Boolean(form.defaultView) },
            ...(hasRequiredChecklist ? [{
                id: 'c9',
                label: 'All required intake checklist items verified',
                valid: unfulfilledRequiredChecklist.length === 0,
            }] : []),
        ];

        const warnings = [];
        if (unfulfilledRequiredChecklist.length > 0) {
            warnings.push(`${unfulfilledRequiredChecklist.length} required intake checklist item(s) pending verification.`);
        }
        const unassignedCount = form.tasks.filter((t) => !t.assignee).length;
        if (unassignedCount > 0) {
            warnings.push(`${unassignedCount} task(s) currently have no assignee specified.`);
        }
        const noEstCount = form.tasks.filter((t) => !t.estimatedHours || t.estimatedHours <= 0).length;
        if (noEstCount > 0) {
            warnings.push(`${noEstCount} task(s) have no estimated effort specified.`);
        }
        if (form.smartGoals.length === 0) {
            warnings.push('No Smart Goals have been created for this project.');
        } else {
            const emptyGoalDates = form.smartGoals.filter((g) => !g.targetDate).length;
            if (emptyGoalDates > 0) {
                warnings.push(`${emptyGoalDates} Smart Goal(s) are missing a target date.`);
            }
            const emptyGoalDescs = form.smartGoals.filter((g) => !g.description?.trim() && !g.successCriteria?.trim()).length;
            if (emptyGoalDescs > 0) {
                warnings.push(`${emptyGoalDescs} Smart Goal(s) are missing a description/success criteria.`);
            }
        }
        const emptyMsDates = form.milestones.filter((m) => !m.dueDate).length;
        if (emptyMsDates > 0) {
            warnings.push(`${emptyMsDates} Milestone(s) are missing a due date.`);
        }
        const emptyMsDescs = form.milestones.filter((m) => !m.description?.trim() && !m.completionCriteria?.trim()).length;
        if (emptyMsDescs > 0) {
            warnings.push(`${emptyMsDescs} Milestone(s) are missing a description/completion criteria.`);
        }
        const emptyMsOwners = form.milestones.filter((m) => !m.owner).length;
        if (emptyMsOwners > 0) {
            warnings.push(`${emptyMsOwners} Milestone(s) are missing an assigned Lead/Owner.`);
        }

        const isReady = checks.every((c) => c.valid);

        return { checks, warnings, isReady };
    }, [form]);

    // ── Final Submit ──────────────────────────────────────────────────────────
    const handleSubmitProject = async (isDraftMode = false) => {
        if (!form.name.trim()) {
            message.warning('Project Name is required.');
            return;
        }
        if (form.durationMode === 'Fixed' && (!form.startDate || !form.dueDate)) {
            message.warning('Start Date and End Date are required for Fixed projects.');
            return;
        }
        setLoading(true);
        try {
            const finalStatus = isDraftMode
                ? 'Draft'
                : (form.status === 'Draft' ? 'In Progress' : form.status);

            const allQuestionnairesList = Array.from(new Set([
                ...(Array.isArray(form.questionnaires) ? form.questionnaires : []),
                ...(form.questionnaireTemplateName ? [form.questionnaireTemplateName] : []),
            ])).filter(Boolean);

            const payload = {
                name: form.name.trim(),
                projectCode: form.projectCode,
                description: form.description,
                brandId: form.brandId || 'brand-dymaxtech',
                brandName: form.brandName || 'DymaxTech',
                isClientProject: form.isClientProject,
                projectType: form.isClientProject ? 'Client' : 'In-house',
                category: form.category,
                subCategory: form.subCategory,
                durationMode: form.durationMode || 'Fixed',
                questionnaires: allQuestionnairesList,
                questionnaireTemplateName: allQuestionnairesList[0] || form.questionnaireTemplateName || '',
                linkedQuestionnaire: allQuestionnairesList.length > 0 || form.questionnaireUrl ? {
                    name: allQuestionnairesList[0] || form.questionnaireTemplateName || 'Client Intake Questionnaire',
                    title: allQuestionnairesList[0] || form.questionnaireTemplateName || 'Client Intake Questionnaire',
                    url: form.questionnaireUrl || '',
                    submittedAt: new Date().toISOString(),
                } : null,
                department: form.department,
                priority: form.priority,
                startDate: form.startDate,
                dueDate: form.durationMode === 'Ongoing' ? null : form.dueDate,
                owner: form.owner,
                createdBy: form.owner,
                projectManager: form.projectManager,
                members: form.collaborators,
                status: finalStatus,
                health: form.health,
                color: form.color,
                goal: form.smartGoals.map((g) => g.name).join(' | '),
                objectives: form.smartGoals.map((g) => `${g.name}: ${g.successCriteria || 'Completed'}`),
                milestones: form.milestones.map((m) => {
                    const item = {
                        name: m.name || m.title || '',
                        title: m.title || m.name || '',
                        description: m.description || '',
                        startDate: m.startDate || null,
                        dueDate: m.dueDate || null,
                        status: (m.status && m.status !== 'Planned') ? m.status : 'Pending',
                        completionCriteria: m.completionCriteria || '',
                        owner: m.owner || form.projectManager || form.owner || null,
                        creMilestoneId: m.creMilestoneId || '',
                        deliverables: Array.isArray(m.deliverables) ? m.deliverables : [],
                        deliverableChecklist: Array.isArray(m.deliverableChecklist) ? m.deliverableChecklist : [],
                    };
                    if (m._id) item._id = m._id;
                    if (m.id) item.id = m.id;
                    return item;
                }),
                tasks: form.tasks.map((t) => ({
                    id: t.id || t._id,
                    name: t.name || t.title,
                    description: t.description || '',
                    milestoneId: t.milestoneId || '',
                    parentId: t.parentId || null,
                    assignee: t.assignee || '',
                    priority: t.priority || 'Medium',
                    estimatedHours: t.estimatedHours || 4,
                })),
                documents: form.documents,
                assets: form.assets,
                requirementChecklist: form.requirementChecklist || [],
                workflowTemplate: form.sprintManagement ? 'Sprint Workflow' : 'Standard Pipeline',
                autoSprintPlanning: form.sprintManagement,
                sprintManagement: form.sprintManagement,
                availableViews: form.availableViews,
                defaultView: form.defaultView,
                customFields: {
                    defaultView: form.defaultView,
                    availableViews: form.availableViews,
                    sprintManagement: form.sprintManagement,
                },
                isDraft: isDraftMode,
                creProjectId: form.creProjectId || projectToEdit?.creProjectId || '',
                projectRequestId: projectToEdit?.pcrNumber || projectToEdit?.creProjectId ? projectToEdit._id : '',
                pcrNumber: projectToEdit?.pcrNumber || '',
            };

            const isExistingProject = Boolean(
                projectToEdit &&
                projectToEdit._id &&
                projectToEdit.projectCode &&
                projectToEdit.status !== 'Pending Review'
            );

            if (isExistingProject) {
                const { data } = await updateProject(projectToEdit._id, payload);
                const updated = data?.result || { _id: projectToEdit._id, ...payload };
                message.success(isDraftMode ? 'Draft updated successfully!' : 'Project updated and published successfully!');
                onSuccess?.(updated);
            } else {
                const { data } = await createProject(payload);
                const created = data?.result || payload;
                const newProjectId = created?._id;

                // If user uploaded any files during project creation, upload them directly to R2 for this project
                const pendingDocs = (form.documents || []).filter((d) => d._rawFile);
                const pendingAssets = (form.assets || []).filter((a) => a._rawFile);
                const allPending = [...pendingDocs, ...pendingAssets];

                if (newProjectId && allPending.length > 0) {
                    allPending.forEach((item) => {
                        const formData = new FormData();
                        formData.append('file', item._rawFile);
                        formData.append('projectId', newProjectId);
                        formData.append('folderCode', item._folderCode || '01');
                        formData.append('category', item._category || 'Project Document');
                        formData.append('description', 'Uploaded during Project Creation');
                        uploadProjectFile(formData).catch((err) =>
                            console.error('Failed to upload pending file to R2 on project creation:', err)
                        );
                    });
                }

                message.success(isDraftMode ? 'Project saved as draft!' : `Project created successfully! Project ID: ${created.projectCode || form.projectCode}`);
                onSuccess?.(created);
            }
            setForm(INITIAL_FORM);
            onClose();
        } catch (err) {
            message.error(err?.response?.data?.message || 'Failed to process project.');
        } finally {
            setLoading(false);
        }
    };

    const inputClass = 'w-full bg-[#f8fafc] dark:bg-[#334155] border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 text-[#191c1d] dark:text-white text-base focus:ring-2 focus:ring-[#03488a] transition-all outline-none';

    return (
        <>
            <Modal
                open={open}
                onCancel={onClose}
                footer={null}
                width="min(1320px, 95vw)"
                style={{ maxWidth: '95vw', top: 20 }}
                destroyOnClose
                centered
                closable={false}
                styles={{ content: { padding: 0, borderRadius: 16, overflow: 'hidden' }, body: { padding: 0 } }}
            >
                <div className="bg-white dark:bg-[#111827] flex flex-col h-[88vh] max-h-[92vh] overflow-hidden text-slate-800 dark:text-slate-200 font-sans">

                    {/* ── Header & Step Indicator ── */}
                    <div className="p-6 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex flex-col gap-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-2xl font-bold text-[#003263] dark:text-[#a7c8ff] tracking-tight">
                                    {projectToEdit
                                        ? (projectToEdit.isDraft || projectToEdit.status === 'Draft' ? 'Complete Project Draft' : 'Edit Project Details')
                                        : 'Create New Project'}
                                </h2>
                                <p className="text-base text-slate-500 dark:text-slate-400 mt-0.5 font-medium">Define project information, milestones, assets, and execution workflow</p>
                            </div>
                            <div className="flex items-center gap-2">
                                {isDemoEnvironment && (
                                    <button
                                        type="button"
                                        onClick={handleFillDemoData}
                                        className="px-3 py-1.5 rounded-xl text-xs font-bold text-amber-700 dark:text-amber-300 bg-amber-100/90 dark:bg-amber-950/70 border border-amber-300 dark:border-amber-800 hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-all flex items-center gap-1.5 shadow-sm shrink-0"
                                        title="Auto-fill rich realistic scenario data for QA & presentations"
                                    >
                                        <span>⚡ Quick Fill Demo Data</span>
                                    </button>
                                )}
                                <button onClick={onClose} className="p-2 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-full transition-colors">
                                    <CloseOutlined className="text-slate-600 dark:text-slate-400 text-lg" />
                                </button>
                            </div>
                        </div>

                        {/* Stepper Header */}
                        <div className="grid grid-cols-5 gap-2.5 pt-2">
                            {[
                                { step: 1, label: 'Information', icon: <FileTextOutlined /> },
                                { step: 2, label: 'Milestones & Tasks', icon: loadingTasks ? <LoadingOutlined spin /> : <RocketOutlined /> },
                                { step: 3, label: 'Documents & Assets', icon: <InboxOutlined /> },
                                { step: 4, label: 'Configuration', icon: <SettingOutlined /> },
                                { step: 5, label: 'Review & Create', icon: <CheckCircleOutlined /> },
                            ].map((s) => (
                                <button
                                    key={s.step}
                                    onClick={() => setCurrentStep(s.step)}
                                    className={`flex items-center gap-2.5 p-3 rounded-xl border text-base font-bold transition-all text-left ${currentStep === s.step
                                        ? 'bg-[#03488a] text-white border-[#03488a] shadow-md'
                                        : currentStep > s.step
                                            ? 'bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                                            : 'bg-white dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700'
                                        }`}
                                >
                                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-white/20 shrink-0 text-xs font-black">
                                        {s.step}
                                    </span>
                                    <span className="truncate">{s.label}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* ── Scrollable Body Step Content ── */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar text-base">

                        {/* ════════════════ Step 1: Project Information ════════════════ */}
                        {currentStep === 1 && (
                            <div className="space-y-6">
                                {/* Basic Info */}
                                <div className="space-y-4 bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                    <h3 className="text-sm font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Basic Information</h3>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="md:col-span-2 space-y-1.5">
                                            <label className="text-base font-bold text-slate-700 dark:text-slate-200">Project Name <span className="text-red-500">*</span></label>
                                            <input
                                                className={inputClass}
                                                placeholder="e.g. ERP Attendance Module Revamp"
                                                value={form.name}
                                                onChange={(e) => handleChange('name', e.target.value)}
                                                autoFocus
                                            />
                                        </div>
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Project Code (Auto-generated)</label>
                                            <input className={`${inputClass} bg-slate-100 dark:bg-slate-800 font-mono text-slate-500`} value={form.projectCode} disabled />
                                        </div>
                                    </div>

                                    {/* Rich Text Description */}
                                    <div className="space-y-1.5">
                                        <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Project Description <span className="text-red-500">*</span></label>
                                        <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 bg-white dark:bg-[#1e293b]">
                                            <style>{`
                                            .ql-container.ql-snow { border: none !important; height: 120px; font-size: 14px; }
                                            .ql-toolbar.ql-snow { border: none !important; border-bottom: 1px solid #e2e8f0 !important; background: #f8fafc; }
                                            .dark .ql-toolbar.ql-snow { background: #111827; border-bottom: 1px solid #374151 !important; }
                                        `}</style>
                                            <ReactQuill
                                                theme="snow"
                                                value={form.description}
                                                onChange={(val) => handleChange('description', val)}
                                                modules={quillModules}
                                                placeholder="Enter project scope and operational description…"
                                            />
                                        </div>
                                    </div>

                                    {/* Operating Brand / Company Selection */}
                                    <div className="space-y-1.5">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">
                                                Operating Brand / Organization <span className="text-red-500">*</span>
                                            </label>
                                            {!form.isClientProject && (
                                                <span className="text-xs font-semibold text-[#006a61] dark:text-[#89f5e7] flex items-center gap-1 bg-[#006a61]/10 px-2 py-0.5 rounded-full">
                                                    <span className="material-symbols-outlined text-xs">folder_special</span>
                                                    Routes into {form.brandName || 'Brand'}'s Portfolios in Internal Work
                                                </span>
                                            )}
                                        </div>
                                        <select
                                            className={inputClass}
                                            value={form.brandId}
                                            onChange={(e) => {
                                                const selectedBrand = INTERNAL_BRANDS.find((b) => b.id === e.target.value);
                                                setForm((prev) => ({
                                                    ...prev,
                                                    brandId: e.target.value,
                                                    brandName: selectedBrand ? selectedBrand.name : 'DymaxTech',
                                                }));
                                            }}
                                        >
                                            {INTERNAL_BRANDS.map((b) => (
                                                <option key={b.id} value={b.id}>
                                                    {b.name} ({b.desc})
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        {/* Type */}
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Project Type <span className="text-red-500">*</span></label>
                                            <div className="flex bg-slate-200 dark:bg-slate-800 p-1 rounded-xl">
                                                <button
                                                    type="button"
                                                    onClick={() => setForm(prev => ({ ...prev, isClientProject: false, projectType: 'In-house' }))}
                                                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${!form.isClientProject ? 'bg-white dark:bg-slate-700 text-[#003263] dark:text-white shadow-sm' : 'text-slate-500'}`}
                                                >
                                                    In-house
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setForm(prev => ({ ...prev, isClientProject: true, projectType: 'Client' }))}
                                                    className={`flex-1 py-2 text-sm font-bold rounded-lg transition-all ${form.isClientProject ? 'bg-white dark:bg-slate-700 text-[#003263] dark:text-white shadow-sm' : 'text-slate-500'}`}
                                                >
                                                    Client
                                                </button>
                                            </div>
                                        </div>

                                        {/* Category */}
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Category <span className="text-red-500">*</span></label>
                                            <select
                                                className={inputClass}
                                                value={form.category}
                                                onChange={(e) => {
                                                    const newCat = e.target.value;
                                                    const firstSub = CATEGORIES_WITH_SUBCATEGORIES[newCat]?.[0] || '';
                                                    setForm(prev => ({ ...prev, category: newCat, subCategory: firstSub }));
                                                }}
                                            >
                                                <option value="">-- Select Main Category --</option>
                                                {Object.keys(CATEGORIES_WITH_SUBCATEGORIES).map((cat) => (
                                                    <option key={cat} value={cat}>{cat}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Sub-Category (Dependent on Category) */}
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Sub-Category <span className="text-red-500">*</span></label>
                                            <select
                                                className={`${inputClass} ${!form.category ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800' : ''}`}
                                                value={form.subCategory}
                                                disabled={!form.category}
                                                onChange={(e) => handleChange('subCategory', e.target.value)}
                                            >
                                                {!form.category ? (
                                                    <option value="">-- Select Category First --</option>
                                                ) : (
                                                    (CATEGORIES_WITH_SUBCATEGORIES[form.category] || []).map((sub) => (
                                                        <option key={sub} value={sub}>{sub}</option>
                                                    ))
                                                )}
                                            </select>
                                        </div>

                                        {/* Department */}
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Department <span className="text-red-500">*</span></label>
                                            <select className={inputClass} value={form.department} onChange={(e) => handleChange('department', e.target.value)}>
                                                {DEPARTMENTS.map((d) => <option key={d} value={d}>{d}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Priority */}
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Priority</label>
                                            <select className={inputClass} value={form.priority} onChange={(e) => handleChange('priority', e.target.value)}>
                                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                            </select>
                                        </div>

                                        {/* Color Swatches */}
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Color Tag</label>
                                            <div className="flex items-center gap-2 pt-1.5">
                                                {COLOR_SWATCHES.slice(0, 5).map((color) => (
                                                    <button
                                                        key={color}
                                                        type="button"
                                                        onClick={() => handleChange('color', color)}
                                                        className={`w-7 h-7 rounded-full transition-all ${form.color === color ? 'ring-2 ring-offset-2 ring-[#03488a] scale-110' : ''}`}
                                                        style={{ backgroundColor: color }}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Questionnaire Reference Section (Exact 2-State Design) */}
                                    <div className="space-y-3 bg-blue-50/70 dark:bg-slate-800/50 p-4 rounded-xl border border-blue-200 dark:border-slate-700">
                                        <div className="flex justify-between items-center">
                                            <div className="flex items-center gap-2">
                                                <FileTextOutlined className="text-blue-600 text-base" />
                                                <label className="text-sm font-bold text-slate-800 dark:text-slate-100">Project Questionnaire</label>
                                            </div>
                                            <span className="text-xs text-slate-400 font-medium">Optional</span>
                                        </div>

                                        {/* State 1: When questionnaire is linked / exists */}
                                        {(form.questionnaires && form.questionnaires.length > 0) || form.questionnaireTemplateName ? (
                                            <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                                                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                                    <div className="space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Primary Questionnaire</span>
                                                            <Tag color="emerald" className="font-bold text-[10px] mr-0">Linked ↗</Tag>
                                                        </div>
                                                        <p className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                            {(form.questionnaires && form.questionnaires[0]) || form.questionnaireTemplateName}
                                                        </p>
                                                        <p className="text-xs text-slate-400">
                                                            Submitted: 15 Aug 2026 {form.clientName ? `• Submitted By: ${form.clientName}` : ''}
                                                        </p>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        {form.questionnaireUrl ? (
                                                            <a
                                                                href={form.questionnaireUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-all shadow-sm"
                                                            >
                                                                View ↗
                                                            </a>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => message.info('Associated questionnaire reference linked.')}
                                                                className="px-3 py-1.5 bg-[#03488a] text-white rounded-lg text-xs font-bold hover:opacity-90 transition-all shadow-sm"
                                                            >
                                                                View ↗
                                                            </button>
                                                        )}
                                                        <button
                                                            type="button"
                                                            onClick={handleUnlinkQuestionnaire}
                                                            className="px-3 py-1.5 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 rounded-lg text-xs font-bold hover:bg-slate-200 transition-all"
                                                        >
                                                            Clear All
                                                        </button>
                                                    </div>
                                                </div>

                                                {/* Display all linked questionnaire chips */}
                                                {form.questionnaires && form.questionnaires.length > 0 && (
                                                    <div className="border-t border-slate-100 dark:border-slate-800 pt-3 space-y-2">
                                                        <p className="text-xs font-bold text-slate-600 dark:text-slate-300">All Linked Questionnaires ({form.questionnaires.length}):</p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {form.questionnaires.map((q, idx) => (
                                                                <div key={idx} className="flex items-center gap-2 bg-blue-50 dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-blue-200 dark:border-slate-700 text-xs font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                                    <span>📋 {q}</span>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveQuestionnaire(idx)}
                                                                        className="text-slate-400 hover:text-red-500 font-bold ml-1"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Inline Add More Section (Presets + Manual Input) */}
                                                <div className="border-t border-slate-100 dark:border-slate-800 pt-3 space-y-2">
                                                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300">+ Link Additional Questionnaire or Type Manual Reference:</p>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {EXISTING_QUESTIONNAIRE_TEMPLATES.map((tmpl) => (
                                                            <button
                                                                key={tmpl}
                                                                type="button"
                                                                onClick={() => handleSelectQuestionnaireTemplate(tmpl)}
                                                                className="px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-md text-[11px] font-semibold hover:bg-blue-100 hover:text-[#003263] transition-all"
                                                            >
                                                                + {tmpl}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div className="flex items-center gap-2 pt-1">
                                                        <input
                                                            type="text"
                                                            className={`${inputClass} text-xs py-1.5 flex-1`}
                                                            placeholder="Type manual questionnaire reference (e.g. Brand Spec Sheet, Custom Scope Form)..."
                                                            value={questionnaireInput}
                                                            onChange={(e) => setQuestionnaireInput(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    handleAddQuestionnaire();
                                                                }
                                                            }}
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => handleAddQuestionnaire()}
                                                            className="px-3.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-bold hover:bg-blue-700 transition-all shrink-0"
                                                        >
                                                            + Add More
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* State 2: When creating manually (Unlinked) */
                                            <div>
                                                {!showLinkSelector ? (
                                                    <div className="flex items-center justify-between bg-white dark:bg-slate-900 p-4 rounded-xl border border-dashed border-blue-200 dark:border-slate-700">
                                                        <div className="space-y-0.5">
                                                            <p className="text-xs font-bold text-slate-700 dark:text-slate-200">No Questionnaire Linked</p>
                                                            <p className="text-[11px] text-slate-400">Client projects may originate from CRE questionnaires. In-house projects can remain unlinked.</p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => setShowLinkSelector(true)}
                                                            className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shadow-sm shrink-0"
                                                        >
                                                            + Link Questionnaire
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className="bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-3">
                                                        <div className="flex justify-between items-center">
                                                            <p className="text-xs font-bold text-[#003263] dark:text-[#a7c8ff]">Select Existing Questionnaire Template or Type Custom Reference:</p>
                                                            <button type="button" onClick={() => setShowLinkSelector(false)} className="text-xs font-bold text-slate-400 hover:text-slate-600">Cancel</button>
                                                        </div>

                                                        {/* Pre-defined Template Presets */}
                                                        <div className="flex flex-wrap gap-2">
                                                            {EXISTING_QUESTIONNAIRE_TEMPLATES.map((tmpl) => (
                                                                <button
                                                                    key={tmpl}
                                                                    type="button"
                                                                    onClick={() => handleSelectQuestionnaireTemplate(tmpl)}
                                                                    className="px-3 py-1.5 bg-blue-50 dark:bg-slate-800 text-[#003263] dark:text-[#a7c8ff] rounded-lg text-xs font-bold border border-blue-200 dark:border-slate-700 hover:bg-blue-100 transition-all"
                                                                >
                                                                    📋 {tmpl}
                                                                </button>
                                                            ))}
                                                        </div>

                                                        {/* Custom Reference Input */}
                                                        <div className="flex items-center gap-2 pt-2">
                                                            <input
                                                                type="text"
                                                                className={`${inputClass} text-xs py-2 flex-1`}
                                                                placeholder="Or type custom questionnaire reference name..."
                                                                value={questionnaireInput}
                                                                onChange={(e) => setQuestionnaireInput(e.target.value)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') {
                                                                        e.preventDefault();
                                                                        handleAddQuestionnaire();
                                                                    }
                                                                }}
                                                            />
                                                            <button
                                                                type="button"
                                                                onClick={() => handleAddQuestionnaire()}
                                                                className="px-4 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-all shrink-0"
                                                            >
                                                                Link Questionnaire
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Schedule & Duration Controls */}
                                    <div className="space-y-3 bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
                                        <div className="flex items-center justify-between">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Project Duration <span className="text-red-500">*</span></label>
                                            <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl">
                                                <button
                                                    type="button"
                                                    onClick={() => handleChange('durationMode', 'Fixed')}
                                                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${form.durationMode === 'Fixed' ? 'bg-white dark:bg-slate-700 text-[#003263] dark:text-white shadow-sm' : 'text-slate-500'}`}
                                                >
                                                    Fixed
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleChange('durationMode', 'Ongoing')}
                                                    className={`px-4 py-1.5 text-xs font-bold rounded-lg transition-all ${form.durationMode === 'Ongoing' ? 'bg-white dark:bg-slate-700 text-[#003263] dark:text-white shadow-sm' : 'text-slate-500'}`}
                                                >
                                                    Ongoing
                                                </button>
                                            </div>
                                        </div>

                                        {form.durationMode === 'Fixed' ? (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Start Date <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="date"
                                                        className={inputClass}
                                                        value={form.startDate}
                                                        onChange={(e) => handleChange('startDate', e.target.value)}
                                                    />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">End Date <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="date"
                                                        className={inputClass}
                                                        value={form.dueDate}
                                                        onChange={(e) => handleChange('dueDate', e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="w-full md:w-1/2 space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider">Start Date <span className="text-red-500">*</span></label>
                                                    <input
                                                        type="date"
                                                        className={inputClass}
                                                        value={form.startDate}
                                                        onChange={(e) => handleChange('startDate', e.target.value)}
                                                    />
                                                </div>
                                                <div className="p-3 bg-blue-50/80 dark:bg-blue-950/40 rounded-xl border border-blue-200 dark:border-blue-800 flex items-center gap-2 text-xs text-blue-700 dark:text-blue-300 font-medium">
                                                    <InfoCircleOutlined className="text-blue-600 text-base shrink-0" />
                                                    <span>ℹ This ongoing project has no predefined completion date and will remain active until manually completed, placed on hold, or canceled.</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Ownership & Team */}
                                <div className="space-y-4 bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                    <h3 className="text-sm font-black text-slate-500 uppercase tracking-wider">Ownership &amp; Team</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Project Owner <span className="text-red-500">*</span></label>
                                            <select className={inputClass} value={form.owner} onChange={(e) => handleChange('owner', e.target.value)}>
                                                {employees.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Project Manager <span className="text-red-500">*</span></label>
                                            <select className={inputClass} value={form.projectManager} onChange={(e) => handleChange('projectManager', e.target.value)}>
                                                {employees.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Collaborators / Stakeholders</label>
                                            <Select
                                                mode="multiple"
                                                placeholder="Select collaborators..."
                                                value={form.collaborators}
                                                onChange={(vals) => handleChange('collaborators', vals)}
                                                className="w-full"
                                                size="large"
                                            >
                                                {employees.map((e) => (
                                                    <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Status</label>
                                            <select className={inputClass} value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                                                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                            </select>
                                        </div>

                                        <div className="space-y-1.5">
                                            <label className="text-sm font-bold text-slate-700 dark:text-slate-200">Health</label>
                                            <select className={inputClass} value={form.health} onChange={(e) => handleChange('health', e.target.value)}>
                                                {HEALTH_OPTIONS.map((h) => <option key={h} value={h}>{h}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Smart Goals (AI-Driven) */}
                                <div className="space-y-4 bg-gradient-to-r from-blue-50/50 to-indigo-50/50 dark:from-slate-900 dark:to-blue-950/20 p-5 rounded-2xl border border-blue-200/60 dark:border-blue-900/40">
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <AimOutlined className="text-blue-600 text-lg" />
                                                <h3 className="text-sm font-bold text-slate-800 dark:text-white">Smart Goals (Project-Level)</h3>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">AI analyzes project info to generate targeted smart goals with criteria</p>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={handleGenerateSmartGoals}
                                                disabled={aiGeneratingGoals}
                                                className="px-3.5 py-2 rounded-xl text-xs font-bold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 transition-all flex items-center gap-1.5 shadow-sm disabled:opacity-50"
                                            >
                                                {aiGeneratingGoals ? <LoadingOutlined /> : <RobotOutlined />}
                                                {aiGeneratingGoals ? 'Analyzing & Generating...' : 'Create Smart Goals with AI'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={openAddGoalGuarded}
                                                className="px-3.5 py-2 rounded-xl text-xs font-bold text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 transition-all flex items-center gap-1"
                                            >
                                                <PlusOutlined /> Add Goal Manually
                                            </button>
                                        </div>
                                    </div>

                                    {/* Generated Smart Goals Cards List */}
                                    {form.smartGoals.length > 0 ? (
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5 pt-2">
                                            {form.smartGoals.map((g) => (
                                                <div key={g.id} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2.5 relative group shadow-sm flex flex-col justify-between">
                                                    <div className="space-y-1.5">
                                                        <div className="flex justify-between items-start gap-2">
                                                            <span className="text-sm font-bold text-[#003263] dark:text-[#a7c8ff] line-clamp-2 leading-snug">{g.name}</span>
                                                            <div className="flex items-center gap-1.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openEditGoal(g)}
                                                                    className="text-slate-400 hover:text-blue-500 transition-colors p-0.5"
                                                                    title="Edit Goal"
                                                                >
                                                                    <EditOutlined style={{ fontSize: 13 }} />
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleRemoveGoal(g.id)}
                                                                    className="text-slate-400 hover:text-red-500 transition-colors p-0.5"
                                                                    title="Remove Goal"
                                                                >
                                                                    <DeleteOutlined style={{ fontSize: 13 }} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <p className="text-xs text-slate-600 dark:text-slate-300 line-clamp-3 leading-relaxed">{g.description}</p>
                                                    </div>

                                                    <div className="pt-2 border-t border-slate-100 dark:border-slate-700/60 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400 font-medium">
                                                        <span>Target: {g.targetDate || 'Project Due'}</span>
                                                        <Tag color={g.priority === 'Critical' ? 'error' : g.priority === 'High' ? 'warning' : 'blue'} className="mr-0 text-xs font-semibold px-2 py-0.5 rounded-md">
                                                            {g.priority || 'Medium'}
                                                        </Tag>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4 border-2 border-dashed border-blue-200 dark:border-blue-900/40 rounded-xl bg-white/50 dark:bg-slate-900/20">
                                            <p className="text-xs text-slate-400 font-medium">No Smart Goals generated yet. Click above to let AI analyze your project!</p>
                                        </div>
                                    )}
                                </div>

                                {/* Intake Requirements Checklist (From CRE Webhook) */}
                                {Array.isArray(form.requirementChecklist) && form.requirementChecklist.length > 0 && (() => {
                                    const totalCount = form.requirementChecklist.length;
                                    const metCount = form.requirementChecklist.filter(x => x.met).length;
                                    const pendingReqCount = form.requirementChecklist.filter(x => x.required && !x.met).length;
                                    const needsActionCount = form.requirementChecklist.filter(x => {
                                        const isResub = (x.status === 'Resubmitted' || x.decision === 'Resubmitted') && !x.met && !x.rejected;
                                        const isPendingReq = x.required && !x.met && !x.rejected;
                                        return isResub || isPendingReq;
                                    }).length;
                                    const rejectedCount = form.requirementChecklist.filter(x => x.rejected || x.status === 'Rejected').length;
                                    const approvedCount = metCount;

                                    const bulkMenuItems = [
                                        {
                                            key: 'approve_all',
                                            icon: <CheckCircleOutlined className="text-emerald-600" />,
                                            label: 'Approve All Requirements',
                                            onClick: () => {
                                                setForm(prev => ({
                                                    ...prev,
                                                    requirementChecklist: prev.requirementChecklist.map(item => ({
                                                        ...item,
                                                        met: true,
                                                        rejected: false,
                                                        status: 'Approved',
                                                        decision: 'Approved',
                                                        approvedBy: 'Project Manager',
                                                        approvedAt: new Date().toISOString(),
                                                        isFeedbackSynced: false,
                                                    }))
                                                }));
                                                const allKeys = new Set((form.requirementChecklist || []).map((item, idx) => getChecklistItemKey(item, idx)));
                                                setSelectedChecklistKeys(allKeys);
                                                message.success('All requirements marked Approved & queued for CRE sync.');
                                            },
                                        },
                                        {
                                            key: 'reset_all',
                                            icon: <ReloadOutlined className="text-slate-500" />,
                                            label: 'Reset All to Pending',
                                            onClick: () => {
                                                setForm(prev => ({
                                                    ...prev,
                                                    requirementChecklist: prev.requirementChecklist.map(item => ({
                                                        ...item,
                                                        met: false,
                                                        rejected: false,
                                                        status: 'Pending',
                                                        decision: 'Pending',
                                                        isFeedbackSynced: false,
                                                    }))
                                                }));
                                                setSelectedChecklistKeys(new Set());
                                                message.info('All requirements reset to Pending.');
                                            },
                                        },
                                    ];

                                    const filteredItems = form.requirementChecklist.map((req, rIdx) => ({ req, rIdx })).filter(({ req }) => {
                                        if (checklistFilter === 'needs_action') {
                                            const isResub = (req.status === 'Resubmitted' || req.decision === 'Resubmitted') && !req.met && !req.rejected;
                                            const isPendingReq = req.required && !req.met && !req.rejected;
                                            return isResub || isPendingReq;
                                        }
                                        if (checklistFilter === 'rejected') {
                                            return Boolean(req.rejected || req.status === 'Rejected');
                                        }
                                        if (checklistFilter === 'approved') {
                                            return Boolean(req.met || req.status === 'Approved');
                                        }
                                        return true;
                                    });

                                    return (
                                        <div className="space-y-3.5 bg-slate-50/70 dark:bg-slate-900/60 p-4 sm:p-5 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                                            {/* Header */}
                                            <div className="flex items-center justify-between flex-wrap gap-3 pb-3 border-b border-slate-200/80 dark:border-slate-800">
                                                <div>
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <h3 className="text-sm sm:text-base font-extrabold text-slate-800 dark:text-white tracking-tight">
                                                            Intake Requirements Checklist
                                                        </h3>
                                                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-slate-200/80 dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                                                            {metCount}/{totalCount} Met
                                                        </span>
                                                        {pendingReqCount > 0 ? (
                                                            <span className="px-2.5 py-0.5 rounded-full font-bold text-xs bg-amber-100 dark:bg-amber-950/70 text-amber-900 dark:text-amber-200 border border-amber-300 dark:border-amber-700 flex items-center gap-1">
                                                                ⚠️ {pendingReqCount} Required Pending
                                                            </span>
                                                        ) : (
                                                            <span className="px-2.5 py-0.5 rounded-full font-bold text-xs bg-emerald-100 dark:bg-emerald-950/70 text-emerald-900 dark:text-emerald-200 border border-emerald-300 dark:border-emerald-700 flex items-center gap-1">
                                                                ✓ All Required Met
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                                        Client requirements & credentials submitted from CRE intake
                                                    </p>
                                                </div>

                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <Dropdown menu={{ items: bulkMenuItems }} trigger={['click']}>
                                                        <button
                                                            type="button"
                                                            className="px-3 py-1.5 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-750 text-slate-700 dark:text-slate-200 font-semibold text-xs rounded-xl border border-slate-300 dark:border-slate-700 transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer active:scale-95"
                                                        >
                                                            <span>Bulk Actions</span>
                                                            <DownOutlined className="text-[9px] text-slate-400" />
                                                        </button>
                                                    </Dropdown>

                                                    <Tooltip
                                                        title={
                                                            canSendFeedbackToCRE
                                                                ? `Send checklist feedback for ${selectedChecklistKeys.size} item(s) to CRE webhook`
                                                                : 'Make changes to requirements to send feedback to CRE.'
                                                        }
                                                    >
                                                        <button
                                                            type="button"
                                                            onClick={handleSyncChecklistToCRE}
                                                            disabled={!canSendFeedbackToCRE || syncingChecklist}
                                                            className={`px-4 py-1.5 font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 shadow-xs ${
                                                                canSendFeedbackToCRE
                                                                    ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-sky-600 hover:from-blue-700 hover:via-indigo-700 hover:to-sky-700 text-white border border-indigo-400 dark:border-indigo-500 cursor-pointer active:scale-95'
                                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-400 dark:text-slate-500 border border-slate-200 dark:border-slate-700 cursor-not-allowed opacity-60'
                                                            }`}
                                                        >
                                                            {syncingChecklist ? <LoadingOutlined className="text-xs" /> : <BranchesOutlined className="text-xs" />}
                                                            <span>Send Feedback to CRE</span>
                                                            {selectedChecklistKeys.size > 0 && (
                                                                <span className="px-1.5 py-0.2 text-[10px] font-black bg-white/30 rounded-full text-white">
                                                                    {selectedChecklistKeys.size}
                                                                </span>
                                                            )}
                                                        </button>
                                                    </Tooltip>
                                                </div>
                                            </div>

                                            {/* Quick Filter Tabs */}
                                            <div className="flex items-center gap-1.5 p-1 bg-slate-100/90 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700/80 text-xs w-fit">
                                                <button
                                                    type="button"
                                                    onClick={() => setChecklistFilter('all')}
                                                    className={`px-3 py-1 rounded-lg font-bold transition-all cursor-pointer ${
                                                        checklistFilter === 'all'
                                                            ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs'
                                                            : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                                                    }`}
                                                >
                                                    All ({totalCount})
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setChecklistFilter('needs_action')}
                                                    className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                                        checklistFilter === 'needs_action'
                                                            ? 'bg-white dark:bg-slate-700 text-amber-700 dark:text-amber-300 shadow-xs'
                                                            : 'text-slate-600 dark:text-slate-400 hover:text-amber-600'
                                                    }`}
                                                >
                                                    <span>Needs Action</span>
                                                    {needsActionCount > 0 && (
                                                        <span className="px-1.5 py-0.2 bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 text-[10px] rounded-full font-black">
                                                            {needsActionCount}
                                                        </span>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setChecklistFilter('rejected')}
                                                    className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                                        checklistFilter === 'rejected'
                                                            ? 'bg-white dark:bg-slate-700 text-rose-700 dark:text-rose-300 shadow-xs'
                                                            : 'text-slate-600 dark:text-slate-400 hover:text-rose-600'
                                                    }`}
                                                >
                                                    <span>Rejected</span>
                                                    {rejectedCount > 0 && (
                                                        <span className="px-1.5 py-0.2 bg-rose-100 dark:bg-rose-900/60 text-rose-800 dark:text-rose-200 text-[10px] rounded-full font-black">
                                                            {rejectedCount}
                                                        </span>
                                                    )}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setChecklistFilter('approved')}
                                                    className={`px-3 py-1 rounded-lg font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                                                        checklistFilter === 'approved'
                                                            ? 'bg-white dark:bg-slate-700 text-emerald-700 dark:text-emerald-300 shadow-xs'
                                                            : 'text-slate-600 dark:text-slate-400 hover:text-emerald-600'
                                                    }`}
                                                >
                                                    <span>Approved</span>
                                                    <span className="text-[10px] text-slate-400">({approvedCount})</span>
                                                </button>
                                            </div>

                                            {/* Checklist Items List */}
                                            <div className="space-y-2.5 pt-0.5">
                                                {filteredItems.map(({ req, rIdx }) => {
                                                    const key = getChecklistItemKey(req, rIdx);
                                                    const isSelected = selectedChecklistKeys.has(key);
                                                    const isSynced = Boolean(req.isFeedbackSynced);

                                                    const isResubmitted = (req.status === 'Resubmitted' || req.decision === 'Resubmitted') && !req.met && !req.rejected;
                                                    const isApproved = (req.status === 'Approved' || req.decision === 'Approved' || Boolean(req.met)) && !isResubmitted;
                                                    const isRejected = (req.status === 'Rejected' || req.decision === 'Rejected' || Boolean(req.rejected)) && !isResubmitted;
                                                    const isPending = !isApproved && !isRejected && !isResubmitted;

                                                    return (
                                                        <div
                                                            key={rIdx}
                                                            className={`p-3 sm:p-3.5 rounded-xl border transition-all ${
                                                                isResubmitted
                                                                    ? 'bg-blue-50/50 dark:bg-blue-950/20 border-blue-300 dark:border-blue-800 shadow-2xs'
                                                                    : isApproved
                                                                    ? 'bg-white dark:bg-slate-900/40 border-emerald-300/80 dark:border-emerald-800/60 shadow-2xs'
                                                                    : isRejected
                                                                    ? 'bg-rose-50/40 dark:bg-rose-950/20 border-rose-300 dark:border-rose-800/60 shadow-2xs'
                                                                    : 'bg-white dark:bg-slate-900/40 border-slate-200 dark:border-slate-800 shadow-2xs'
                                                            }`}
                                                        >
                                                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                                                                {/* Status Icon + Title + Badges */}
                                                                <div className="flex items-start gap-2.5 flex-1 min-w-0">
                                                                    <div className="shrink-0 mt-0.5">
                                                                        {isResubmitted ? (
                                                                            <div className="w-6 h-6 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 flex items-center justify-center font-bold text-xs" title="Resubmitted by client / CRE">
                                                                                🔄
                                                                            </div>
                                                                        ) : isApproved ? (
                                                                            <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-200 flex items-center justify-center font-bold text-xs" title="Approved">
                                                                                ✓
                                                                            </div>
                                                                        ) : isRejected ? (
                                                                            <div className="w-6 h-6 rounded-full bg-rose-100 text-rose-700 dark:bg-rose-900 dark:text-rose-200 flex items-center justify-center font-bold text-xs" title="Rejected">
                                                                                ✕
                                                                            </div>
                                                                        ) : (
                                                                            <div className="w-6 h-6 rounded-full bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500 border border-slate-300 dark:border-slate-700 flex items-center justify-center font-bold text-xs" title="Pending Verification">
                                                                                ○
                                                                            </div>
                                                                        )}
                                                                    </div>

                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-1.5 flex-wrap">
                                                                            <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
                                                                                {req.label}
                                                                            </span>
                                                                            {req.category && (
                                                                                <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                                                                                    {req.category}
                                                                                </span>
                                                                            )}
                                                                            {req.required ? (
                                                                                <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800">
                                                                                    Required
                                                                                </span>
                                                                            ) : (
                                                                                <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                                                                                    Optional
                                                                                </span>
                                                                            )}
                                                                            {isResubmitted && (
                                                                                <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-300 border border-blue-200 dark:border-blue-800">
                                                                                    🔄 Review Needed
                                                                                </span>
                                                                            )}
                                                                            {isSelected && !isSynced && (
                                                                                <span className="px-2 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                                                                                    Queued for CRE
                                                                                </span>
                                                                            )}
                                                                            {isSynced && (
                                                                                <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300">
                                                                                    ✓ Synced
                                                                                </span>
                                                                            )}
                                                                        </div>

                                                                        {req.detail && (
                                                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                                                                                {req.detail}
                                                                            </p>
                                                                        )}

                                                                        {/* Collapsible details preview for Resubmission Fix Note or Rejection Note */}
                                                                        {(isResubmitted || isRejected || req.rejectionNote || req.fixNote) && (
                                                                            <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                                                                                {isResubmitted && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleExpandChecklistNote(key)}
                                                                                        className="text-blue-700 dark:text-blue-300 hover:underline font-semibold flex items-center gap-1 text-[11px] cursor-pointer"
                                                                                    >
                                                                                        <span>🛠️ Fix Note: {req.fixNote ? `"${req.fixNote.slice(0, 45)}..."` : 'View CRE updates'}</span>
                                                                                        {expandedChecklistNotes.has(key) ? <DownOutlined className="text-[9px]" /> : <RightOutlined className="text-[9px]" />}
                                                                                    </button>
                                                                                )}
                                                                                {isRejected && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleExpandChecklistNote(key)}
                                                                                        className="text-rose-700 dark:text-rose-300 hover:underline font-semibold flex items-center gap-1 text-[11px] cursor-pointer"
                                                                                    >
                                                                                        <span>⚠️ Rejection Note: "{((req.rejectionNote || req.note) || '').slice(0, 45)}..."</span>
                                                                                        {expandedChecklistNotes.has(key) ? <DownOutlined className="text-[9px]" /> : <RightOutlined className="text-[9px]" />}
                                                                                    </button>
                                                                                )}
                                                                                {isRejected && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => handleOpenRejectItemModal(rIdx)}
                                                                                        className="text-[10px] text-rose-600 dark:text-rose-400 font-bold hover:underline cursor-pointer"
                                                                                    >
                                                                                        (Edit Note)
                                                                                    </button>
                                                                                )}
                                                                            </div>
                                                                        )}

                                                                        {/* Expanded Card Details */}
                                                                        {expandedChecklistNotes.has(key) && (
                                                                            <div className="mt-2 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 text-xs space-y-1">
                                                                                {isResubmitted && (
                                                                                    <div>
                                                                                        <p className="font-bold text-blue-900 dark:text-blue-200">
                                                                                            🛠️ Fix Note from CRE: <span className="font-normal">{req.fixNote || 'Updated credentials provided on CRE portal.'}</span>
                                                                                        </p>
                                                                                        {(req.acknowledgedBy || req.acknowledgedByEmail) && (
                                                                                            <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                                                                                Resubmitted by: {req.acknowledgedBy || req.acknowledgedByEmail} {req.acknowledgedAt ? `• ${dayjs(req.acknowledgedAt).format('MMM DD, YYYY h:mm A')}` : ''}
                                                                                            </p>
                                                                                        )}
                                                                                        {req.previousRejectionNote && (
                                                                                            <p className="text-[11px] text-amber-800 dark:text-amber-300 pt-1 border-t border-slate-200 dark:border-slate-700">
                                                                                                <strong>Previous Rejection:</strong> {req.previousRejectionNote}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                                {isRejected && (
                                                                                    <div>
                                                                                        <p className="font-bold text-rose-900 dark:text-rose-200">
                                                                                            ⚠️ Rejection Reason: <span className="font-normal">{req.rejectionNote || req.note}</span>
                                                                                        </p>
                                                                                        {req.rejectedBy && (
                                                                                            <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                                                                                Rejected by: {req.rejectedBy} {req.rejectedAt ? `• ${dayjs(req.rejectedAt).format('MMM DD, YYYY h:mm A')}` : ''}
                                                                                            </p>
                                                                                        )}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {/* Right Side: Sleek 3-Way Segmented Control */}
                                                                <div className="shrink-0 self-end sm:self-center">
                                                                    <div className="inline-flex items-center p-0.5 bg-slate-100 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSetItemStatus(rIdx, 'Pending')}
                                                                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                                                                                isPending
                                                                                    ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-xs font-bold'
                                                                                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                                                                            }`}
                                                                        >
                                                                            Pending
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleSetItemStatus(rIdx, 'Approved')}
                                                                            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                                                                isApproved
                                                                                    ? 'bg-emerald-600 text-white shadow-xs'
                                                                                    : 'text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/40'
                                                                            }`}
                                                                        >
                                                                            <CheckOutlined className="text-[10px]" /> Approve
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleOpenRejectItemModal(rIdx)}
                                                                            className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer ${
                                                                                isRejected
                                                                                    ? 'bg-rose-600 text-white shadow-xs'
                                                                                    : 'text-rose-700 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40'
                                                                            }`}
                                                                        >
                                                                            <CloseOutlined className="text-[10px]" /> Reject
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {filteredItems.length === 0 && (
                                                    <div className="p-6 text-center text-xs text-slate-500 dark:text-slate-400 bg-white dark:bg-slate-800/40 rounded-xl border border-dashed border-slate-200 dark:border-slate-700">
                                                        No requirements match the selected filter.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        )}

                        {/* ════════════════ Step 2: Milestones & Tasks ════════════════ */}
                        {currentStep === 2 && (
                            <div className="space-y-6">
                                {/* Mode Selector - 4 Tabs */}
                                <div className="bg-slate-100 dark:bg-slate-900 p-1.5 rounded-2xl flex items-center gap-2 border border-slate-200 dark:border-slate-800">
                                    {[
                                        { id: 'tasks', label: loadingTasks ? 'All Tasks (...)' : `All Tasks (${form.tasks.length})`, icon: loadingTasks ? <LoadingOutlined /> : <CheckSquareOutlined /> },
                                        { id: 'milestones', label: loadingTasks ? 'Milestones (...)' : `Milestones (${form.milestones.length})`, icon: loadingTasks ? <LoadingOutlined /> : <AimOutlined /> },
                                        { id: 'ai', label: 'AI-Assisted Plan', icon: <RobotOutlined /> },
                                        { id: 'excel', label: 'Import Excel / CSV', icon: <FileExcelOutlined /> },
                                    ].map((m) => {
                                        const isActive = form.planMode === m.id || (m.id === 'tasks' && form.planMode === 'manual');
                                        return (
                                            <button
                                                key={m.id}
                                                type="button"
                                                onClick={() => handleChange('planMode', m.id)}
                                                className={`flex-1 py-2.5 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all ${isActive
                                                    ? 'bg-white dark:bg-slate-800 text-[#003263] dark:text-[#a7c8ff] shadow-sm'
                                                    : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                                                    }`}
                                            >
                                                {m.icon}
                                                {m.label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Loader when loading tasks & milestones */}
                                {loadingTasks && (form.planMode === 'tasks' || form.planMode === 'manual' || form.planMode === 'milestones') ? (
                                    <div className="py-24 flex flex-col items-center justify-center space-y-4 bg-slate-50/60 dark:bg-slate-900/40 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800">
                                        <Spin size="large" />
                                        <div className="text-center">
                                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-200">
                                                Loading Project Milestones & Tasks...
                                            </h4>
                                            <p className="text-xs text-slate-400 mt-1 max-w-sm">
                                                Retrieving task hierarchy, milestones, and deliverable records from the database.
                                            </p>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        {/* Tab 1: All Tasks (The Undisturbed Master Tasks View) */}
                                        {(form.planMode === 'tasks' || form.planMode === 'manual') && (
                                    <div className="space-y-4">
                                        {/* KPI Metrics Summary Bar */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Total Tasks</p>
                                                <div className="flex items-baseline gap-1.5 mt-0.5">
                                                    <p className="text-xl font-black text-[#003263] dark:text-[#a7c8ff]">{form.tasks.length}</p>
                                                    <span className="text-[11px] font-semibold text-slate-400">({allParentTasks.length} parent · {allSubtasks.length} subtasks)</span>
                                                </div>
                                            </div>
                                            <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Top-Level Parents</p>
                                                <p className="text-xl font-black text-indigo-600 dark:text-indigo-400 mt-0.5">{allParentTasks.length}</p>
                                            </div>
                                            <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Nested Subtasks</p>
                                                <p className="text-xl font-black text-purple-600 dark:text-purple-400 mt-0.5">{allSubtasks.length}</p>
                                            </div>
                                            <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800">
                                                <p className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Completed</p>
                                                <p className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">{completedTasksCount}</p>
                                            </div>
                                        </div>

                                        {/* Search, View Modes & Filters Bar */}
                                        <div className="bg-slate-50 dark:bg-slate-900/60 p-3 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-2.5">
                                            {/* Upper Row: Search & Action Buttons */}
                                            <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5">
                                                <div className="relative flex-1 w-full">
                                                    <SearchOutlined className="absolute left-3 top-2.5 text-slate-400 text-xs" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search by title, code (TSK-...), or assignee..."
                                                        value={taskSearchQuery}
                                                        onChange={(e) => { setTaskSearchQuery(e.target.value); setTaskPage(1); }}
                                                        className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 pl-8 pr-3 py-1.5 rounded-xl text-xs font-semibold text-slate-800 dark:text-slate-200 placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                                                    />
                                                </div>
                                                <div className="flex items-center gap-2 w-full sm:w-auto justify-end shrink-0">
                                                    {(projectToEdit?._id || form.projectRequestId || form.creProjectId) && (
                                                        <button
                                                            type="button"
                                                            onClick={reloadTasks}
                                                            disabled={loadingTasks}
                                                            className="px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all border border-slate-200 dark:border-slate-700 cursor-pointer disabled:opacity-50"
                                                            title="Refresh project tasks & milestones from database"
                                                        >
                                                            <ReloadOutlined spin={loadingTasks} /> Refresh
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={openAddTask}
                                                        className="px-3.5 py-1.5 bg-[#03488a] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 hover:opacity-90 shrink-0 shadow-sm transition-all"
                                                    >
                                                        <PlusOutlined /> Add Task
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Lower Row: View Toggle & Filters */}
                                            <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200/60 dark:border-slate-800/60">
                                                {/* Left: View Mode Toggle & Expand/Collapse */}
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <div className="flex items-center bg-slate-200/80 dark:bg-slate-800 p-1 rounded-xl">
                                                        <button
                                                            type="button"
                                                            onClick={() => { setTaskViewMode('tree'); setTaskPage(1); }}
                                                            className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${taskViewMode === 'tree'
                                                                ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                                                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                                                                }`}
                                                            title="Group subtasks under parent tasks (Tree View)"
                                                        >
                                                            <BranchesOutlined /> Hierarchy
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setTaskViewMode('flat'); setTaskPage(1); }}
                                                            className={`px-2.5 py-1 text-xs font-bold rounded-lg transition-all flex items-center gap-1.5 ${taskViewMode === 'flat'
                                                                ? 'bg-white dark:bg-slate-700 text-[#03488a] dark:text-[#a7c8ff] shadow-sm'
                                                                : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-200'
                                                                }`}
                                                            title="View all tasks flat with subtask badges"
                                                        >
                                                            <MenuOutlined /> Flat List
                                                        </button>
                                                    </div>

                                                    {taskViewMode === 'tree' && (
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={expandAllTree}
                                                                className="px-2.5 py-1 text-[11px] font-bold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 rounded-lg transition-colors"
                                                            >
                                                                Expand All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={collapseAllTree}
                                                                className="px-2.5 py-1 text-[11px] font-bold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 rounded-lg transition-colors"
                                                            >
                                                                Collapse All
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Right: Filter Dropdowns */}
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    {taskViewMode === 'flat' && (
                                                        <Select
                                                            size="small"
                                                            value={taskTypeFilter}
                                                            onChange={(val) => { setTaskTypeFilter(val); setTaskPage(1); }}
                                                            className="min-w-[130px] text-xs font-semibold"
                                                        >
                                                            <Select.Option value="all">All Types ({form.tasks.length})</Select.Option>
                                                            <Select.Option value="parents">Parents Only ({allParentTasks.length})</Select.Option>
                                                            <Select.Option value="subtasks">Subtasks Only ({allSubtasks.length})</Select.Option>
                                                        </Select>
                                                    )}

                                                    {form.milestones.length > 0 && (
                                                        <Select
                                                            size="small"
                                                            value={taskMilestoneFilter}
                                                            onChange={(val) => { setTaskMilestoneFilter(val); setTaskPage(1); }}
                                                            className="min-w-[160px] text-xs font-semibold"
                                                        >
                                                            <Select.Option value="all">Milestones: All ({form.tasks.length})</Select.Option>
                                                            <Select.Option value="unassigned">Backlog / Unassigned ({unassignedToMilestoneTasks.length})</Select.Option>
                                                            {form.milestones.map((m, idx) => {
                                                                const mCount = form.tasks.filter((t) => getTaskMilestoneIndex(t, form.milestones) === idx).length;
                                                                return (
                                                                    <Select.Option key={m._id || m.id || idx} value={String(idx)}>
                                                                        ◆ {m.name || `Milestone ${idx + 1}`} ({mCount})
                                                                    </Select.Option>
                                                                );
                                                            })}
                                                        </Select>
                                                    )}

                                                    <Select
                                                        size="small"
                                                        value={taskStatusFilter}
                                                        onChange={(val) => { setTaskStatusFilter(val); setTaskPage(1); }}
                                                        className="min-w-[120px] text-xs font-semibold"
                                                    >
                                                        <Select.Option value="all">All Statuses</Select.Option>
                                                        <Select.Option value="Backlog">Backlog</Select.Option>
                                                        <Select.Option value="In Progress">In Progress</Select.Option>
                                                        <Select.Option value="Under Review">Under Review</Select.Option>
                                                        <Select.Option value="Completed">Completed</Select.Option>
                                                        <Select.Option value="On Hold">On Hold</Select.Option>
                                                        <Select.Option value="Canceled">Canceled</Select.Option>
                                                    </Select>

                                                    <Select
                                                        size="small"
                                                        value={taskAssigneeFilter}
                                                        onChange={(val) => { setTaskAssigneeFilter(val); setTaskPage(1); }}
                                                        className="min-w-[140px] text-xs font-semibold"
                                                    >
                                                        <Select.Option value="all">Team: All</Select.Option>
                                                        <Select.Option value="assigned">Team: Assigned ({assignedTasksCount})</Select.Option>
                                                        <Select.Option value="unassigned">Team: Unassigned ({unassignedTasksCount})</Select.Option>
                                                    </Select>

                                                    <Select
                                                        size="small"
                                                        value={taskPageSize}
                                                        onChange={(val) => { setTaskPageSize(val); setTaskPage(1); }}
                                                        className="w-24 text-xs font-semibold"
                                                    >
                                                        <Select.Option value={15}>15 / page</Select.Option>
                                                        <Select.Option value={30}>30 / page</Select.Option>
                                                        <Select.Option value={50}>50 / page</Select.Option>
                                                        <Select.Option value={9999}>View All</Select.Option>
                                                    </Select>
                                                </div>
                                            </div>
                                        </div>

                                        {form.milestones.length === 0 && form.tasks.length === 0 ? (
                                            <div className="text-center py-8 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800">
                                                <RocketOutlined className="text-3xl text-slate-300 mb-2" />
                                                <p className="text-xs font-bold text-slate-400">No Milestones or Tasks added yet</p>
                                                <p className="text-[11px] text-slate-400">Add manually, or switch to AI-Assisted to auto-generate a complete plan.</p>
                                            </div>
                                        ) : taskViewMode === 'flat' ? (
                                            /* ── FLAT VIEW ── */
                                            <div className="space-y-2.5">
                                                {paginatedFlatTasks.length > 0 ? (
                                                    paginatedFlatTasks.map((t) => {
                                                        const tId = String(t.id || t._id);
                                                        const assigneeObj = t.assignee || t.assignedTo;
                                                        const assigneeName = typeof assigneeObj === 'object' && assigneeObj !== null
                                                            ? `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim()
                                                            : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' && assigneeObj ? assigneeObj : null));

                                                        const isDone = ['Completed', 'Closed', 'Close'].includes(t.status);
                                                        const isInProg = ['In Progress', 'Active'].includes(t.status);
                                                        const parentTask = t.parentId ? taskById.get(String(typeof t.parentId === 'object' ? (t.parentId._id || t.parentId.id) : t.parentId)) : null;
                                                        const milestoneIdx = getTaskMilestoneIndex(t, form.milestones);
                                                        const milestone = milestoneIdx !== -1 ? form.milestones[milestoneIdx] : null;

                                                        return (
                                                            <div
                                                                key={tId}
                                                                className="flex flex-wrap items-center justify-between bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-sm hover:border-blue-400 dark:hover:border-blue-500 transition-all gap-3"
                                                            >
                                                                <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                                                                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isDone ? 'bg-emerald-500' : isInProg ? 'bg-blue-500' : 'bg-slate-300'}`} />
                                                                    <div className="min-w-0">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            {t.taskId && (
                                                                                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                                    {t.taskId}
                                                                                </span>
                                                                            )}
                                                                            <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                                                                                {t.name || t.title}
                                                                            </p>
                                                                            {t.parentId && (
                                                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-md">
                                                                                    ↳ Subtask of: {parentTask ? (parentTask.name || parentTask.title) : 'Parent Task'}
                                                                                </span>
                                                                            )}
                                                                            {milestone && (
                                                                                <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-md">
                                                                                    ◆ {milestone.name}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {t.description && (
                                                                            <p className="text-xs text-slate-400 truncate max-w-lg mt-0.5">
                                                                                {t.description.replace(/<[^>]*>?/gm, '')}
                                                                            </p>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                <div className="flex items-center gap-2 flex-wrap shrink-0">
                                                                    <Tag color={isDone ? 'green' : isInProg ? 'blue' : 'default'} className="text-xs font-semibold mr-0">
                                                                        {isDone ? 'Completed' : isInProg ? 'In Progress' : (t.status || 'Pending')}
                                                                    </Tag>
                                                                    <Tag color={t.priority === 'Critical' ? 'error' : t.priority === 'High' ? 'warning' : 'blue'} className="text-xs font-semibold mr-0">
                                                                        {t.priority || 'Medium'}
                                                                    </Tag>
                                                                    {assigneeName ? (
                                                                        <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                            <TeamOutlined className="text-blue-600" /> {assigneeName}
                                                                        </span>
                                                                    ) : (
                                                                        <Tag color="default" className="text-xs font-bold text-slate-400 border-dashed mr-0">
                                                                            Unassigned
                                                                        </Tag>
                                                                    )}
                                                                    <span className="font-mono text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                        ⏱️ {t.estimatedHours || 4}h
                                                                    </span>
                                                                    {t.dueDate && (
                                                                        <span className="text-[11px] font-medium text-slate-400">
                                                                            Due {dayjs(t.dueDate).format('MMM DD')}
                                                                        </span>
                                                                    )}
                                                                    {form.milestones.length > 0 && (
                                                                        <select
                                                                            value={milestone ? String(milestone._id || milestone.id || `m-${milestoneIdx}`) : ''}
                                                                            onChange={(e) => handleMoveTaskToMilestone(t.id || t._id, e.target.value)}
                                                                            className="text-xs font-semibold px-2 py-0.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-750 text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                                                            title="Assign or change Milestone"
                                                                        >
                                                                            <option value="">Backlog (Unassigned)</option>
                                                                            {form.milestones.map((mItem, mItemIdx) => (
                                                                                <option key={mItem._id || mItem.id || mItemIdx} value={String(mItem._id || mItem.id || `m-${mItemIdx}`)}>
                                                                                    ◆ {mItem.name || `Milestone ${mItemIdx + 1}`}
                                                                                </option>
                                                                            ))}
                                                                        </select>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openEditTask(t)}
                                                                        className="text-slate-400 hover:text-blue-600 transition-colors text-xs font-bold flex items-center gap-1 p-1"
                                                                        title="Edit task"
                                                                    >
                                                                        <EditOutlined />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setForm((prev) => ({ ...prev, tasks: prev.tasks.filter((x) => String(x.id || x._id) !== tId) }))}
                                                                        className="text-slate-300 hover:text-red-500 transition-colors text-xs p-1"
                                                                        title="Delete task"
                                                                    >
                                                                        <DeleteOutlined />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-2">
                                                        <CheckSquareOutlined className="text-4xl text-slate-300" />
                                                        <p className="text-sm font-bold text-slate-600 dark:text-slate-400">No tasks match your filter criteria</p>
                                                        <p className="text-xs text-slate-400">Try adjusting your search keyword or clearing filters.</p>
                                                    </div>
                                                )}

                                                {/* Flat View Pagination */}
                                                {totalTaskPages > 1 && (
                                                    <div className="flex items-center justify-between pt-2 px-1 text-xs font-semibold text-slate-500">
                                                        <span>
                                                            Showing page {taskPage} of {totalTaskPages} ({filteredFlatTasks.length} total tasks)
                                                        </span>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => setTaskPage((p) => Math.max(1, p - 1))}
                                                                disabled={taskPage <= 1}
                                                                className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-slate-200 transition-colors"
                                                            >
                                                                Prev
                                                            </button>
                                                            <span className="px-2 font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                                {taskPage} / {totalTaskPages}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setTaskPage((p) => Math.min(totalTaskPages, p + 1))}
                                                                disabled={taskPage >= totalTaskPages}
                                                                className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-slate-200 transition-colors"
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ) : (
                                            /* ── HIERARCHY / TREE VIEW (THE OLD VIEW - PRESERVED & UNDISTURBED) ── */
                                            <div className="space-y-3">
                                                {paginatedTreeParents.length > 0 ? (
                                                    paginatedTreeParents.map((parent) => {
                                                        const pKey = String(parent._id || parent.id);
                                                        const subtasks = subtasksByParentId.get(pKey) || [];
                                                        const matchingSubtasks = subtasks.filter(doesTaskMatchFilters);
                                                        const visibleSubtasks = taskSearchQuery.trim() ? matchingSubtasks : subtasks;
                                                        const isExpanded = expandedTaskIds.has(pKey) || (Boolean(taskSearchQuery.trim()) && matchingSubtasks.length > 0);

                                                        const isDone = ['Completed', 'Closed', 'Close'].includes(parent.status);
                                                        const isInProg = ['In Progress', 'Active'].includes(parent.status);
                                                        const assigneeObj = parent.assignee || parent.assignedTo;
                                                        const assigneeName = typeof assigneeObj === 'object' && assigneeObj !== null
                                                            ? `${assigneeObj.name || ''} ${assigneeObj.surname || ''}`.trim()
                                                            : (employees.find((e) => String(e._id) === String(assigneeObj))?.name || (typeof assigneeObj === 'string' && assigneeObj ? assigneeObj : null));

                                                        const mIdx = getTaskMilestoneIndex(parent, form.milestones);
                                                        const milestone = mIdx !== -1 ? form.milestones[mIdx] : null;

                                                        return (
                                                            <div key={pKey} className="bg-white dark:bg-slate-800/90 rounded-2xl border border-slate-200 dark:border-slate-700/80 shadow-sm overflow-hidden transition-all">
                                                                {/* Parent Row Header */}
                                                                <div className="p-3.5 flex flex-wrap items-center justify-between gap-3 hover:bg-slate-50/50 dark:hover:bg-slate-750 transition-colors">
                                                                    <div className="flex items-center gap-2.5 flex-1 min-w-[260px]">
                                                                        {subtasks.length > 0 ? (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => toggleTaskExpand(pKey)}
                                                                                className="w-6 h-6 flex items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-100 transition-colors shrink-0 text-xs"
                                                                                title={isExpanded ? 'Collapse subtasks' : 'Expand subtasks'}
                                                                            >
                                                                                {isExpanded ? <DownOutlined /> : <RightOutlined />}
                                                                            </button>
                                                                        ) : (
                                                                            <div className="w-6 shrink-0" />
                                                                        )}

                                                                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${isDone ? 'bg-emerald-500' : isInProg ? 'bg-blue-500' : 'bg-slate-300'}`} />

                                                                        <div className="min-w-0 flex-1">
                                                                            <div className="flex items-center gap-2 flex-wrap">
                                                                                {parent.taskId && (
                                                                                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                                        {parent.taskId}
                                                                                    </span>
                                                                                )}
                                                                                <p className="text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
                                                                                    {parent.name || parent.title}
                                                                                </p>
                                                                                {subtasks.length > 0 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleTaskExpand(pKey)}
                                                                                        className={`inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full transition-all ${isExpanded
                                                                                            ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-300'
                                                                                            : 'bg-slate-100 text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 dark:bg-slate-700 dark:text-slate-300'
                                                                                            }`}
                                                                                    >
                                                                                        ↳ {subtasks.length} Subtasks
                                                                                        <span className="text-[9px]">{isExpanded ? '▲' : '▼'}</span>
                                                                                    </button>
                                                                                )}
                                                                                {milestone && (
                                                                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/60 px-2 py-0.5 rounded-md">
                                                                                        ◆ {milestone.name}
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                            {parent.description && (
                                                                                <p className="text-xs text-slate-400 truncate max-w-xl mt-0.5">
                                                                                    {parent.description.replace(/<[^>]*>?/gm, '')}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <div className="flex items-center gap-2 flex-wrap shrink-0">
                                                                        <Tag color={isDone ? 'green' : isInProg ? 'blue' : 'default'} className="text-xs font-semibold mr-0">
                                                                            {isDone ? 'Completed' : isInProg ? 'In Progress' : (parent.status || 'Pending')}
                                                                        </Tag>
                                                                        <Tag color={parent.priority === 'Critical' ? 'error' : parent.priority === 'High' ? 'warning' : 'blue'} className="text-xs font-semibold mr-0">
                                                                            {parent.priority || 'Medium'}
                                                                        </Tag>
                                                                        {assigneeName ? (
                                                                            <span className="text-xs text-slate-700 dark:text-slate-200 font-semibold flex items-center gap-1.5 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                                <TeamOutlined className="text-blue-600" /> {assigneeName}
                                                                            </span>
                                                                        ) : (
                                                                            <Tag color="default" className="text-xs font-bold text-slate-400 border-dashed mr-0">
                                                                                Unassigned
                                                                            </Tag>
                                                                        )}
                                                                        <span className="font-mono text-xs font-bold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700/60 px-2.5 py-1 rounded-lg">
                                                                            ⏱️ {parent.estimatedHours || 4}h
                                                                        </span>
                                                                        {parent.dueDate && (
                                                                            <span className="text-[11px] font-medium text-slate-400">
                                                                                Due {dayjs(parent.dueDate).format('MMM DD')}
                                                                            </span>
                                                                        )}

                                                                        {/* Milestone Assignment Dropdown (if milestones exist) */}
                                                                        {form.milestones.length > 0 && (
                                                                            <select
                                                                                value={milestone ? String(milestone._id || milestone.id || `m-${mIdx}`) : ''}
                                                                                onChange={(e) => handleMoveTaskToMilestone(parent._id || parent.id, e.target.value)}
                                                                                className="text-xs font-semibold px-2 py-0.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-750 text-slate-700 dark:text-slate-200 outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer"
                                                                                title="Assign or change Milestone"
                                                                            >
                                                                                <option value="">No Milestone</option>
                                                                                {form.milestones.map((m, idx) => (
                                                                                    <option key={m._id || m.id || idx} value={String(m._id || m.id || `m-${idx}`)}>
                                                                                        ◆ {m.name || `Milestone ${idx + 1}`}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        )}

                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openEditTask(parent)}
                                                                            className="text-slate-400 hover:text-blue-600 transition-colors text-xs font-bold flex items-center gap-1 p-1"
                                                                            title="Edit parent task"
                                                                        >
                                                                            <EditOutlined /> Edit
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setForm((prev) => ({ ...prev, tasks: prev.tasks.filter((x) => String(x.id || x._id) !== pKey) }))}
                                                                            className="text-slate-300 hover:text-red-500 transition-colors text-xs p-1"
                                                                            title="Delete task"
                                                                        >
                                                                            <DeleteOutlined />
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {/* Subtasks Container */}
                                                                {isExpanded && visibleSubtasks.length > 0 && (
                                                                    <div className="bg-slate-50/70 dark:bg-slate-900/40 p-3.5 border-t border-slate-100 dark:border-slate-800 space-y-2">
                                                                        <div className="flex items-center justify-between mb-1.5 pl-6">
                                                                            <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                                                                                Nested Subtasks ({visibleSubtasks.length})
                                                                            </span>
                                                                        </div>
                                                                        <div className="space-y-1.5 pl-4 border-l-2 border-indigo-200 dark:border-indigo-900/60 ml-3">
                                                                            {visibleSubtasks.map((st) => {
                                                                                const stId = String(st._id || st.id);
                                                                                const stDone = ['Completed', 'Closed', 'Close'].includes(st.status);
                                                                                const stInProg = ['In Progress', 'Active'].includes(st.status);
                                                                                const stAssignee = st.assignee || st.assignedTo;
                                                                                const stAssigneeName = typeof stAssignee === 'object' && stAssignee !== null
                                                                                    ? `${stAssignee.name || ''} ${stAssignee.surname || ''}`.trim()
                                                                                    : (employees.find((e) => String(e._id) === String(stAssignee))?.name || (typeof stAssignee === 'string' && assigneeObj ? stAssignee : null));

                                                                                return (
                                                                                    <div
                                                                                        key={stId}
                                                                                        className="flex flex-wrap items-center justify-between bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-slate-200/80 dark:border-slate-700/60 hover:border-indigo-300 dark:hover:border-indigo-500 transition-all gap-2"
                                                                                    >
                                                                                        <div className="flex items-center gap-2.5 flex-1 min-w-[200px]">
                                                                                            <span className="text-slate-400 text-xs font-bold">↳</span>
                                                                                            <span className={`w-2 h-2 rounded-full shrink-0 ${stDone ? 'bg-emerald-500' : stInProg ? 'bg-blue-500' : 'bg-slate-300'}`} />
                                                                                            {st.taskId && (
                                                                                                <span className="font-mono text-[9px] font-bold px-1 rounded bg-slate-100 dark:bg-slate-700 text-slate-500">
                                                                                                    {st.taskId}
                                                                                                </span>
                                                                                            )}
                                                                                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-200 truncate">
                                                                                                {st.name || st.title}
                                                                                            </span>
                                                                                        </div>

                                                                                        <div className="flex items-center gap-2 flex-wrap shrink-0 text-xs">
                                                                                            <Tag color={stDone ? 'green' : stInProg ? 'blue' : 'default'} className="text-[11px] font-semibold mr-0">
                                                                                                {stDone ? 'Completed' : stInProg ? 'In Progress' : (st.status || 'Pending')}
                                                                                            </Tag>
                                                                                            <Tag color={st.priority === 'Critical' ? 'error' : st.priority === 'High' ? 'warning' : 'blue'} className="text-[11px] font-semibold mr-0">
                                                                                                {st.priority || 'Medium'}
                                                                                            </Tag>
                                                                                            {stAssigneeName ? (
                                                                                                <span className="text-[11px] text-slate-600 dark:text-slate-300 font-medium flex items-center gap-1 bg-slate-100 dark:bg-slate-700/60 px-2 py-0.5 rounded-md">
                                                                                                    <TeamOutlined className="text-blue-500 text-[10px]" /> {stAssigneeName}
                                                                                                </span>
                                                                                            ) : (
                                                                                                <span className="text-[11px] text-slate-400 italic">Unassigned</span>
                                                                                            )}
                                                                                            <span className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                                                                                                ⏱️ {st.estimatedHours || 4}h
                                                                                            </span>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => openEditTask(st)}
                                                                                                className="text-slate-400 hover:text-blue-600 p-1"
                                                                                                title="Edit subtask"
                                                                                            >
                                                                                                <EditOutlined />
                                                                                            </button>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => setForm((prev) => ({ ...prev, tasks: prev.tasks.filter((x) => String(x.id || x._id) !== stId) }))}
                                                                                                className="text-slate-300 hover:text-red-500 p-1"
                                                                                                title="Delete subtask"
                                                                                            >
                                                                                                <DeleteOutlined />
                                                                                            </button>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })
                                                ) : (
                                                    <div className="text-center py-12 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-2xl space-y-2">
                                                        <CheckSquareOutlined className="text-4xl text-slate-300" />
                                                        <p className="text-sm font-bold text-slate-600 dark:text-slate-400">No tasks match your filter criteria</p>
                                                        <p className="text-xs text-slate-400">Try adjusting your search keyword or clearing assignment filters.</p>
                                                    </div>
                                                )}

                                                {/* Orphan subtasks container (if any) */}
                                                {orphanSubtasks.length > 0 && (
                                                    <div className="bg-amber-50/50 dark:bg-amber-950/20 p-4 rounded-2xl border border-amber-200/80 dark:border-amber-900/40 space-y-2 mt-4">
                                                        <p className="text-xs font-bold text-amber-800 dark:text-amber-300 uppercase tracking-wider">
                                                            Direct Subtasks ({orphanSubtasks.length})
                                                        </p>
                                                        <div className="space-y-1.5">
                                                            {orphanSubtasks.map((st) => (
                                                                <div key={st._id || st.id} className="flex items-center justify-between bg-white dark:bg-slate-800 p-2.5 rounded-xl border border-amber-200/60 dark:border-amber-800/40 text-xs">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="text-amber-500 font-bold">↳</span>
                                                                        <span className="font-semibold text-slate-800 dark:text-slate-200">{st.name || st.title}</span>
                                                                    </div>
                                                                    <Tag color="blue">{st.status || 'Backlog'}</Tag>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Tree View Pagination */}
                                                {totalTaskPages > 1 && (
                                                    <div className="flex items-center justify-between pt-2 px-1 text-xs font-semibold text-slate-500">
                                                        <span>
                                                            Showing page {taskPage} of {totalTaskPages} ({filteredTreeParents.length} parent tasks)
                                                        </span>
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                type="button"
                                                                onClick={() => setTaskPage((p) => Math.max(1, p - 1))}
                                                                disabled={taskPage <= 1}
                                                                className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-slate-200 transition-colors"
                                                            >
                                                                Prev
                                                            </button>
                                                            <span className="px-2 font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                                {taskPage} / {totalTaskPages}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                onClick={() => setTaskPage((p) => Math.min(totalTaskPages, p + 1))}
                                                                disabled={taskPage >= totalTaskPages}
                                                                className="px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 disabled:opacity-30 hover:bg-slate-200 transition-colors"
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Tab 2: Milestones & Roadmap (Focus on Milestones & Unassigned Tasks) */}
                                {form.planMode === 'milestones' && (
                                    <div className="space-y-4">
                                        {/* Milestones Header & Action Bar */}
                                        <div className="bg-slate-50 dark:bg-slate-900/60 p-3.5 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-3">
                                            <div className="flex items-center gap-2.5">
                                                <div className="w-8 h-8 rounded-xl bg-amber-100 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center text-base font-bold">
                                                    ◆
                                                </div>
                                                <div>
                                                    <h4 className="text-sm font-bold text-slate-800 dark:text-white">
                                                        Project Milestones ({form.milestones.length})
                                                    </h4>
                                                    <p className="text-[11px] text-slate-400">
                                                        Define project milestones, track deliverables, and assign backlog tasks below
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap">
                                                {(projectToEdit?._id || form.projectRequestId || form.creProjectId) && (
                                                    <button
                                                        type="button"
                                                        onClick={reloadTasks}
                                                        disabled={loadingTasks}
                                                        className="px-3 py-1.5 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 text-xs font-bold rounded-xl flex items-center gap-1.5 transition-all border border-slate-200 dark:border-slate-700 cursor-pointer disabled:opacity-50"
                                                        title="Refresh project tasks & milestones from database"
                                                    >
                                                        <ReloadOutlined spin={loadingTasks} /> Refresh
                                                    </button>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={openAddMilestone}
                                                    className="px-3.5 py-1.5 bg-[#03488a] text-white text-xs font-bold rounded-xl flex items-center gap-1.5 hover:opacity-90 shadow-sm transition-all"
                                                >
                                                    <PlusOutlined /> Add Milestone
                                                </button>
                                            </div>
                                        </div>

                                        {/* Milestone Cards Grid */}
                                        {form.milestones.length === 0 ? (
                                            <div className="text-center py-10 bg-slate-50 dark:bg-slate-900/40 rounded-2xl border-2 border-dashed border-slate-200 dark:border-slate-800 space-y-2">
                                                <AimOutlined className="text-3xl text-slate-300 mb-1" />
                                                <p className="text-sm font-bold text-slate-600 dark:text-slate-300">No Milestones Defined Yet</p>
                                                <p className="text-xs text-slate-400">Click "+ Add Milestone" above to create your first milestone, or switch to AI-Assisted Plan.</p>
                                            </div>
                                        ) : (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {form.milestones.map((m, mIdx) => {
                                                    const mTasks = form.tasks.filter((t) => getTaskMilestoneIndex(t, form.milestones) === mIdx);
                                                    const mHours = mTasks.reduce((acc, t) => acc + (Number(t.estimatedHours) || 0), 0);
                                                    const mDoneTasks = mTasks.filter((t) => ['Completed', 'Closed', 'Close'].includes(t.status)).length;
                                                    return (
                                                        <div key={m._id || m.id || mIdx} className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700/80 p-4 space-y-2.5 shadow-xs">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <div className="flex items-center gap-2 min-w-0">
                                                                    <span className="text-amber-500 font-bold text-sm">◆</span>
                                                                    <h4 className="text-sm font-bold text-slate-800 dark:text-white truncate">
                                                                        {m.name || `Milestone ${mIdx + 1}`}
                                                                    </h4>
                                                                </div>
                                                                <div className="flex items-center gap-1.5 shrink-0">
                                                                    <Tag color={m.status === 'Completed' || m.status === 'Released' ? 'green' : 'blue'} className="text-[10px] font-semibold mr-0">
                                                                        {m.status || 'Pending'}
                                                                    </Tag>
                                                                    {m.status !== 'Completed' && m.status !== 'Released' && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleQuickCompleteMilestone(mIdx)}
                                                                            className="text-[10px] px-2 py-0.5 font-bold rounded-md bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 hover:bg-emerald-100 border border-emerald-200 dark:border-emerald-800 flex items-center gap-1 transition-all"
                                                                            title="Complete milestone (requires all deliverables checked)"
                                                                        >
                                                                            <CheckCircleOutlined /> Complete
                                                                        </button>
                                                                    )}
                                                                    <button type="button" onClick={() => openEditMilestone(m)} className="text-slate-400 hover:text-blue-600 p-1 font-bold text-xs" title="Edit milestone">
                                                                        <EditOutlined />
                                                                    </button>
                                                                    <button type="button" onClick={() => setForm((prev) => ({ ...prev, milestones: prev.milestones.filter((x, idx) => idx !== mIdx) }))} className="text-slate-300 hover:text-red-500 p-1" title="Delete milestone">
                                                                        <DeleteOutlined />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            {m.description && (
                                                                <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2">{m.description}</p>
                                                            )}
                                                            {m.completionCriteria && (
                                                                <p className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1">
                                                                    <CheckCircleOutlined /> {m.completionCriteria}
                                                                </p>
                                                            )}

                                                            {/* Milestone Deliverables Checklist */}
                                                            {(() => {
                                                                const deliverablesList = (Array.isArray(m.deliverableChecklist) && m.deliverableChecklist.length > 0)
                                                                    ? m.deliverableChecklist
                                                                    : (Array.isArray(m.deliverables) ? m.deliverables : []);
                                                                if (deliverablesList.length === 0) return null;

                                                                const mKey = String(m._id || m.id || mIdx);
                                                                const isExpanded = expandedDeliverableMilestoneIds.has(mKey);
                                                                const visibleDeliverables = isExpanded ? deliverablesList : deliverablesList.slice(0, 2);

                                                                return (
                                                                    <div className="bg-slate-50/80 dark:bg-slate-900/50 rounded-xl p-2.5 border border-slate-100 dark:border-slate-800/80 space-y-1.5">
                                                                        <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                                                            <span className="flex items-center gap-1.5">
                                                                                <span className="text-blue-500">📋</span> Deliverables ({deliverablesList.length})
                                                                            </span>
                                                                            {deliverablesList.length > 2 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => toggleExpandDeliverables(mKey)}
                                                                                    className="text-[10px] text-blue-600 dark:text-blue-400 hover:underline cursor-pointer font-semibold"
                                                                                >
                                                                                    {isExpanded ? 'Show less ▲' : `+${deliverablesList.length - 2} more ▼`}
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                        <div className="space-y-1">
                                                                            {visibleDeliverables.map((d, dIdx) => {
                                                                                const isDone = Boolean(d.completed || d.done);
                                                                                const isRej = Boolean(d.rejected);
                                                                                const code = d.deliverableId || d.creDeliverableId;
                                                                                return (
                                                                                    <div
                                                                                        key={d.deliverableId || dIdx}
                                                                                        onClick={() => handleToggleStep2Deliverable(mIdx, dIdx)}
                                                                                        className="flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700/60 text-xs cursor-pointer hover:border-blue-400 select-none transition-colors"
                                                                                        title="Click to toggle deliverable verification"
                                                                                    >
                                                                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                                            <span className={`w-3.5 h-3.5 rounded flex items-center justify-center font-bold text-[9px] border transition-colors ${
                                                                                                isDone
                                                                                                    ? 'bg-emerald-600 text-white border-emerald-600'
                                                                                                    : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-transparent'
                                                                                            }`}>
                                                                                                ✓
                                                                                            </span>
                                                                                            <span className={`truncate text-[11px] font-medium ${isDone ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                                                {d.title || d.name || 'Deliverable'}
                                                                                            </span>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })()}

                                                            <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700/60 text-xs text-slate-500">
                                                                <span className="font-semibold text-blue-600 dark:text-blue-400">
                                                                    {mTasks.length} Tasks ({mDoneTasks} done · {mHours}h)
                                                                </span>
                                                                {m.dueDate && (
                                                                    <span className="text-slate-400 font-medium text-[11px]">Due {dayjs(m.dueDate).format('MMM DD, YYYY')}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Unassigned Tasks Backlog Section */}
                                        {form.tasks.length === 0 ? (
                                            <div className="bg-slate-50/70 dark:bg-slate-900/40 p-3.5 rounded-xl border border-dashed border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3 text-xs text-slate-500 dark:text-slate-400 mt-4">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-base">💡</span>
                                                    <span>Milestones & deliverables ready. You can use <strong>AI-Assisted Plan</strong>, <strong>Import Excel/CSV</strong>, or click <strong>+ Add Task</strong> to break down execution tasks.</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={openAddTask}
                                                    className="shrink-0 px-3 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-blue-600 dark:text-blue-400 font-semibold hover:border-blue-300 transition-all cursor-pointer"
                                                >
                                                    + Add Task
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="bg-slate-50/90 dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3 mt-4">
                                                <div className="flex items-center justify-between flex-wrap gap-2">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <InboxOutlined className="text-slate-500 text-sm" />
                                                            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
                                                                Unassigned Tasks ({unassignedToMilestoneTasks.length})
                                                            </h4>
                                                        </div>
                                                        <p className="text-[11px] text-slate-400 mt-0.5">
                                                            Tasks not yet linked to any milestone. Use the dropdown on each task to assign them:
                                                        </p>
                                                    </div>
                                                    {form.milestones.length > 0 && unassignedToMilestoneTasks.length > 0 && (
                                                        <div className="flex items-center gap-1.5 text-xs">
                                                            <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400">Bulk Assign All To:</span>
                                                            <select
                                                                defaultValue=""
                                                                onChange={(e) => {
                                                                    if (e.target.value) {
                                                                        handleBulkMoveUnassignedToMilestone(e.target.value);
                                                                        e.target.value = '';
                                                                    }
                                                                }}
                                                                className="text-xs font-bold px-2 py-1 rounded-lg border border-blue-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-blue-700 dark:text-blue-300 outline-none cursor-pointer"
                                                            >
                                                                <option value="" disabled>Select Milestone...</option>
                                                                {form.milestones.map((m, idx) => (
                                                                    <option key={m._id || m.id || idx} value={String(m._id || m.id || `m-${idx}`)}>
                                                                        ◆ {m.name || `Milestone ${idx + 1}`}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>

                                                {unassignedToMilestoneTasks.length === 0 ? (
                                                    <div className="text-center py-2.5 px-3 text-xs text-emerald-600 dark:text-emerald-400 font-medium bg-white dark:bg-slate-800 rounded-xl border border-emerald-100 dark:border-emerald-900/40 flex items-center justify-center gap-1.5">
                                                        <CheckCircleOutlined /> All {form.tasks.length} tasks are assigned to milestones!
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                                                        {unassignedMilestoneParents.map((parent) => {
                                                            const pKey = String(parent._id || parent.id);
                                                            const subtasks = subtasksByParentId.get(pKey) || [];
                                                            return (
                                                                <div key={pKey} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-wrap items-center justify-between gap-2.5">
                                                                    <div className="min-w-0 flex-1">
                                                                        <div className="flex items-center gap-2 flex-wrap">
                                                                            {parent.taskId && (
                                                                                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                                                    {parent.taskId}
                                                                                </span>
                                                                            )}
                                                                            <p className="text-xs font-bold text-slate-800 dark:text-slate-200 truncate">
                                                                                {parent.name || parent.title}
                                                                            </p>
                                                                            {subtasks.length > 0 && (
                                                                                <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded">
                                                                                    ↳ {subtasks.length} Subtasks
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </div>

                                                                    <div className="flex items-center gap-2 shrink-0">
                                                                        {form.milestones.length > 0 ? (
                                                                            <select
                                                                                defaultValue=""
                                                                                onChange={(e) => {
                                                                                    if (e.target.value) {
                                                                                        handleMoveTaskToMilestone(parent._id || parent.id, e.target.value);
                                                                                    }
                                                                                }}
                                                                                className="text-xs font-semibold px-2 py-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-750 text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
                                                                            >
                                                                                <option value="" disabled>Move to Milestone...</option>
                                                                                {form.milestones.map((m, idx) => (
                                                                                    <option key={m._id || m.id || idx} value={String(m._id || m.id || `m-${idx}`)}>
                                                                                        ◆ {m.name || `Milestone ${idx + 1}`}
                                                                                    </option>
                                                                                ))}
                                                                            </select>
                                                                        ) : (
                                                                            <span className="text-[11px] text-slate-400 italic">Create a milestone first</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                                {/* Mode B: AI-Assisted */}
                                {form.planMode === 'ai' && (
                                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-slate-900 dark:to-blue-950/30 p-6 rounded-2xl border border-blue-200 dark:border-blue-900/40 text-center space-y-4">
                                        <RobotOutlined className="text-4xl text-blue-600" />
                                        <div className="max-w-md mx-auto space-y-1">
                                            <h4 className="font-bold text-sm text-slate-800 dark:text-white">Generate Full Execution Plan with AI</h4>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">AI will inspect Step 1 project parameters and propose Milestones, Tasks, effort estimates, and priorities.</p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={handleGenerateAIPlan}
                                            disabled={aiGeneratingPlan}
                                            className="px-6 py-2.5 rounded-xl font-bold text-xs text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90 transition-all shadow-md disabled:opacity-50"
                                        >
                                            {aiGeneratingPlan ? <LoadingOutlined /> : 'Generate Execution Plan'}
                                        </button>

                                        {form.milestones.length > 0 && (
                                            <div className="text-left bg-white dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3 mt-4">
                                                <p className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                                                    <CheckCircleOutlined /> Generated {form.milestones.length} Milestones &amp; {form.tasks.length} Tasks
                                                </p>
                                                <div className="space-y-2 max-h-48 overflow-y-auto">
                                                    {form.milestones.map((m) => (
                                                        <div key={m.id} className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                                            • {m.name}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Mode C: Import Excel */}
                                {form.planMode === 'excel' && (
                                    <div className="space-y-4">
                                        <div className="border-2 border-dashed border-slate-300 dark:border-slate-700 p-6 rounded-2xl text-center space-y-3 bg-slate-50 dark:bg-slate-900/30">
                                            <FileExcelOutlined className="text-3xl text-emerald-600" />
                                            <div>
                                                <p className="text-xs font-bold">Upload Excel or CSV project plan</p>
                                                <p className="text-[11px] text-slate-400">Supported formats: .csv, .xlsx</p>
                                            </div>
                                            <input
                                                type="file"
                                                accept=".csv, .xlsx"
                                                onChange={(e) => e.target.files[0] && handleExcelFileUpload(e.target.files[0])}
                                                className="hidden"
                                                id="excel-upload"
                                            />
                                            <label
                                                htmlFor="excel-upload"
                                                className="inline-block px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl cursor-pointer"
                                            >
                                                Browse File
                                            </label>
                                        </div>

                                        {excelColumns.length > 0 && (
                                            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3">
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Map Columns to ERP Fields</h4>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
                                                    {['name', 'description', 'milestone', 'priority', 'estimatedHours'].map((f) => (
                                                        <div key={f} className="space-y-1">
                                                            <label className="font-bold text-slate-600 capitalize">{f}</label>
                                                            <select
                                                                className={inputClass}
                                                                value={excelMapping[f] || ''}
                                                                onChange={(e) => setExcelMapping({ ...excelMapping, [f]: e.target.value })}
                                                            >
                                                                <option value="">Select column...</option>
                                                                {excelColumns.map((col) => <option key={col} value={col}>{col}</option>)}
                                                            </select>
                                                        </div>
                                                    ))}
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleImportExcelData}
                                                    className="px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-xl hover:opacity-90"
                                                >
                                                    Import {excelRows.length} Tasks
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Mandatory ERP Pipeline Banner */}
                                <div className="bg-slate-100 dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 flex items-center justify-between text-xs font-semibold text-slate-500">
                                    <span>Mandatory Task Pipeline:</span>
                                    <div className="flex items-center gap-1 font-mono text-[11px] font-bold text-slate-700 dark:text-slate-300">
                                        <Tag>Backlog</Tag> → <Tag color="blue">In Progress</Tag> → <Tag color="amber">Under Review</Tag> → <Tag color="green">Completed</Tag>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ════════════════ Step 3: Documents & Assets ════════════════ */}
                        {currentStep === 3 && (
                            <div className="space-y-6">
                                {/* Project Documents */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider">Project Documents</h3>
                                            <p className="text-[11px] text-slate-400">Formal specifications, contracts, PDF, DOCX, XLSX</p>
                                        </div>
                                        <label className="px-3.5 py-1.5 bg-[#03488a] text-white text-xs font-bold rounded-xl cursor-pointer hover:opacity-90">
                                            + Upload Document
                                            <input type="file" multiple onChange={(e) => handleFileUpload(e, 'documents')} className="hidden" />
                                        </label>
                                    </div>

                                    {form.documents.length > 0 ? (
                                        <div className="space-y-2">
                                            {form.documents.map((doc, idx) => {
                                                const fileUrl = doc.publicUrl || doc.url || doc.fileUrl || doc.storageKey;
                                                const displaySize = typeof doc.fileSize === 'number'
                                                    ? (doc.fileSize > 1048576 ? (doc.fileSize / 1048576).toFixed(1) + ' MB' : (doc.fileSize / 1024).toFixed(1) + ' KB')
                                                    : (doc.fileSize || '');
                                                return (
                                                    <div key={doc.id || doc._id || idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 flex justify-between items-center text-xs shadow-sm hover:border-blue-300 transition-all">
                                                        <div className="flex items-center gap-2.5 overflow-hidden">
                                                            <FilePdfOutlined className="text-blue-600 text-lg flex-shrink-0" />
                                                            <div className="truncate">
                                                                {fileUrl ? (
                                                                    <a
                                                                        href={fileUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="font-bold text-slate-800 dark:text-slate-100 hover:text-blue-600 dark:hover:text-blue-400 hover:underline inline-flex items-center gap-1"
                                                                        title="Click to open in new tab"
                                                                    >
                                                                        <span className="truncate">{doc.fileName}</span>
                                                                        <EyeOutlined className="text-slate-400 text-xs" />
                                                                    </a>
                                                                ) : (
                                                                    <span className="font-bold text-slate-800 dark:text-slate-100">{doc.fileName}</span>
                                                                )}
                                                                {displaySize && <span className="text-slate-400 text-[11px] ml-2">({displaySize})</span>}
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            {fileUrl && (
                                                                <a
                                                                    href={fileUrl}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="px-2.5 py-1 bg-blue-50 dark:bg-slate-700 text-blue-600 dark:text-blue-300 rounded-lg text-[11px] font-bold hover:bg-blue-100 transition-colors inline-flex items-center gap-1"
                                                                >
                                                                    <EyeOutlined /> View
                                                                </a>
                                                            )}
                                                            <button type="button" onClick={() => handleRemoveFile('documents', doc.id || doc._id || idx)} className="text-slate-400 hover:text-red-500 p-1 transition-colors">
                                                                <DeleteOutlined />
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-400 text-center py-4 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">No documents attached yet.</p>
                                    )}
                                </div>

                                {/* Project Assets */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-xs font-bold text-slate-800 dark:text-white uppercase tracking-wider">Project Assets</h3>
                                            <p className="text-[11px] text-slate-400">PNG, JPG, SVG, MP4, Design files, Brand assets</p>
                                        </div>
                                        <label className="px-3.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-xl cursor-pointer hover:opacity-90">
                                            + Upload Asset
                                            <input type="file" multiple onChange={(e) => handleFileUpload(e, 'assets')} className="hidden" />
                                        </label>
                                    </div>

                                    {form.assets.length > 0 ? (
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                            {form.assets.map((asset, idx) => {
                                                const fileUrl = asset.publicUrl || asset.url || asset.fileUrl || asset.storageKey;
                                                const displaySize = typeof asset.fileSize === 'number'
                                                    ? (asset.fileSize > 1048576 ? (asset.fileSize / 1048576).toFixed(1) + ' MB' : (asset.fileSize / 1024).toFixed(1) + ' KB')
                                                    : (asset.fileSize || '');
                                                return (
                                                    <div key={asset.id || asset._id || idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1.5 text-xs relative group shadow-sm hover:border-indigo-300 transition-all">
                                                        <div className="flex items-center justify-between">
                                                            <PictureOutlined className="text-indigo-500 text-xl" />
                                                            <div className="flex items-center gap-1">
                                                                {fileUrl && (
                                                                    <a
                                                                        href={fileUrl}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        className="text-slate-400 hover:text-indigo-600 text-xs p-1"
                                                                        title="View in new tab"
                                                                    >
                                                                        <EyeOutlined />
                                                                    </a>
                                                                )}
                                                                <button type="button" onClick={() => handleRemoveFile('assets', asset.id || asset._id || idx)} className="text-slate-400 hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <DeleteOutlined />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        {fileUrl ? (
                                                            <a
                                                                href={fileUrl}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-bold truncate block text-slate-800 dark:text-slate-100 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline"
                                                                title="Click to view in new tab"
                                                            >
                                                                {asset.fileName}
                                                            </a>
                                                        ) : (
                                                            <p className="font-bold truncate text-slate-800 dark:text-slate-100">{asset.fileName}</p>
                                                        )}
                                                        <div className="flex items-center justify-between pt-1 text-[10px] text-slate-400">
                                                            <span>{displaySize}</span>
                                                            {fileUrl && (
                                                                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="text-indigo-600 font-bold hover:underline">
                                                                    Open →
                                                                </a>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-400 text-center py-4 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl">No assets attached yet.</p>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* ════════════════ Step 4: Configuration ════════════════ */}
                        {currentStep === 4 && (
                            <div className="space-y-6">
                                {/* Sprint Management Switch */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-sm font-bold text-slate-800 dark:text-white">Include Project in Sprint Management?</h3>
                                            <p className="text-xs text-slate-500 dark:text-slate-400">If ON, eligible tasks automatically participate in the automated Sprint Engine.</p>
                                        </div>
                                        <Switch
                                            checked={form.sprintManagement}
                                            onChange={(checked) => {
                                                handleChange('sprintManagement', checked);
                                                if (!checked) {
                                                    setForm((prev) => ({
                                                        ...prev,
                                                        availableViews: { ...prev.availableViews, sprint: false },
                                                        defaultView: prev.defaultView === 'Sprint View' ? 'Kanban Board View' : prev.defaultView,
                                                    }));
                                                }
                                            }}
                                        />
                                    </div>
                                </div>

                                {/* Project Views */}
                                <div className="bg-slate-50 dark:bg-slate-900/40 p-5 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
                                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Available Project Views</h3>
                                    <div className="grid grid-cols-2 gap-3 text-xs">
                                        {[
                                            { id: 'list', label: 'List View' },
                                            { id: 'kanban', label: 'Kanban Board View' },
                                            { id: 'timeline', label: 'Timeline View' },
                                            { id: 'sprint', label: 'Sprint View', disabled: !form.sprintManagement },
                                        ].map((v) => (
                                            <label key={v.id} className={`flex items-center gap-2 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 font-semibold cursor-pointer ${v.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
                                                <Checkbox
                                                    checked={form.availableViews[v.id]}
                                                    disabled={v.disabled}
                                                    onChange={(e) => setForm((prev) => ({ ...prev, availableViews: { ...prev.availableViews, [v.id]: e.target.checked } }))}
                                                />
                                                <span>{v.label}</span>
                                                {v.disabled && <Tag className="ml-auto text-[9px]">Disabled</Tag>}
                                            </label>
                                        ))}
                                    </div>

                                    <div className="space-y-1 pt-2">
                                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Default Opening View</label>
                                        <select className={inputClass} value={form.defaultView} onChange={(e) => handleChange('defaultView', e.target.value)}>
                                            {['List View', 'Kanban Board View', 'Timeline View', 'Sprint View']
                                                .filter((v) => v !== 'Sprint View' || form.sprintManagement)
                                                .map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ════════════════ Step 5: Review & Create ════════════════ */}
                        {currentStep === 5 && (
                            <div className="space-y-5 text-base">

                                {/* ── Banner ── */}
                                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-[#03488a]/10 to-blue-50 dark:from-blue-950/30 dark:to-slate-900 rounded-2xl border border-blue-200 dark:border-blue-900/40">
                                    <CheckCircleOutlined className="text-2xl text-[#03488a] dark:text-[#a7c8ff]" />
                                    <div>
                                        <p className="font-black text-base text-[#003263] dark:text-[#a7c8ff]">{form.name}</p>
                                        <p className="text-slate-500 dark:text-slate-400 font-mono text-sm">
                                            {form.projectCode} · <span className="text-[#03488a] dark:text-[#a7c8ff] font-bold">{form.brandName || 'DymaxTech'}</span> · {form.department} · {form.isClientProject ? 'Client Project' : 'In-house'}
                                        </p>
                                    </div>
                                    <div className="ml-auto text-right">
                                        <span className={`px-2.5 py-1 rounded-full font-bold text-xs ${form.priority === 'Critical' ? 'bg-red-100 text-red-700' : form.priority === 'High' ? 'bg-orange-100 text-orange-700' : form.priority === 'Medium' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}`}>{form.priority} Priority</span>
                                    </div>
                                </div>

                                {/* ── 3-col grid: Brand & Specs + Schedule + Ownership ── */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                                        <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Brand &amp; Classification</h4>
                                        <div className="space-y-1 text-slate-700 dark:text-slate-300 text-sm">
                                            <div className="flex items-center gap-1.5">
                                                <span>Brand:</span>
                                                <Tag color="cyan" className="font-bold text-xs mr-0">
                                                    {form.brandName || (form.brandId?.includes('e360') ? 'e360Digital' : form.brandId?.includes('cport') ? 'CellularPort' : 'DymaxTech')}
                                                </Tag>
                                            </div>
                                            <p>Category: <b>{form.category || 'General'}</b></p>
                                            <p>Department: <b>{form.department || 'Development'}</b></p>
                                            <p>Type: <b>{form.isClientProject ? 'Client Project' : 'In-house Project'}</b></p>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                                        <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Schedule</h4>
                                        <div className="space-y-1 text-slate-700 dark:text-slate-300 text-sm">
                                            <p>Start: <b>{form.startDate || '—'}</b></p>
                                            <p>End: <b>{form.dueDate || '—'}</b></p>
                                            <p>Status: <b>{form.status}</b> · Health: <b>{form.health}</b></p>
                                        </div>
                                    </div>
                                    <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                                        <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Team</h4>
                                        <div className="space-y-1 text-slate-700 dark:text-slate-300 text-sm">
                                            <p>Owner: <b>{employees.find((e) => e._id === form.owner)?.name || 'Unassigned'}</b></p>
                                            <p>Manager: <b>{employees.find((e) => e._id === form.projectManager)?.name || 'Unassigned'}</b></p>
                                            <p>Collaborators: <b>{form.collaborators.length} member{form.collaborators.length !== 1 ? 's' : ''}</b></p>
                                        </div>
                                    </div>
                                </div>

                                {/* ── Questionnaire References in Step 5 Review ── */}
                                {(() => {
                                    const allReviewQs = Array.from(new Set([
                                        ...(Array.isArray(form.questionnaires) ? form.questionnaires : []),
                                        ...(form.questionnaireTemplateName ? [form.questionnaireTemplateName] : []),
                                    ])).filter(Boolean);
                                    if (allReviewQs.length === 0) return null;
                                    return (
                                        <div className="bg-blue-50/70 dark:bg-slate-900/60 p-4 rounded-xl border border-blue-200 dark:border-slate-800 space-y-2">
                                            <div className="flex items-center justify-between">
                                                <h4 className="font-bold uppercase tracking-wider text-[#003263] dark:text-[#a7c8ff] text-xs flex items-center gap-2">
                                                    <FileTextOutlined className="text-blue-600 text-sm" /> Linked Client Intake Questionnaires ({allReviewQs.length})
                                                </h4>
                                                <Tag color="emerald" className="font-bold text-[10px] mr-0">CRE Linked ↗</Tag>
                                            </div>
                                            <div className="flex flex-wrap gap-2 pt-1">
                                                {allReviewQs.map((q, idx) => (
                                                    <div key={idx} className="flex items-center gap-2 bg-white dark:bg-slate-800 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-xs font-bold text-[#003263] dark:text-[#a7c8ff]">
                                                        <span>📋 {q}</span>
                                                        <Tag color="blue" className="mr-0 text-[9px]">Linked ↗</Tag>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })()}

                                {/* ── Smart Goals ── */}
                                {form.smartGoals.length > 0 && (
                                    <div className="bg-blue-50/60 dark:bg-blue-950/20 p-4 rounded-xl border border-blue-200 dark:border-blue-900/40 space-y-2">
                                        <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Smart Goals ({form.smartGoals.length})</h4>
                                        <div className="space-y-1.5">
                                            {form.smartGoals.map((g) => (
                                                <div key={g.id} className="flex items-start gap-2 text-sm">
                                                    <span className="mt-0.5 text-blue-500"><CheckCircleOutlined /></span>
                                                    <div>
                                                        <span className="font-bold text-slate-700 dark:text-slate-200">{g.name}</span>
                                                        {g.successCriteria && <p className="text-xs text-slate-500">↳ {g.successCriteria}</p>}
                                                    </div>
                                                    <Tag color="blue" className="ml-auto text-xs shrink-0">{g.priority}</Tag>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* ── Milestones + Tasks ── */}
                                {(form.milestones.length > 0 || form.tasks.length > 0) && (() => {
                                    const allParentTasks = form.tasks.filter((t) => !t.parentId);
                                    const allSubtasks = form.tasks.filter((t) => Boolean(t.parentId));
                                    const unlinkedTasks = form.tasks.filter((t) => getTaskMilestoneIndex(t, form.milestones) === -1);
                                    const totalAssigned = form.tasks.filter((t) => Boolean(t.assignee)).length;
                                    const totalUnassigned = form.tasks.length - totalAssigned;

                                    return (
                                        <div className="space-y-3">
                                            <div className="flex items-center justify-between flex-wrap gap-2">
                                                <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">
                                                    {form.milestones.length > 0
                                                        ? `Milestones & Tasks (${form.milestones.length} milestones · ${allParentTasks.length} parent tasks · ${allSubtasks.length} subtasks · ${form.tasks.length} total)`
                                                        : `Project Tasks (${allParentTasks.length} parent · ${allSubtasks.length} subtasks · ${form.tasks.length} total)`
                                                    }
                                                </h4>
                                                <div className="text-xs font-semibold text-slate-500 flex items-center gap-2">
                                                    <span className="text-emerald-600 dark:text-emerald-400">{totalAssigned} assigned</span>
                                                    <span>•</span>
                                                    <span className="text-amber-600 dark:text-amber-400">{totalUnassigned} unassigned</span>
                                                </div>
                                            </div>

                                            {/* Milestones list */}
                                            {form.milestones.map((m, mIdx) => {
                                                const mTasks = form.tasks.filter((t) => getTaskMilestoneIndex(t, form.milestones) === mIdx);
                                                const mParents = mTasks.filter((t) => !t.parentId);
                                                return (
                                                    <div key={m._id || m.id || `m-${mIdx}`} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                                                        <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800 text-sm">
                                                            <span className="w-2 h-2 rounded-full bg-[#03488a]" />
                                                            <span className="font-bold text-slate-800 dark:text-slate-100">{m.name}</span>
                                                            {m.dueDate && <span className="ml-auto text-slate-500 font-mono text-xs">Due: {m.dueDate}</span>}
                                                            <Tag color="blue" className="text-xs ml-1">{mTasks.length} task{mTasks.length !== 1 ? 's' : ''}</Tag>
                                                        </div>
                                                        {m.completionCriteria && <p className="px-4 py-1 text-xs text-emerald-600 dark:text-emerald-400 border-b border-slate-100 dark:border-slate-700/60">Completion: {m.completionCriteria}</p>}
                                                        {mParents.length > 0 && (
                                                            <div className="divide-y divide-slate-100 dark:divide-slate-700/60">
                                                                {mParents.map((t) => {
                                                                    const tId = String(t.id || t._id);
                                                                    const childSubs = form.tasks.filter((sub) => String(sub.parentId) === tId);
                                                                    const isExpanded = expandedReviewTaskIds.has(tId);
                                                                    return (
                                                                        <div key={tId} className="px-5 py-2">
                                                                            <div className="flex items-center gap-3 text-sm">
                                                                                <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                                                                                <span className="flex-1 font-semibold text-slate-700 dark:text-slate-300 truncate">{t.name || t.title}</span>
                                                                                {childSubs.length > 0 && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => toggleReviewTaskExpand(tId)}
                                                                                        className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800 cursor-pointer"
                                                                                    >
                                                                                        ↳ {childSubs.length} subtasks {isExpanded ? '▲' : '▼'}
                                                                                    </button>
                                                                                )}
                                                                                <span className="text-slate-400 text-xs font-mono">{t.estimatedHours}h</span>
                                                                                <Tag color={t.priority === 'Critical' ? 'red' : t.priority === 'High' ? 'orange' : 'default'} className="text-xs">{t.priority}</Tag>
                                                                            </div>
                                                                            {isExpanded && childSubs.length > 0 && (
                                                                                <div className="ml-5 mt-1.5 pl-3 border-l-2 border-blue-200 dark:border-blue-800 space-y-1">
                                                                                    {childSubs.map((sub) => (
                                                                                        <div key={sub.id || sub._id} className="flex items-center gap-2 text-xs py-1 text-slate-600 dark:text-slate-400">
                                                                                            <span>↳</span>
                                                                                            <span className="flex-1 truncate">{sub.name || sub.title}</span>
                                                                                            <span className="font-mono text-[10px]">{sub.estimatedHours || 4}h</span>
                                                                                        </div>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            {/* Direct / Unlinked Tasks */}
                                            {unlinkedTasks.length > 0 && (() => {
                                                const directParents = unlinkedTasks.filter((t) => !t.parentId);
                                                const directSubs = unlinkedTasks.filter((t) => Boolean(t.parentId));
                                                const orphanSubs = directSubs.filter((sub) => !unlinkedTasks.some((p) => String(p.id || p._id) === String(sub.parentId)));

                                                return (
                                                    <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden text-sm">
                                                        <div className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-bold flex items-center justify-between text-xs uppercase tracking-wider flex-wrap gap-2">
                                                            <span>General / Direct Tasks ({directParents.length} Parent · {directSubs.length} Subtasks · {unlinkedTasks.length} Total)</span>
                                                            <span className="font-normal text-slate-400 lowercase">click subtasks to expand</span>
                                                        </div>
                                                        <div className="divide-y divide-slate-100 dark:divide-slate-700/60 max-h-80 overflow-y-auto custom-scrollbar">
                                                            {directParents.map((t) => {
                                                                const tId = String(t.id || t._id);
                                                                const childSubs = unlinkedTasks.filter((sub) => String(sub.parentId) === tId);
                                                                const isExpanded = expandedReviewTaskIds.has(tId);
                                                                const assigneeObj = employees.find((e) => String(e._id) === String(t.assignee));
                                                                const assigneeName = assigneeObj?.name || null;

                                                                return (
                                                                    <div key={tId} className="px-4 py-2 text-sm">
                                                                        <div className="flex items-center gap-3">
                                                                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${assigneeName ? 'bg-emerald-500' : 'bg-slate-400'}`} />
                                                                            <span className="flex-1 font-semibold text-slate-700 dark:text-slate-300 truncate">{t.name}</span>
                                                                            {childSubs.length > 0 && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => toggleReviewTaskExpand(tId)}
                                                                                    className="text-[11px] font-bold px-2 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border border-blue-200 dark:border-blue-800 cursor-pointer"
                                                                                >
                                                                                    ↳ {childSubs.length} subtasks {isExpanded ? '▲' : '▼'}
                                                                                </button>
                                                                            )}
                                                                            {assigneeName ? (
                                                                                <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded">
                                                                                    {assigneeName}
                                                                                </span>
                                                                            ) : (
                                                                                <span className="text-[11px] font-medium text-slate-400 italic">
                                                                                    Unassigned
                                                                                </span>
                                                                            )}
                                                                            <span className="text-slate-400 text-xs font-mono">{t.estimatedHours}h</span>
                                                                            <Tag color={t.priority === 'Critical' ? 'red' : t.priority === 'High' ? 'orange' : 'default'} className="text-xs mr-0">{t.priority}</Tag>
                                                                        </div>
                                                                        {isExpanded && childSubs.length > 0 && (
                                                                            <div className="ml-5 mt-1.5 pl-3 border-l-2 border-blue-200 dark:border-blue-800 space-y-1">
                                                                                {childSubs.map((sub) => (
                                                                                    <div key={sub.id || sub._id} className="flex items-center gap-2 text-xs py-1 text-slate-600 dark:text-slate-400">
                                                                                        <span>↳</span>
                                                                                        <span className="flex-1 truncate">{sub.name || sub.title}</span>
                                                                                        <span className="font-mono text-[10px]">{sub.estimatedHours || 4}h</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}

                                                            {orphanSubs.map((sub) => (
                                                                <div key={sub.id || sub._id} className="flex items-center gap-3 px-4 py-1.5 text-xs text-slate-500">
                                                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300">Subtask</span>
                                                                    <span className="flex-1 truncate">{sub.name || sub.title}</span>
                                                                    <span className="font-mono">{sub.estimatedHours || 4}h</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    );
                                })()}

                                {/* ── Documents & Assets ── */}
                                {(form.documents.length > 0 || form.assets.length > 0) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {form.documents.length > 0 && (
                                            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                                                <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Documents ({form.documents.length})</h4>
                                                {form.documents.map((d, idx) => {
                                                    const url = d.publicUrl || d.url || d.storageKey;
                                                    return (
                                                        <div key={d.id || d._id || idx} className="flex items-center justify-between text-slate-600 dark:text-slate-300 text-sm">
                                                            <span className="truncate flex items-center gap-1.5">
                                                                📄 <span className="font-bold">{d.fileName}</span> <span className="text-slate-400 font-normal">({d.fileSize})</span>
                                                            </span>
                                                            {url && (
                                                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 font-bold hover:underline ml-2 flex items-center gap-1 shrink-0">
                                                                    <EyeOutlined /> View
                                                                </a>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                        {form.assets.length > 0 && (
                                            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                                                <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Assets ({form.assets.length})</h4>
                                                {form.assets.map((a, idx) => {
                                                    const url = a.publicUrl || a.url || a.storageKey;
                                                    return (
                                                        <div key={a.id || a._id || idx} className="flex items-center justify-between text-slate-600 dark:text-slate-300 text-sm">
                                                            <span className="truncate flex items-center gap-1.5">
                                                                🖼 <span className="font-bold">{a.fileName}</span> <span className="text-slate-400 font-normal">({a.fileSize})</span>
                                                            </span>
                                                            {url && (
                                                                <a href={url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 dark:text-indigo-400 font-bold hover:underline ml-2 flex items-center gap-1 shrink-0">
                                                                    <EyeOutlined /> View
                                                                </a>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ── Workflow Config ── */}
                                <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-2">
                                    <h4 className="font-bold uppercase tracking-wider text-slate-400 text-xs">Workflow Configuration</h4>
                                    <div className="flex flex-wrap gap-2 text-sm">
                                        <Tag color="blue">{form.defaultView}</Tag>
                                        {form.sprintManagement && <Tag color="purple">Sprint Management ON</Tag>}
                                        {Object.entries(form.availableViews).filter(([, v]) => v).map(([k]) => <Tag key={k}>{k}</Tag>)}
                                    </div>
                                </div>

                                {/* ── Validation Engine ── */}
                                <div className="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-3 text-sm">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400">Project Readiness Validation</h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {validationResults.checks.map((c) => (
                                            <div key={c.id} className="flex items-center gap-2">
                                                {c.valid ? <CheckCircleOutlined className="text-emerald-500" /> : <WarningOutlined className="text-amber-500" />}
                                                <span className={c.valid ? 'text-slate-700 dark:text-slate-300' : 'text-amber-600 font-bold'}>{c.label}</span>
                                            </div>
                                        ))}
                                    </div>
                                    {validationResults.warnings.length > 0 && (
                                        <div className="p-3 bg-amber-50 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-900/60 text-amber-700 dark:text-amber-300 space-y-1 text-sm">
                                            <p className="font-bold">Non-blocking Warnings:</p>
                                            {validationResults.warnings.map((w, i) => <p key={i}>• {w}</p>)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ── Footer Action Bar ── */}
                    <div className="bg-slate-50 dark:bg-slate-900 p-5 border-t border-slate-200 dark:border-slate-800 flex justify-between items-center">
                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 text-base font-bold text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors cursor-pointer"
                            >
                                Cancel
                            </button>

                            {Boolean(projectToEdit?.status === 'Pending Review' || projectToEdit?.pcrNumber || form.creProjectId) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setRejectProjectReason('');
                                        setShowRejectProjectModal(true);
                                    }}
                                    disabled={loading || rejectingProject}
                                    className="px-4 py-2 text-sm font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-950/40 dark:text-red-400 rounded-xl border border-red-200 dark:border-red-900/60 transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                >
                                    <CloseOutlined /> Reject Project Request
                                </button>
                            )}
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => handleSubmitProject(true)}
                                disabled={loading}
                                className="px-5 py-2.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-base font-bold rounded-xl hover:bg-slate-300 transition-all"
                            >
                                Save as Draft
                            </button>

                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={() => setCurrentStep((prev) => prev - 1)}
                                    className="px-5 py-2.5 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-base font-bold rounded-xl hover:bg-slate-200 transition-all flex items-center gap-1.5"
                                >
                                    <ArrowLeftOutlined /> Back
                                </button>
                            )}

                            {currentStep < 5 ? (
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (currentStep === 1) {
                                            const unfulfilledReqs = Array.isArray(form.requirementChecklist)
                                                ? form.requirementChecklist.filter((item) => item && item.required && !item.met)
                                                : [];
                                            if (unfulfilledReqs.length > 0) {
                                                message.warning(`${unfulfilledReqs.length} required intake checklist item(s) are still pending verification.`);
                                            }
                                        }
                                        setCurrentStep((prev) => prev + 1);
                                    }}
                                    className="px-6 py-2.5 text-base font-bold text-white rounded-xl shadow-md flex items-center gap-1.5 hover:opacity-90 transition-all"
                                    style={{ background: 'linear-gradient(90deg, #03488A 0%, #006591 100%)' }}
                                >
                                    Next <ArrowRightOutlined />
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => handleSubmitProject(false)}
                                    disabled={loading || !validationResults.isReady}
                                    className="px-7 py-2.5 text-base font-bold text-white rounded-xl shadow-md flex items-center gap-2 disabled:opacity-50 transition-all"
                                    style={{ background: 'linear-gradient(90deg, #059669 0%, #10b981 100%)' }}
                                >
                                    {loading ? <LoadingOutlined /> : <CheckCircleOutlined />}
                                    {loading
                                        ? (projectToEdit ? (projectToEdit.isDraft || projectToEdit.status === 'Draft' ? 'Publishing Draft...' : 'Updating Project...') : 'Creating Project...')
                                        : (projectToEdit ? (projectToEdit.isDraft || projectToEdit.status === 'Draft' ? 'Complete & Publish Draft' : 'Update Project') : 'Create Project')}
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── Sub-Modals for Manual Add / Edit Goal / Milestone / Task ── */}
                <Modal
                    open={showGoalModal}
                    onCancel={() => {
                        setShowGoalModal(false);
                        setEditingGoalId(null);
                        setGoalForm({ name: '', description: '', successCriteria: '', targetDate: '', priority: 'Medium' });
                    }}
                    footer={null}
                    title={editingGoalId ? 'Edit Smart Goal' : 'Add Smart Goal Manually'}
                    centered
                    width={480}
                >
                    <div className="space-y-3 pt-2">
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Goal Name *</label>
                            <input
                                className={inputClass}
                                placeholder="e.g. Automate Attendance Processing"
                                value={goalForm.name}
                                onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Description</label>
                            <textarea
                                className={inputClass}
                                placeholder="Brief description of the goal objectives..."
                                rows={2}
                                value={goalForm.description}
                                onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Success Criteria</label>
                            <input
                                className={inputClass}
                                placeholder="e.g. 95%+ operational accuracy"
                                value={goalForm.successCriteria}
                                onChange={(e) => setGoalForm({ ...goalForm, successCriteria: e.target.value })}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Target Date</label>
                                <input
                                    type="date"
                                    className={inputClass}
                                    value={goalForm.targetDate || ''}
                                    onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                                />
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Priority</label>
                                <select
                                    className={inputClass}
                                    value={goalForm.priority || 'Medium'}
                                    onChange={(e) => setGoalForm({ ...goalForm, priority: e.target.value })}
                                >
                                    {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                                </select>
                            </div>
                        </div>
                        <button type="button" onClick={handleAddGoal} className="w-full py-2.5 bg-[#03488a] text-white text-xs font-bold rounded-xl mt-2 hover:opacity-90 transition-all">
                            {editingGoalId ? 'Update Smart Goal' : 'Save Smart Goal'}
                        </button>
                    </div>
                </Modal>

            </Modal>

            {/* ── Sub-Modals for Manual Add / Edit Goal / Milestone / Task ── */}
            <Modal
                open={showGoalModal}
                zIndex={1100}
                onCancel={() => {
                    setShowGoalModal(false);
                    setEditingGoalId(null);
                    setGoalForm({ name: '', description: '', successCriteria: '', targetDate: '', priority: 'Medium' });
                }}
                footer={null}
                title={editingGoalId ? 'Edit Smart Goal' : 'Add Smart Goal Manually'}
                centered
                width={480}
            >
                <div className="space-y-3 pt-2">
                    <div className="space-y-1">
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Goal Name *</label>
                        <input
                            className={inputClass}
                            placeholder="e.g. Automate Attendance Processing"
                            value={goalForm.name}
                            onChange={(e) => setGoalForm({ ...goalForm, name: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Description *</label>
                        <textarea
                            className={inputClass}
                            placeholder="Brief description of the goal objectives..."
                            rows={2}
                            value={goalForm.description}
                            onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })}
                        />
                    </div>
                    <div className="space-y-1">
                        <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Success Criteria</label>
                        <input
                            className={inputClass}
                            placeholder="e.g. 95%+ operational accuracy"
                            value={goalForm.successCriteria}
                            onChange={(e) => setGoalForm({ ...goalForm, successCriteria: e.target.value })}
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Target Date</label>
                            <input
                                type="date"
                                className={inputClass}
                                value={goalForm.targetDate || ''}
                                onChange={(e) => setGoalForm({ ...goalForm, targetDate: e.target.value })}
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Priority</label>
                            <select
                                className={inputClass}
                                value={goalForm.priority || 'Medium'}
                                onChange={(e) => setGoalForm({ ...goalForm, priority: e.target.value })}
                            >
                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>
                    <button type="button" onClick={handleAddGoal} className="w-full py-2.5 bg-[#03488a] text-white text-base font-bold rounded-xl mt-2 hover:opacity-90 transition-all">
                        {editingGoalId ? 'Update Smart Goal' : 'Save Smart Goal'}
                    </button>
                </div>
            </Modal>

            <Modal
                open={showMilestoneModal}
                zIndex={1100}
                onCancel={() => setShowMilestoneModal(false)}
                footer={null}
                title={editingMilestone ? 'Edit Milestone' : 'Add Milestone'}
                centered
                width={500}
            >
                <div className="space-y-3 pt-2">
                    <input
                        className={inputClass}
                        placeholder="Milestone Name *"
                        value={milestoneForm.name}
                        onChange={(e) => setMilestoneForm({ ...milestoneForm, name: e.target.value })}
                    />
                    <textarea
                        className={inputClass}
                        placeholder="Description * (Key objectives & deliverables)"
                        rows={2}
                        value={milestoneForm.description}
                        onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-600 dark:text-slate-300">Start Date</label>
                            <input type="date" className={inputClass} value={milestoneForm.startDate} onChange={(e) => setMilestoneForm({ ...milestoneForm, startDate: e.target.value })} />
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-600 dark:text-slate-300">Target / Due Date</label>
                            <input type="date" className={inputClass} value={milestoneForm.dueDate} onChange={(e) => setMilestoneForm({ ...milestoneForm, dueDate: e.target.value })} />
                        </div>
                    </div>
                    <input
                        className={inputClass}
                        placeholder="Completion Criteria (e.g. Signed-off spec document)"
                        value={milestoneForm.completionCriteria}
                        onChange={(e) => setMilestoneForm({ ...milestoneForm, completionCriteria: e.target.value })}
                    />
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Milestone Lead / Owner *</label>
                            <select
                                className={inputClass}
                                value={milestoneForm.owner || ''}
                                onChange={(e) => setMilestoneForm({ ...milestoneForm, owner: e.target.value })}
                            >
                                <option value="">-- Select Milestone Lead --</option>
                                {employees.map((emp) => (
                                    <option key={emp._id} value={emp._id}>
                                        {emp.name || `${emp.firstName || ''} ${emp.lastName || ''}`}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-700 dark:text-slate-300">Status</label>
                            <select
                                className={inputClass}
                                value={milestoneForm.status || 'Pending'}
                                onChange={(e) => setMilestoneForm({ ...milestoneForm, status: e.target.value })}
                            >
                                <option value="Pending">Pending</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                                <option value="Closed">Closed</option>
                                <option value="Delayed">Delayed</option>
                                <option value="Canceled">Canceled</option>
                                <option value="In Escrow">In Escrow</option>
                                <option value="Released">Released</option>
                            </select>
                        </div>
                    </div>

                    {/* Milestone Deliverable Verification Checklist */}
                    {Array.isArray(milestoneForm.deliverableChecklist) && milestoneForm.deliverableChecklist.length > 0 && (() => {
                        const totalDels = milestoneForm.deliverableChecklist.length;
                        const completedDels = milestoneForm.deliverableChecklist.filter((d) => d.completed).length;
                        const allDelsChecked = totalDels > 0 && completedDels === totalDels;
                        const isCompletedStatus = milestoneForm.status === 'Completed';

                        return (
                            <div className="space-y-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                <div className="flex items-center justify-between">
                                    <label className="text-sm font-bold text-slate-700 dark:text-slate-300 flex items-center gap-1.5">
                                        <span>📋 Deliverables Checklist</span>
                                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${
                                            allDelsChecked
                                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                                                : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
                                        }`}>
                                            {completedDels}/{totalDels} Checked
                                        </span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const targetVal = !allDelsChecked;
                                            setMilestoneForm((prev) => ({
                                                ...prev,
                                                deliverableChecklist: (prev.deliverableChecklist || []).map((item) => ({
                                                    ...item,
                                                    completed: targetVal,
                                                    rejected: targetVal ? false : item.rejected,
                                                    completedAt: targetVal ? new Date().toISOString() : null,
                                                })),
                                                deliverables: (prev.deliverables || []).map((item) => ({
                                                    ...item,
                                                    done: targetVal,
                                                    completed: targetVal,
                                                })),
                                            }));
                                        }}
                                        className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline cursor-pointer"
                                    >
                                        {allDelsChecked ? 'Uncheck All' : 'Check All'}
                                    </button>
                                </div>

                                {/* Warning banner if user selects Completed but not all deliverables are checked */}
                                {isCompletedStatus && !allDelsChecked && (
                                    <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-200 flex items-start gap-2">
                                        <span className="text-amber-600 font-bold text-sm">⚠️</span>
                                        <div>
                                            <p className="font-bold">Deliverable Verification Required</p>
                                            <p className="text-[11px] opacity-90 mt-0.5">
                                                All {totalDels} deliverables must be verified and checked before this milestone can be saved as Completed.
                                            </p>
                                        </div>
                                    </div>
                                )}

                                {isCompletedStatus && allDelsChecked && (
                                    <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-xs text-emerald-800 dark:text-emerald-200 flex items-center gap-2">
                                        <span className="text-emerald-600 font-bold">✓</span>
                                        <span className="font-semibold text-[11px]">All deliverables verified! Milestone is ready to be completed.</span>
                                    </div>
                                )}

                                <div className="max-h-48 overflow-y-auto space-y-1.5 pr-1">
                                    {milestoneForm.deliverableChecklist.map((item, dIdx) => (
                                        <div
                                            key={item.deliverableId || dIdx}
                                            onClick={() => {
                                                setMilestoneForm((prev) => {
                                                    const nextList = [...(prev.deliverableChecklist || [])];
                                                    const currentItem = nextList[dIdx];
                                                    if (!currentItem) return prev;
                                                    const nextCompleted = !currentItem.completed;
                                                    nextList[dIdx] = {
                                                        ...currentItem,
                                                        completed: nextCompleted,
                                                        rejected: nextCompleted ? false : currentItem.rejected,
                                                        completedAt: nextCompleted ? new Date().toISOString() : null,
                                                    };
                                                    const nextDels = (prev.deliverables || []).map((del, idx) => {
                                                        if (idx === dIdx) return { ...del, done: nextCompleted, completed: nextCompleted };
                                                        return del;
                                                    });
                                                    return {
                                                        ...prev,
                                                        deliverableChecklist: nextList,
                                                        deliverables: nextDels,
                                                    };
                                                });
                                            }}
                                            className={`flex items-center justify-between p-2 rounded-xl border text-xs cursor-pointer transition-all select-none ${
                                                item.completed
                                                    ? 'bg-emerald-50/60 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800/60'
                                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-blue-400'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <span className={`w-4 h-4 rounded flex items-center justify-center font-bold text-[10px] border shrink-0 transition-colors ${
                                                    item.completed
                                                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                                                        : 'bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-transparent'
                                                }`}>
                                                    ✓
                                                </span>
                                                <span className={`font-medium truncate ${item.completed ? 'line-through text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
                                                    {item.title || item.name || `Deliverable ${dIdx + 1}`}
                                                </span>
                                            </div>
                                            {item.rejected && !item.completed && (
                                                <span className="text-[10px] text-rose-500 font-bold shrink-0 ml-1">Rejected by CRE</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    <button
                        type="button"
                        onClick={() => {
                            if (!milestoneForm.name.trim()) { message.warning('Milestone name is required.'); return; }
                            if (!milestoneForm.description?.trim() && !milestoneForm.completionCriteria?.trim()) {
                                message.warning('Milestone Description or Completion Criteria is required.');
                                return;
                            }
                            if (!milestoneForm.owner) {
                                message.warning('Milestone Lead / Owner is required.');
                                return;
                            }
                            // Strict deliverable verification gate: all deliverables must be checked if setting status to Completed
                            if (milestoneForm.status === 'Completed' && Array.isArray(milestoneForm.deliverableChecklist) && milestoneForm.deliverableChecklist.length > 0) {
                                const uncompleted = milestoneForm.deliverableChecklist.filter((d) => !d.completed);
                                if (uncompleted.length > 0) {
                                    message.error(`Cannot complete milestone: All ${milestoneForm.deliverableChecklist.length} deliverables must be verified and checked first!`);
                                    return;
                                }
                            }
                            if (editingMilestone) {
                                setForm((prev) => ({ ...prev, milestones: prev.milestones.map((m) => m.id === editingMilestone.id ? { ...m, ...milestoneForm } : m) }));
                                message.success('Milestone updated.');
                            } else {
                                setForm((prev) => ({ ...prev, milestones: [...prev.milestones, { id: `m-${Date.now()}`, ...milestoneForm }] }));
                                message.success('Milestone added.');
                            }
                            setShowMilestoneModal(false);
                            setEditingMilestone(null);
                            setMilestoneForm({ name: '', description: '', startDate: '', dueDate: '', completionCriteria: '', owner: '' });
                        }}
                        className="w-full py-2.5 bg-[#03488a] text-white text-base font-bold rounded-xl mt-1 hover:opacity-90 transition-all"
                    >
                        {editingMilestone ? 'Update Milestone' : 'Save Milestone'}
                    </button>
                </div>
            </Modal>

            <Modal
                open={showTaskModal}
                zIndex={1100}
                onCancel={() => setShowTaskModal(false)}
                footer={null}
                title={editingTask ? 'Edit Task' : 'Add Task'}
                centered
                width={500}
            >
                <div className="space-y-3 pt-2">
                    <input
                        className={inputClass}
                        placeholder="Task Name *"
                        value={taskForm.name}
                        onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                    />
                    <textarea
                        className={inputClass}
                        placeholder="Description (optional)"
                        rows={2}
                        value={taskForm.description}
                        onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                    />
                    <div className="space-y-1">
                        <label className="text-sm font-bold text-slate-600 dark:text-slate-300">Milestone (which milestone does this task belong to?)</label>
                        <select
                            className={inputClass}
                            value={taskForm.milestoneId}
                            onChange={(e) => setTaskForm({ ...taskForm, milestoneId: e.target.value })}
                        >
                            <option value="">— No Milestone (standalone task) —</option>
                            {form.milestones.map((m) => (
                                <option key={m._id || m.id} value={m._id || m.id}>{m.name}{m.dueDate ? ` (Due: ${m.dueDate})` : ''}</option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-600 dark:text-slate-300">Assignee</label>
                            <select className={inputClass} value={taskForm.assignee} onChange={(e) => setTaskForm({ ...taskForm, assignee: e.target.value })}>
                                <option value="">Unassigned</option>
                                {employees.map((e) => <option key={e._id} value={e._id}>{e.name}</option>)}
                            </select>
                        </div>
                        <div className="space-y-1">
                            <label className="text-sm font-bold text-slate-600 dark:text-slate-300">Priority</label>
                            <select className={inputClass} value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}>
                                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                    </div>
                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-600 dark:text-slate-300">Estimated Hours</label>
                        <input
                            type="number"
                            className={inputClass}
                            placeholder="e.g. 8"
                            min={1}
                            value={taskForm.estimatedHours}
                            onChange={(e) => setTaskForm({ ...taskForm, estimatedHours: parseInt(e.target.value, 10) || 0 })}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            if (!taskForm.name.trim()) { message.warning('Task name is required.'); return; }
                            if (editingTask) {
                                const targetId = editingTask.id || editingTask._id;
                                setForm((prev) => ({
                                    ...prev,
                                    tasks: prev.tasks.map((t) => {
                                        const matches =
                                            t === editingTask ||
                                            (t.id && targetId && String(t.id) === String(targetId)) ||
                                            (t._id && targetId && String(t._id) === String(targetId));
                                        return matches ? { ...t, ...taskForm } : t;
                                    }),
                                }));
                                message.success('Task updated.');
                            } else {
                                setForm((prev) => ({ ...prev, tasks: [...prev.tasks, { id: `t-${Date.now()}`, ...taskForm }] }));
                                message.success('Task added.');
                            }
                            setShowTaskModal(false);
                            setEditingTask(null);
                            setTaskForm({ name: '', description: '', milestoneId: '', assignee: '', priority: 'Medium', estimatedHours: 4 });
                        }}
                        className="w-full py-2.5 bg-[#03488a] text-white text-sm font-bold rounded-xl mt-1 hover:opacity-90 transition-all"
                    >
                        {editingTask ? 'Update Task' : 'Save Task'}
                    </button>
                </div>
            </Modal>

            {/* AI Requirements Guidance Modal */}
            <Modal
                open={aiGuidanceModal.open}
                zIndex={1300}
                onCancel={() => setAiGuidanceModal({ open: false, target: 'goals', missingFields: [] })}
                footer={null}
                title={null}
                centered
                width={540}
            >
                <div className="p-5 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-blue-100 dark:bg-blue-900/40 text-blue-600 flex items-center justify-center text-xl shrink-0">
                            <RobotOutlined />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-slate-800 dark:text-white">AI Generation Guidance</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Info required in Step 1 to generate high-quality {aiGuidanceModal.target === 'goals' ? 'Smart Goals' : 'Milestones & Tasks'}
                            </p>
                        </div>
                    </div>

                    <div className="bg-amber-50/80 dark:bg-amber-950/30 p-4 rounded-xl border border-amber-200 dark:border-amber-900/40 space-y-2">
                        <p className="text-xs font-bold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                            <InfoCircleOutlined /> To get accurate, tailored results, please fill in these Step 1 fields:
                        </p>
                        <ul className="space-y-1.5 text-xs text-slate-700 dark:text-slate-300 pt-1">
                            {aiGuidanceModal.missingFields.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                    <span className="text-amber-600 dark:text-amber-400 font-bold">•</span>
                                    <div>
                                        <span className="font-bold text-slate-800 dark:text-slate-100">{item.field}:</span>{' '}
                                        <span className="text-slate-600 dark:text-slate-400">{item.reason}</span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex items-center justify-between pt-2 gap-3">
                        <button
                            type="button"
                            onClick={() => {
                                setAiGuidanceModal({ open: false, target: 'goals', missingFields: [] });
                                setCurrentStep(1);
                            }}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-bold text-xs transition-colors flex items-center gap-1.5 shadow-sm"
                        >
                            Edit Step 1 Fields
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const target = aiGuidanceModal.target;
                                setAiGuidanceModal({ open: false, target: 'goals', missingFields: [] });
                                if (target === 'goals') triggerSmartGoalsGeneration(false);
                                else triggerAIPlanGeneration(false);
                            }}
                            className="px-4 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 text-slate-700 dark:text-slate-200 rounded-xl font-bold text-xs transition-colors"
                        >
                            Generate Anyway (Using Defaults)
                        </button>
                    </div>
                </div>
            </Modal>

            {/* AI Conflict Resolution Modal (Replace vs Append) */}
            <Modal
                open={aiConflictModal.open}
                zIndex={1300}
                onCancel={() => setAiConflictModal({ open: false, target: 'goals' })}
                footer={null}
                centered
                width={480}
            >
                <div className="p-5 space-y-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-amber-100 dark:bg-amber-900/40 text-amber-600 flex items-center justify-center text-xl shrink-0">
                            <QuestionCircleOutlined />
                        </div>
                        <div>
                            <h3 className="text-base font-bold text-slate-800 dark:text-white">Existing Items Detected</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                You already have {aiConflictModal.target === 'goals' ? `${form.smartGoals.length} Smart Goal(s)` : `${form.milestones.length} Milestone(s)`} in this project.
                            </p>
                        </div>
                    </div>

                    <p className="text-xs text-slate-600 dark:text-slate-300">
                        Would you like to replace existing items with a fresh AI plan, or append new items sequentially starting from{' '}
                        <strong className="text-blue-600 dark:text-blue-400 font-bold">
                            {aiConflictModal.target === 'goals' ? `Goal ${form.smartGoals.length + 1}` : `Milestone ${form.milestones.length + 1}`}
                        </strong>?
                    </p>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => {
                                const target = aiConflictModal.target;
                                setAiConflictModal({ open: false, target: 'goals' });
                                if (target === 'goals') triggerSmartGoalsGeneration(false);
                                else triggerAIPlanGeneration(false);
                            }}
                            className="px-4 py-2 bg-red-50 dark:bg-red-950/40 hover:bg-red-100 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900 rounded-xl font-bold text-xs transition-colors"
                        >
                            Replace Existing
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const target = aiConflictModal.target;
                                setAiConflictModal({ open: false, target: 'goals' });
                                if (target === 'goals') triggerSmartGoalsGeneration(true);
                                else triggerAIPlanGeneration(true);
                            }}
                            className="px-4 py-2 bg-[#03488a] hover:opacity-90 text-white rounded-xl font-bold text-xs transition-colors shadow-sm"
                        >
                            Append New ({aiConflictModal.target === 'goals' ? `Goal ${form.smartGoals.length + 1}+` : `Milestone ${form.milestones.length + 1}+`})
                        </button>
                    </div>
                </div>
            </Modal>

            {/* ── Reject Checklist Item Reason Modal ── */}
            <Modal
                open={showRejectItemModal}
                zIndex={1300}
                onCancel={() => {
                    setShowRejectItemModal(false);
                    setRejectingItemIndex(null);
                }}
                footer={null}
                title={
                    <div className="flex items-center gap-2 text-red-600">
                        <CloseOutlined className="text-red-500" />
                        <span className="font-bold">
                            Reject Requirement: {rejectingItemIndex !== null ? form.requirementChecklist[rejectingItemIndex]?.label : ''}
                        </span>
                    </div>
                }
                centered
                width={520}
            >
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-slate-500">
                        Specify why this requirement is rejected or what is needed from BD / Client before approval:
                    </p>

                    {/* Quick Reason Presets */}
                    <div className="space-y-1.5">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Quick Presets:</span>
                        <div className="flex flex-wrap gap-1.5">
                            {[
                                'Missing client brand assets / vector logo',
                                'Invalid or missing login credentials / access',
                                'Requirements out of scope or budget',
                                'Need clarification from client on specs',
                                'Low quality / incomplete reference files',
                            ].map((preset, pIdx) => (
                                <button
                                    key={pIdx}
                                    type="button"
                                    onClick={() => setRejectItemReason(preset)}
                                    className="text-[11px] px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors cursor-pointer border border-slate-200 dark:border-slate-700"
                                >
                                    {preset}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Rejection Note / Instructions *</label>
                        <textarea
                            className={inputClass}
                            placeholder="e.g. Client provided password that expired. Please ask client for fresh access."
                            rows={3}
                            value={rejectItemReason}
                            onChange={(e) => setRejectItemReason(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center justify-between pt-2">
                        <button
                            type="button"
                            onClick={() => {
                                if (rejectingItemIndex === null) return;
                                setForm((prev) => ({
                                    ...prev,
                                    requirementChecklist: prev.requirementChecklist.map((item, i) =>
                                        i === rejectingItemIndex ? { ...item, met: false, rejected: false, note: '' } : item
                                    ),
                                }));
                                setShowRejectItemModal(false);
                                setRejectingItemIndex(null);
                            }}
                            className="text-xs text-slate-500 hover:text-slate-800 underline font-semibold cursor-pointer"
                        >
                            Reset to Pending
                        </button>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowRejectItemModal(false);
                                    setRejectingItemIndex(null);
                                }}
                                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={handleSaveItemRejection}
                                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-all shadow-sm cursor-pointer"
                            >
                                Save Rejection Note
                            </button>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* ── Reject Overall Project Request Modal ── */}
            <Modal
                open={showRejectProjectModal}
                zIndex={1300}
                onCancel={() => setShowRejectProjectModal(false)}
                footer={null}
                title={
                    <div className="flex items-center gap-2 text-red-600">
                        <CloseOutlined className="text-red-500" />
                        <span className="font-bold">Reject Project Request ({projectToEdit?.pcrNumber || form.name})</span>
                    </div>
                }
                centered
                width={520}
            >
                <div className="space-y-4 pt-2">
                    <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                        Are you sure you want to reject this incoming project request? This will mark the request as <strong className="text-red-600">Rejected</strong> in ERP and notify CRE / BD team.
                    </p>

                    <div className="space-y-1">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Overall Rejection Reason *</label>
                        <textarea
                            className={inputClass}
                            placeholder="State the reason for rejecting this project request (e.g. Scope cannot be fulfilled, client cancelled, missing fundamental assets)..."
                            rows={4}
                            value={rejectProjectReason}
                            onChange={(e) => setRejectProjectReason(e.target.value)}
                        />
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={() => setShowRejectProjectModal(false)}
                            className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 cursor-pointer"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleRejectEntireProject}
                            disabled={rejectingProject}
                            className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                            {rejectingProject ? <LoadingOutlined /> : <CloseOutlined />}
                            Confirm Reject &amp; Notify CRE
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
